"use client";

// OsShell — the Internet OS spine. One continuous client surface used by both
// the dashboard root and a project: a breadcrumb, the morphing navigator, a
// content stage (directory grid ↔ document carousel ↔ a reused panel), the
// download/share dock, and a single living organism that focuses and spins
// deeper as you descend. One horizontal-swipe model moves between siblings, or
// between documents when one is open — arbitrated so it never fights scroll,
// folder-drag, or an open reader.

import PanelBoundary from "./PanelBoundary";
import SafeBoundary, { rearmable } from "./SafeBoundary";
import { retryImport } from "../../lib/os/resilience";
import { lockScroll } from "../../lib/os/scrollLock";
import { useNettiBody } from "../../lib/netti/useNettiBody";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useTheme } from "../ThemeProvider";
import { OPEN_RECORD_EVENT, recordAnchor, parseRecordAnchor, type RecordRef } from "../../lib/you/record";
import { publishShareNetti, sameNetti } from "../../lib/sharecard/netti";
import { desktopBridge } from "../../lib/desktop";
import { useRouter, usePathname } from "next/navigation";
import OrganismBackdrop from "../OrganismBackdrop";
import { loadVeilPrefs } from "../../lib/organism/veilPrefs";
import BrandCrumb, { type Crumb } from "../BrandCrumb";
import OrganismMark from "../brand/OrganismMark";
import UserChip from "../UserChip";
import {
  agentLanding,
  agentMovedPath,
  canonicalProjectOf,
  agentsSection,
  agentsStat,
  buildRoot,
  defaultView,
  findByPath,
  parentPath,
  projectFolderNode,
  spliceSubtree,
  type OsNode,
  type PanelKind,
  type PanelMeta,
  type RootAgent,
  type RootProject,
  type RootSpace,
} from "../../lib/os/tree";
import { useOsLayout } from "../../lib/os/useOsLayout";
import { useLiveMemory } from "../../lib/organism/useLiveMemory";
import { useOrganismChoreography } from "../../lib/os/useOrganismChoreography";
import { organismFromSeed, seedFromString, identityKey } from "../../lib/organism/identity";
import { ageFromMemory, sizeFromMemory } from "../../lib/organism/macro-tuners";
import { useHSwipe } from "./useHSwipe";
import OsNavigator, { type ChipDef } from "./OsNavigator";
import FolderSortLine, { SortArrow } from "./FolderSortLine";
import { DEFAULT_SORT, SORTABLE_VIEWS, flip, readSort, type FolderSort } from "../../lib/os/folderSort";

import DirectoryView, { type ContextAction } from "./DirectoryView";
import type { PSTarget } from "./ProjectSettings";
import { symbolFor } from "../../lib/os/symbols";
import OrbitDial from "./OrbitDial";
import { useEditStack, type EditOp } from "../../lib/os/useEditStack";
import { canSee, F, type FeatureGate } from "../../lib/features/registry";
import * as toursNext from "../../lib/tours/tours";
import type { Tour } from "../../lib/tours/tours";
import * as toursV1 from "../../lib/tours/tours.v1";
import { useNext } from "../../lib/release/next";
import { openAgentMake, TOUR_EVENT } from "../../lib/tours/events";
import ArtifactMobile from "./artifact/ArtifactMobile";
import { warmArtifactReader, loadArtifactEditor } from "./artifact/ArtifactDoc";
import { noteUse, warmByUsage } from "../../lib/os/usage";
import { useArtifactSave } from "./artifact/useArtifactSave";
import ShareOptionsBar from "./artifact/ShareOptionsBar";
import GroundDial from "./GroundDial";
import FeedbackLauncher from "../feedback/FeedbackLauncher";
import dynamic from "next/dynamic";

// founder QA bench for the next-gen organism — split out of the shell chunk
import StatusCorner from "./StatusCorner";
import OutageWindow from "../status/OutageWindow";
import { reachOrReport, reachQuietly, onSignedIn, onSignedOut } from "../../lib/os/outage";
import { fetchWithDeadline, within } from "../../lib/os/fetchDeadline";
import { startPoll } from "../../lib/os/poll";
import { ancestorIds } from "../../lib/os/projectChain";
import { historyStep, osNavOf, sameNav, withOsNav, type OsNav } from "../../lib/os/shellHistory";
import { shellShouldIgnoreKey } from "../../lib/os/shellKeys";
import { PillButton } from "../ui";
import { reportClientError } from "../../lib/os/reportError";
import { GUIDE_VEIL_EVENT, GUIDE_VEIL_NETTI_Z } from "../connect/StepShell";
import { CONNECT_TOOLS } from "../../lib/connect/tools";
import { useLiveConnections } from "../../lib/connect/useLiveConnections";
import { usePendingDoor, doorName } from "../../lib/connect/pendingDoor";
import { futurePalette } from "../../lib/organism/future/identity";
import * as obNext from "../../lib/os/onboarding";
import type { ObState } from "../../lib/os/onboarding";
import * as obV1 from "../../lib/os/onboarding.v1";
import { announceProject } from "../../lib/walkthrough/bring";
import { narrate, narrateClear, narrateAct } from "../../lib/os/noticer";
import type { ColorState } from "../../lib/organism/color";
import { setOrganismCalm } from "../../lib/organism/calmBus";
import { setFocusFloor } from "../../lib/os/nettiFocus";
import { AGENTS_ORBIT_NARROW, agentsOrbitShort, agentsOrbitUp, sectionFromPath } from "../../lib/os/organismChoreography";
import { writeReadRamp, clearReadRamp, holdReadRamp, writeBandRamp } from "../../lib/os/readRamp";
import OsSearch from "./OsSearch";
import OsMenu from "./OsMenu";
import { buildMenuTree, type MenuItem } from "../../lib/nav/siteNav";
import OrganismIdentity from "../OrganismIdentity";
import { OsShellProvider, type OsShellApi } from "./OsShellContext";

// law_snappy — the shell's rarer parts are their own chunks, loaded on first use
// (they only ever mount when opened). What this person actually uses is warmed
// while idle (lib/os/usage), so for them it is already here; what they never
// open is never downloaded. The import() stays inline in each dynamic() (Next
// matches it to its chunk for SSR preloading); the warm-up loaders below import
// the same modules, so both resolve to one chunk and one request.
// Both halves retry a dropped download (retryImport), and each component is
// rearmable (SafeBoundary): React.lazy remembers a rejected import for the life
// of the page, so a view whose code failed to download used to stay dead however
// often it was opened again. `load` retries the download, and if it still fails
// the boundary's next retry gets a fresh lazy.
const load = {
  orbit: () => retryImport(() => import("./OrbitView")),
  timeline: () => retryImport(() => import("./ProjectTimeline")),
  tasks: () => retryImport(() => import("./TaskListView")),
  story: () => retryImport(() => import("../story/ProjectStoryView")),
  repo: () => retryImport(() => import("../repo/ProjectRepoView")),
  compose: () => retryImport(() => import("./FolderComposer")),
  move: () => retryImport(() => import("./MovePicker")),
  emoji: () => retryImport(() => import("./EmojiPicker")),
  // the welcome's two overlays: asked for early by the safety valve below, which
  // is how it knows whether they ever arrived
  arrival: () => retryImport(() => import("../onboarding/Arrival")),
  guide: () => retryImport(() => import("../onboarding/NettiGuide")),
};
// Every part loaded on use has a Suspense boundary of its OWN (the `loading`
// option is what makes next/dynamic put one there), and its fallback is nothing.
// Without one, a chunk that is slow to arrive suspends up to the nearest boundary
// ABOVE it: the welcome overlay's chunk stalling used to hold hydration of the
// whole dashboard, which is how a new person met a page that looked finished and
// answered nothing. Now the rest of the shell comes alive around the part that
// is late, and the part itself arrives when it arrives.
function Quiet() {
  return null;
}
const OrbitView = rearmable((load) => dynamic(() => load(() => import("./OrbitView")), { loading: Quiet }));
const ProjectTimeline = rearmable((load) => dynamic(() => load(() => import("./ProjectTimeline")), { loading: Quiet }));
const TaskListView = rearmable((load) => dynamic(() => load(() => import("./TaskListView")), { loading: Quiet }));
const ProjectStoryView = rearmable((load) => dynamic(() => load(() => import("../story/ProjectStoryView")), { loading: Quiet }));
const ProjectRepoView = rearmable((load) => dynamic(() => load(() => import("../repo/ProjectRepoView")), { loading: Quiet }));
const FolderComposer = rearmable((load) => dynamic(() => load(() => import("./FolderComposer")), { loading: Quiet }));
const MovePicker = rearmable((load) => dynamic(() => load(() => import("./MovePicker")), { loading: Quiet }));
// onboarding + the connect guide: only a new person, or a deliberate connect.
const OnboardingConductor = rearmable((load) => dynamic(() => load(() => import("../onboarding/OnboardingConductor")), { loading: Quiet }));
const Arrival = rearmable((load) => dynamic(() => load(() => import("../onboarding/Arrival")), { loading: Quiet }));
const NettiGuide = rearmable((load) => dynamic(() => load(() => import("../onboarding/NettiGuide")), { loading: Quiet }));
const NettiTour = rearmable((load) => dynamic(() => load(() => import("../onboarding/NettiTour")), { loading: Quiet }));
// today's walk, tours, connect window and folder sheet, for everyone who is not a test account
// (lib/release/next); the ones above are the v1.1 release's
const OnboardingConductorV1 = rearmable((load) => dynamic(() => load(() => import("../onboarding/OnboardingConductor.v1")), { loading: Quiet }));
const ArrivalV1 = rearmable((load) => dynamic(() => load(() => import("../onboarding/Arrival.v1")), { loading: Quiet }));
const NettiGuideV1 = rearmable((load) => dynamic(() => load(() => import("../onboarding/NettiGuide.v1")), { loading: Quiet }));
const NettiTourV1 = rearmable((load) => dynamic(() => load(() => import("../onboarding/NettiTour.v1")), { loading: Quiet }));
const ConnectGuideModalV1 = rearmable((load) => dynamic(() => load(() => import("../connect/ConnectGuideModal.v1")), { loading: Quiet }));
const FolderComposerV1 = rearmable((load) => dynamic(() => load(() => import("./FolderComposer.v1")), { loading: Quiet }));
const EmojiPickerV1 = rearmable((load) => dynamic(() => load(() => import("./EmojiPicker.v1")), { loading: Quiet }));
const ConnectGuideModal = rearmable((load) => dynamic(() => load(() => import("../connect/ConnectGuideModal")), { loading: Quiet }));
// the agents orbit's depth of field over netti (components/agents/OrbitVeil): a
// decoration, only ever on the agents home's orbit
const OrbitVeil = rearmable((load) => dynamic(() => load(() => import("../agents/OrbitVeil")), { loading: Quiet }));
// the icon picker: a whole emoji collection's worth of sheet, for the one press
// of "icon…". it mounts the first time it is asked for (see iconEver below)
const EmojiPicker = rearmable((load) => dynamic(() => load(() => import("./EmojiPicker")), { loading: Quiet }));
import { useBandHeight } from "../../lib/os/useBandHeight";
import { useLockFold } from "../../lib/os/lockFold";
import { useSeamMask } from "../../lib/os/seamMask";
import { useAvatarAnchor } from "../../lib/os/useAvatarAnchor";
import Stage from "../stage/Stage";
import { ReaderTabsRow, ReaderDoc } from "./artifact/ArtifactMobile";
import { onStageScroll, stageScrollTop, stageScrollMax, stageScroller } from "../../lib/stage/scroller";
import { relationOf, wrapStep, type Dir } from "../../lib/stage/direction";
import { shellPathOf, urlFor } from "../../lib/stage/location";
import { entryIdOf, newEntryId, readScroll, saveScroll, withEntryId } from "../../lib/stage/memory";
import { keepResumable, peekResumable } from "../../lib/os/resume";
import { PLACES, projectPlaceReady } from "../../lib/os/places";
import { replaceStageUrl, stageIdxOf, writeStageEntry } from "../../lib/stage/history";
import { scrollHolders } from "../../lib/os/scrollLock";

/** where this browser keeps how folders are ordered */
const FOLDER_SORT_KEY = "innernet:folder-sort";

// short first-look lines netti says when a view is opened for the first time
// during the onboarding reveal (post-graduation). Unknown views just reveal.
const VIEW_INTRO: Record<string, string> = {
  grid: "grid — your folders laid out as a desktop. drag one onto another to nest it.",
  list: "list — the same folders as a dense, scannable column.",
  documents: "artifacts — the living documents netti keeps building as findings land.",
  timeline: "timeline — everything touched here, in order, narrated.",
  tasks: "tasks — every open loop across your projects, one checklist.",
  home: "a desktop you arrange by hand — drag a folder where you want it.",
  story: "every project, told as a story. the weeks are chapters.",
  repo: "every commit, with the reason it was made.",
};

export interface OsShellProps {
  /** lightweight project list for the root tree (built server-side too). */
  projects: RootProject[];
  /** where to start: '/projects' (the root) or '/projects/<id>' (a project). */
  initialPath: string;
  /** open this page or cell on arrival (the dashboard's `?open=`). */
  initialOpen?: string | null;
  /** a view to open `initialOpen` on (`repo`), from `?view=` beside `?open=` */
  initialOpenView?: string | null;
  /** a project subtree preloaded for SSR deep entry (the /projects/[id] route). */
  initialSubtree?: OsNode | null;
  /** the existing dashboard panels, rendered when a root section is one. */
  panels: Partial<Record<PanelKind, React.ReactNode>>;
  userName: string;
  /** founder — show the operator-console link beside the identity. */
  isAdmin?: boolean;
  /** total memory size (nodes + facts) — drives the organism's age on a slow curve. */
  /** total cells (the unified usage metric) — drives the organism's resting age. */
  cells?: number;
  /** per-panel status chips (me · activity · parked · connections · agents) so each section
   *  shows its "N items ● lastActive" line in the navigator's consistent location. */
  panelMeta?: PanelMeta;
  /** gate the cross-project + per-project tasks UI to specific accounts (beta). */
  tasksEnabled?: boolean;
  /** show the shared-spaces "ours" section (founder-gated while it settles). */
  oursEnabled?: boolean;
  /** show the agents section (beta: founders and anyone granted F.agents). */
  agentsEnabled?: boolean;
  /** the caller's shared spaces — they hang under `ours` so the ≡ menu drills
   *  into each room (each lives at its own /ours/<slug> route). */
  spaces?: RootSpace[];
  /** every agent the person keeps (top-level and sub-agents), so each is a real
   *  path under /agents from the first paint: a reload or a deep link lands on it,
   *  and the crumbs and the ≡ menu know it. The agents panel keeps it current after
   *  that (OsShellApi.setAgentRows). Only read when `agentsEnabled`. */
  agentRows?: RootAgent[];
  /** the signed-in user's uploaded avatar photo (wears the cell in the header). */
  avatarUrl?: string | null;
  /** in-dashboard onboarding "odyssey" state (null = not onboarding / graduated). */
  onboarding?: ObState | null;
  /** netti's tours this person has seen (user_settings.tours, 154): tour id → when */
  toursSeen?: Record<string, string>;
  /** when the account was made: an update tour greets only people who were here before it shipped */
  joinedAt?: string | null;
  /** features granted to this person (feature_access), for which tours they may meet */
  betaFeatures?: string[];
  /** the console's overlay (feature_gates, migration 157), read beside the grants */
  featureGates?: Record<string, FeatureGate>;
  /** a real AI connection exists (drives the onboarding "connected" beat). */
  connected?: boolean;
  /** THE STAGE (components/stage): the navigator in flow on top, the content in
   *  its own scroller below, and every place its own address. */
  stage?: boolean;
  /** where the shell's addresses live (`/dashboard`, `/mirror`) — stage only. */
  base?: string;
  /** the place the address named on arrival (`/projects/p1/vision`) — stage only. */
  initialPlace?: string | null;
  /** the view chip the address named (`?v=`) — stage only. */
  initialView?: string | null;
  /** how the stage holds the split: "layer" (default) or "split" (`?fold=split`). */
  stageFold?: "layer" | "split";
}

/** where a place is in the tree: a folder is a path; a document is its folder
 *  plus its place among the folder's pages. null when the tree can't say yet. */
function resolvePlace(tree: OsNode, place: string): { path: string; viewerIndex: number | null } | null {
  const node = findByPath(tree, place);
  if (!node) return null;
  if (node.icon !== "page") return { path: node.path, viewerIndex: null };
  const pp = parentPath(place);
  const parent = pp ? findByPath(tree, pp) : null;
  if (!parent) return null;
  const pages = (parent.children ?? []).filter((c) => c.icon === "page");
  const idx = pages.findIndex((c) => c.path === node.path);
  return idx >= 0 ? { path: parent.path, viewerIndex: idx } : null;
}

// the tree node whose children contain `key` — used to record where a moved
// project USED to sit, so undo can put it back.
function findParentOf(root: OsNode, key: string): OsNode | null {
  for (const c of root.children ?? []) {
    if (c.key === key) return root;
    const hit = findParentOf(c, key);
    if (hit) return hit;
  }
  return null;
}

// one project's read on the wire: whoever started it last owns the project's
// answer (see pullSubtree in the shell)
interface SubtreeReq {
  seq: number;
  ctl: AbortController;
  done: Promise<boolean>;
}

/** how long the overture / closing may hold the content dark before, with no
 *  overlay on screen, the shell lets go of it. */
const OVERLAY_GRACE_MS = 10_000;

/** folders the orbit reads in the background at one time. */
const ORBIT_PREFETCH_AT_ONCE = 3;

/** how long an open may keep the stage dimmed or its ghosts up before the shell
 *  stops waiting and says it could not be opened. The read itself has a shorter
 *  deadline (lib/os/fetchDeadline); this one is the second lock on the same door. */
const OPEN_CAP_MS = 12_000;
/** the least an open must have left to be worth asking a second time. */
const SECOND_ASK_MIN_MS = 5_000;

/** a project's own subtree and nothing else. Splicing is by path, so a payload
 *  that is not one (an error body answered 200, a proxy's page, a node that
 *  claims to be /projects) must never reach the tree. */
function isProjectSubtree(v: unknown): v is OsNode {
  const n = v as Partial<OsNode> | null;
  return !!n && typeof n === "object" && typeof n.path === "string" && /^\/projects\/[^/]+$/.test(n.path) && Array.isArray(n.children);
}

/** where a sign-in comes back to: here, wherever here is. */
function signInHref(): string {
  if (typeof window === "undefined") return "/login";
  return `/login?next=${encodeURIComponent(window.location.pathname + window.location.search)}`;
}

/** how far out of focus netti rests in a deep place before any scroll (the
 *  scroll takes it to fully soft); law_focus_pull, lib/os/nettiFocus. */
const DEEP_FOCUS = 0.8;

export default function OsShell({ projects, initialPath, initialOpen = null, initialOpenView = null, initialSubtree, panels, userName, isAdmin = false, cells = 0, panelMeta, tasksEnabled = false, oursEnabled = false, agentsEnabled = false, spaces, agentRows, onboarding = null, toursSeen = {}, joinedAt = null, betaFeatures = [], featureGates, connected = false, avatarUrl = null, stage = false, base = "/dashboard", initialPlace = null, initialView = null, stageFold = "layer" }: OsShellProps) {
  // THE NEXT RELEASE (lib/release/next): a test account walks the v1.1 onboarding; everyone else today's
  const next = useNext();
  // today's walk never holds a `walk` stage (the loader parses it with onboarding.v1), so its readers take it as theirs
  const unlockedFor = next ? obNext.unlockedFor : (s: ObState | null | undefined) => obV1.unlockedFor(s as obV1.ObState | null | undefined);
  const STAGE_HUE: Record<string, number> = next ? obNext.STAGE_HUE : obV1.STAGE_HUE;
  const parseObState = next ? obNext.parseObState : (raw: unknown) => obV1.parseObState(raw) as ObState | null;
  const { dueTour, markSeen, TOUR_BY_ID } = next ? toursNext : toursV1;
  const FolderSheet = next ? FolderComposer : FolderComposerV1;
  const IconSheet = next ? EmojiPicker : EmojiPickerV1;
  const [tree, setTree] = useState<OsNode>(() => {
    const root = buildRoot(projects, { panelMeta, tasks: tasksEnabled, ours: oursEnabled, agents: agentsEnabled, agentRows, spaces, today: !next });
    if (!initialSubtree) return root;
    if (findByPath(root, initialSubtree.path)) return spliceSubtree(root, initialSubtree);
    // current project isn't in the sibling list (e.g. archived) — inject it.
    const section = findByPath(root, "/projects");
    if (section) section.children = [initialSubtree, ...(section.children ?? [])];
    return { ...root };
  });
  const treeRef = useRef(tree);
  useEffect(() => {
    treeRef.current = tree;
  }, [tree]);
  // the root list as the shell last heard it, for answers that need the shape of
  // the nesting (which projects sit inside which) without waiting on a render
  const projectsRef = useRef(projects);
  useEffect(() => {
    projectsRef.current = projects;
  }, [projects]);

  // THE HOME SPACE IS GONE — projects is where you arrive. Anything still
  // pointing at `/home` (a stored layout, a history entry from before the
  // change, a bookmarked deep link) lands on projects rather than on a dead
  // path, which would leave `current` and `path` disagreeing.
  const livePath = (p: string) => (p === "/home" || p.startsWith("/home/") ? "/projects" : p);
  // THE STAGE: a fresh load lands on the place its address names. A place the
  // tree already holds (a space, a project, a page of the preloaded project) is
  // where the shell starts; a deeper one is opened on mount (below), from the
  // nearest place that IS known, so the first paint is never a wrong room.
  const arrival = useMemo(() => {
    if (!stage || !initialPlace) return null;
    const want = livePath(initialPlace);
    // THE GATE: a place the walk has not opened yet is not where a walk starts
    // (the loader already chose an open one, initialPath)
    const seg = want.split("/").filter(Boolean)[0];
    if (next && seg && unlockedFor(onboarding)?.has(seg) === false) return null;
    const hit = resolvePlace(tree, want);
    if (hit) return { ...hit, pending: false };
    // not in the tree yet (inside a project whose contents load on demand):
    // stand in the nearest place that is, and open the rest on mount
    let up = parentPath(want);
    while (up && up !== "/" && !findByPath(tree, up)) up = parentPath(up);
    return { path: up && up !== "/" ? up : livePath(initialPath), viewerIndex: null, pending: true };
    // the first tree only: this is where the shell STARTS
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [path, setPath] = useState(() => arrival?.path ?? livePath(initialPath));
  const [viewerIndex, setViewerIndex] = useState<number | null>(() => arrival?.viewerIndex ?? null);
  const [expanded, setExpanded] = useState(false);
  // notion-like inline editing of the focused artifact + its in-session edited
  // bodies (so switching docs and back keeps the edit) + the debounced autosave.
  const [editing, setEditing] = useState(false);
  const [docEdits, setDocEdits] = useState<Record<string, string>>({});
  const { save: saveDoc, flushNow: flushDoc, discard: discardDoc, landedAfter, writeNow } = useArtifactSave();
  // what each page said the moment you began editing it — cancel restores this
  // (law_edit_frame). Keyed by page so switching documents mid-edit keeps a
  // snapshot per document; cleared on save, on cancel, and on leaving edit mode.
  const editSnapshots = useRef<Map<string, { markdown: string; at: number; projectId?: string; name?: string }>>(new Map());
  const saveArtifactDocument = useCallback(
    (key: string, projectId: string | undefined, md: string, name?: string) => {
      setDocEdits((prev) => ({ ...prev, [key]: md }));
      saveDoc({ key, projectId, name }, md);
    },
    [saveDoc],
  );
  // a COUNT, not a flag: two opens in flight (the second finishing first) used to
  // let the first one's `false` lift the dim while the other was still waiting
  const [loadingN, setLoadingN] = useState(0);
  const loading = loadingN > 0;
  const [isNarrow, setIsNarrow] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 640px)");
    const on = () => setIsNarrow(mq.matches);
    on();
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    setReduced(window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false);
  }, []);
  // first-load preloader: the dashboard arrives on the eggshell ground and the
  // organism GROWS IN (scale + opacity) as the loading animation; the chrome
  // then reveals. The organism's own growth is the preloader.
  const [revealed, setRevealed] = useState(false);
  const firstReveal = useRef(true);
  // the same moment as state, for render: after it the chrome answers in one
  // beat (law_snappy) instead of the preloader's slower fade.
  const [introDone, setIntroDone] = useState(false);
  useEffect(() => {
    const id = setTimeout(() => setRevealed(true), 70);
    const done = setTimeout(() => {
      firstReveal.current = false;
      setIntroDone(true);
    }, 1900);
    return () => {
      clearTimeout(id);
      clearTimeout(done);
    };
  }, []);
  // the organism reacts when you record/refine memory on the dashboard
  const [pulse, setPulse] = useState(0);
  const firePulse = useCallback(() => setPulse((p) => p + 1), []);
  // how a folder's grid and list are ordered (lib/os/folderSort): one choice per
  // viewer, kept in this browser. Read after mount so the server render and the
  // first client render agree.
  const [folderSort, setFolderSort] = useState<FolderSort>(DEFAULT_SORT);
  useEffect(() => {
    try {
      setFolderSort(readSort(window.localStorage.getItem(FOLDER_SORT_KEY)));
    } catch {
      /* the default */
    }
  }, []);
  const saveSort = useCallback((next: FolderSort) => {
    setFolderSort(next);
    try {
      window.localStorage.setItem(FOLDER_SORT_KEY, JSON.stringify(next));
    } catch {
      /* this visit only */
    }
  }, []);

  // the explicit save — the reader's "save" button. Pushes whatever is buffered,
  // and only on a landed write says so ("memory updated") and leaves edit mode.
  const commitDocEdit = useCallback(
    async (key: string): Promise<boolean> => {
      const ok = await flushDoc(key);
      if (ok) {
        editSnapshots.current.clear();
        setEditing(false);
        firePulse(); // the organism feels its memory change
        window.dispatchEvent(new CustomEvent("innernet:clip", { detail: { action: "note", name: "memory updated" } }));
      } else {
        window.dispatchEvent(new CustomEvent("innernet:clip", { detail: { action: "note", name: "couldn't save — kept your words, will retry" } }));
      }
      return ok;
    },
    [flushDoc, firePulse],
  );

  // cancel — the other way out of edit mode. Drops anything still buffered,
  // and if an autosave already reached the server since the snapshot, writes
  // the snapshot back through the SAME path so the memory is what it was —
  // only then does it say so. The local edit is forgotten either way.
  const cancelDocEdit = useCallback(
    async (key: string): Promise<void> => {
      const snap = editSnapshots.current.get(key);
      await discardDoc(key); // drops the buffer, waits out any write on the wire
      if (snap && landedAfter(key, snap.at)) {
        const ok = await writeNow({ key, projectId: snap.projectId, name: snap.name }, snap.markdown);
        if (!ok) {
          // the server holds the autosaved edit and we couldn't undo it — the
          // screen must keep showing what memory now says, so the local body stays.
          editSnapshots.current.delete(key);
          setEditing(false);
          window.dispatchEvent(new CustomEvent("innernet:clip", { detail: { action: "note", name: "couldn't restore the original — your edit is what's kept" } }));
          return;
        }
      }
      setDocEdits((prev) => {
        if (!(key in prev)) return prev;
        const next = { ...prev };
        delete next[key];
        return next;
      });
      editSnapshots.current.delete(key);
      setEditing(false);
      window.dispatchEvent(new CustomEvent("innernet:clip", { detail: { action: "note", name: "changes discarded" } }));
    },
    [discardDoc, landedAfter, writeNow],
  );

  // LIVE NERVOUS SYSTEM — useLiveMemory folds the in-app event bus + Supabase
  // realtime (this user's activity_events INSERTs) into one monotonic counter.
  // Every write that hits innernet — a capture/commit/save/sync from ANY tool or
  // tab — fires the organism's neurons here, even when it didn't originate in this
  // view. Combined with the local `pulse` (dashboard edits) below.
  const live = useLiveMemory();

  // LIVE PROJECTS — when a new project arrives (auto-pushed by `innernet connect`,
  // saved from another tab, or created by an agent) the parent re-renders with an
  // updated `projects` list. Fold any project not already in the tree into the
  // /projects section: the new tile MOUNTS and plays the os-enter fade-in, and the
  // organism fires. Additive only — existing (possibly expanded) project subtrees
  // are preserved.
  const knownProjects = useRef<Set<string>>(new Set(projects.map((p) => p.id)));
  useEffect(() => {
    const incoming = projects.filter((p) => !knownProjects.current.has(p.id));
    if (incoming.length === 0) return;
    incoming.forEach((p) => knownProjects.current.add(p.id));
    // the first walk is listening for a project the ai brings in (lib/walkthrough/bring)
    incoming
      .filter((p) => !p.parentId && !(p as RootProject & { parked?: boolean }).parked)
      .forEach((p) => announceProject({ id: p.id, name: p.name, slug: p.slug }));
    setTree((prev) => {
      const section = findByPath(prev, "/projects");
      if (!section) return prev;
      const have = new Set((section.children ?? []).map((c) => c.key));
      // only TOP-LEVEL, un-parked projects fold into the root section; nested ones
      // live inside their parent, parked ones under /parked. (`parked` is an
      // incoming optional flag — tolerate it being absent.)
      const additions = incoming
        .filter((p) => !have.has(p.id) && !p.parentId && !(p as RootProject & { parked?: boolean }).parked)
        .map(projectFolderNode);
      if (additions.length === 0) return prev;
      const total = (section.children?.length ?? 0) + additions.length;
      const nextSection: OsNode = {
        ...section,
        children: [...additions, ...(section.children ?? [])],
        stats: [{ label: total === 1 ? "project" : "projects", value: total }],
      };
      return spliceSubtree(prev, nextSection);
    });
    firePulse(); // a live arrival fires the organism
  }, [projects, firePulse]);
  // LIVE AGENTS — the agents panel reads the person's agents (and re-reads them
  // after a make, a remove, a poll) and hands the rows here, so every agent is a
  // path in the tree: /agents/<slug> and /agents/<parent>/<slug>. Splices the
  // agents subtree whole (rows ARE the truth: an agent gone from the rows leaves
  // the tree) and updates the chip. `agentRowsRef` holds the latest rows
  // synchronously, so a goTo made in the same breath resolves a just-made agent
  // before React has rendered the tree that holds it (see goTo).
  const agentRowsRef = useRef<RootAgent[] | null>(agentRows ?? null);
  // the panel polls: an identical answer must not rebuild the tree (and the whole shell
  // with it) every half minute, so what was last spliced in is remembered as a signature
  const agentRowsSig = useRef<string | null>(null);
  const setAgentRows = useCallback((rows: RootAgent[]) => {
    agentRowsRef.current = rows;
    const sig = rows
      .map((r) => [r.id, r.slug, r.name, r.status, r.parentSlug ?? "", r.memories ?? "", r.held ?? "", r.lastActiveAt ?? "", r.kind ?? "", r.users ?? "", (r.projects ?? []).map((t) => `${t.id}:${t.home ? 1 : 0}${t.folder ? 1 : 0}`).join(",")].join("\u001f"))
      .join("\u001e");
    if (sig === agentRowsSig.current) return;
    agentRowsSig.current = sig;
    setTree((prev) => {
      const node = findByPath(prev, "/agents");
      if (!node) return prev; // the space is not open to this person
      return spliceSubtree(prev, agentsSection({ ...node, stats: agentsStat(rows) }, rows));
    });
  }, []);
  // single-tap selects a folder/file (the compact 2px rect); double-tap opens it.
  // null = nothing selected. Cleared on Escape + on navigating to a new level.
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  // a search keyword carried into the opened document, so the reader highlights it.
  // Set when you click a search hit; cleared on any manual navigation.
  const [searchHighlight, setSearchHighlight] = useState<string | null>(null);
  // the search shows its full "search" string by default — even on mobile. It only
  // collapses to a magnifying glass when the breadcrumb has grown long enough that
  // the string would clash with the avatar (measured below). The string is priority.
  const [searchTight, setSearchTight] = useState(false);
  const crumbRef = useRef<HTMLSpanElement>(null);
  const identityRef = useRef<HTMLDivElement>(null);
  // the avatar itself — the `you` space seats netti's central cell over it, so
  // its centre is published as --os-avatar-dx/dy (lib/os/useAvatarAnchor).
  const avatarRef = useRef<HTMLSpanElement>(null);
  // the level's currently-shown view. Reset to the leftmost (default / priority)
  // chip on every level change, EXCEPT a project's: coming back to a project lands
  // on the view (and the document) it was left on (lib/os/places project, John
  // 2026-10-24 "resume where you left off"). The chip ORDER persists for every level.
  const [viewSel, setViewSel] = useState<string | null>(() => (stage ? initialView : null));
  // a view ASKED FOR with a navigation (goTo(target, { view })) — the path effect
  // that resets the selection consumes it instead, so "open this project's repo"
  // lands on the repo rather than the level's default.
  const pendingView = useRef<string | null>(stage ? initialView : null);
  const pathRef = useRef(path);
  useEffect(() => { pathRef.current = path; }, [path]);
  // the view a PANEL reports for itself (panels are pre-built nodes the shell
  // can't see into): the activity panel's "repos" seats netti bottom-left.
  const [panelView, setPanelView] = useState<string | null>(null);
  // the orbit revolve turns the ORGANISM too (the tiles report their spin; the
  // organism co-rotates on a slightly looser timing curve). `orbitDial` carries
  // whether the orbit is revolvable + its window, for the dial in the orbit chip.
  const [orbitSpin, setOrbitSpin] = useState(0);
  const [orbitDial, setOrbitDial] = useState<{ active: boolean; windowArc: number; page: number; pages: number }>({ active: false, windowArc: Math.PI, page: 0, pages: 1 });
  // nav direction (+1 next / right · -1 prev / left) — drives the directional
  // content slide. Set synchronously before each navigation so the re-render reads it.
  const navDir = useRef(1);
  // a SWIPE between sibling spaces lands with a slower, spring-graced slide; a
  // drill into a folder (or back out) stays quick. Holds the PATH the swipe
  // landed on, so the flag stays true for every re-render of that stage (a
  // flag reset after one render dropped the attribute mid-animation and the
  // slide fell back to the quick one) and reads false the moment a drill
  // moves to any other path.
  const navSwipePath = useRef<string | null>(null);
  // stable: OrbitView keys its rAF loop on this callback, so an inline arrow
  // restarted the orbit's animation loop on every render of the shell.
  const onOrbitState = useCallback(
    ({ active, windowArc, page, pages }: { active: boolean; windowArc: number; page: number; pages: number }) =>
      setOrbitDial((d) =>
        d.active === active && d.windowArc === windowArc && d.page === page && d.pages === pages
          ? d
          : { active, windowArc, page, pages },
      ),
    [],
  );

  // ── in-dashboard onboarding "odyssey" ───────────────────────────────────────
  // The shell owns the ObState so the unlocked set (derived from stage) can filter
  // the navigator + menu; the conductor sequences + persists. `unlocked` = null →
  // everything shown (graduated / not onboarding). isLocked gates a section's key.
  const [ob, setOb] = useState<ObState | null>(onboarding);
  const unlocked = useMemo(() => unlockedFor(ob), [ob]);
  const isLocked = useCallback((key: string) => unlocked != null && !unlocked.has(key), [unlocked]);
  // does a place a walk wants to open exist, and is it open right now? goTo walks
  // an unknown place up to its nearest ancestor without a word; a walk must know,
  // so a beat about a place that is not there is stepped over (and reported)
  // rather than spoken over the wrong room.
  const walkPlaceResolves = useCallback((p: string) => {
    const seg = p.split("/").filter(Boolean)[0];
    if (seg && isLocked(seg)) return false;
    return !!findByPath(treeRef.current, p);
  }, [isLocked]);
  // the OVERTURE — netti's cinematic birth — owns the very first entry: a fresh
  // odyssey that hasn't been born yet. Once bornAt lands (or the person skips to
  // `free`), it never returns. The GUIDE — netti walking you in as a chat — runs
  // from birth until the free-stage greeting is dismissed ("explore").
  // PARKED ON A PHONE — the person pressed "continue later on desktop" during
  // the overture. The walk waits (bornAt stays unset, so a desktop session
  // replays the whole welcome); on the phone the home space opens plainly with
  // a quiet note instead of the tour. Keyed to the viewport, not the device:
  // the same account on a desktop is NOT parked.
  const mobileParked = !!ob && isNarrow && !!ob.deferredAt && ob.stage !== "free";
  const overtureActive = !!ob && ob.stage === "home" && !ob.bornAt && !mobileParked;
  const guideActive = !!ob && !overtureActive && !mobileParked && (ob.stage !== "free" || !ob.dismissed);
  // the walk is still under way (the connection step, or the walk after it):
  // one voice speaks, and the corner keeps to hints
  const walkRunning = next && !!ob && ob.stage !== "free";
  // THE CLOSING — the beat between the walk ending (whether you finished it or
  // skipped it) and the place becoming yours. The tour is over, but nothing has
  // been handed over yet: the chrome is down, every space is still out of sight,
  // and netti holds the centre with one line. Pressing "explore" ends it and the
  // whole navigator arrives at once.
  //
  // This is also what fixed the shift: the spaces used to unlock UNDER a
  // navigator that was already on screen, so the strip re-measured and slid
  // while you watched. Now the navigator mounts once, after, with its full set.
  const closing = !!ob && !overtureActive && ob.stage === "free" && !ob.dismissed;
  // chrome sleeps during the overture; the attribute is what the corner CSS keys on.
  useEffect(() => {
    document.documentElement.toggleAttribute("data-overture", overtureActive);
    return () => document.documentElement.removeAttribute("data-overture");
  }, [overtureActive]);
  // THE SAFETY VALVE. The overture and the closing hold the whole content surface
  // at opacity 0 with pointer-events none, and the only way out of either is the
  // overlay each one owns (Arrival · NettiGuide). Both are chunks loaded on use: if
  // one never arrives (a dropped connection, a download that stalls) a new person
  // faced a blank dashboard they could not click, for as long as the tab lived. So
  // the room is given a grace to light; if the overlay's code has not arrived by
  // then, the content is released exactly as if the walk had been skipped. (An
  // overlay that arrives and trips releases at once, through SafeBoundary's onTrip
  // below; this is for the one that never gets that far.) Releasing the arrival
  // hands the person to the connection step, never past it (the conductor steps
  // a born `home` on to `connections`). Local only, like those: a reload plays
  // the welcome again.
  const overlayReady = useRef({ arrival: false, guide: false });
  useEffect(() => {
    if (!overtureActive) return;
    load.arrival().then(() => { overlayReady.current.arrival = true; }, () => { /* the valve below is the answer */ });
    const t = setTimeout(() => {
      if (overlayReady.current.arrival) return; // it arrived; the walk is under way
      reportClientError(new Error("the welcome never arrived"), "onboarding:valve", { overlay: "arrival" });
      setOb((o) => (o && !o.bornAt ? { ...o, bornAt: new Date().toISOString() } : o));
    }, OVERLAY_GRACE_MS);
    return () => clearTimeout(t);
  }, [overtureActive]);
  useEffect(() => {
    if (!closing) return;
    load.guide().then(() => { overlayReady.current.guide = true; }, () => { /* the valve below is the answer */ });
    const t = setTimeout(() => {
      if (overlayReady.current.guide) return;
      reportClientError(new Error("netti's last word never arrived"), "onboarding:valve", { overlay: "guide" });
      setOb((o) => (o && o.stage === "free" && !o.dismissed ? { ...o, dismissed: true } : o));
    }, OVERLAY_GRACE_MS);
    return () => clearTimeout(t);
  }, [closing]);
  // THE FALLBACK. Something in the walk tripped (its brain, its guide): the
  // person is put back on the connection step, which is always reachable (the
  // connections space and its invite stand on their own), and never let through
  // to `free` — nobody enters before a connection is kept. Local only: a reload
  // plays the walk from where it was stored. A graduated walk's closing line
  // simply steps aside.
  const walkFallback = useCallback((where: string) => {
    // today's walk (everyone but a test account) simply lets go: the arrival marks itself born,
    // anything else steps aside to `free`, as before the gate
    if (!next) {
      setOb((o) => (!o ? o : where === "arrival" ? { ...o, bornAt: o.bornAt ?? new Date().toISOString() } : { ...o, stage: "free", dismissed: true }));
      return;
    }
    reportClientError(new Error("the walk tripped: back to the connection step"), "onboarding:fallback", { where });
    setOb((o) => {
      if (!o) return o;
      if (o.stage === "free") return o.dismissed ? o : { ...o, dismissed: true };
      return { ...o, stage: "connections", beat: 0, bornAt: o.bornAt ?? new Date().toISOString() };
    });
  }, [next]);
  // bumped each time a folder is created: every arrival moves the colour (growthHue below).
  const [createdSignal, setCreatedSignal] = useState(0);
  // EVERY ARRIVAL MOVES THE COLOUR A LITTLE. A stage hue alone makes the tour a
  // slideshow of flat colours; what the walk is actually about is that the thing
  // in front of you is ALIVE and takes what you give it. So each folder made and
  // each tool wired turns netti's whole palette on by a step — small enough to
  // stay the same creature, large enough to notice. Folded into the stage colour
  // below; the engine eases between pins, so it drifts rather than repaints.
  const [growthHue, setGrowthHue] = useState(0);
  const growthSeen = useRef({ created: createdSignal, connected });
  useEffect(() => {
    const was = growthSeen.current;
    growthSeen.current = { created: createdSignal, connected };
    // a step per NEW arrival — never on mount, where nothing actually happened.
    if (createdSignal !== was.created) setGrowthHue((h) => (h + 37) % 360);
    else if (connected && !was.connected) setGrowthHue((h) => (h + 23) % 360);
  }, [createdSignal, connected]);
  // when the Noticer introduces netti ("that bloom is netti…"), the bloom itself
  // answers with a pulse — the introduction shimmers through the organism.
  useEffect(() => {
    const onNarrate = (e: Event) => {
      const d = (e as CustomEvent).detail as { id?: string } | null;
      if (d?.id === "home.netti") firePulse();
    };
    window.addEventListener("innernet:narrate", onNarrate as EventListener);
    return () => window.removeEventListener("innernet:narrate", onNarrate as EventListener);
  }, [firePulse]);
  // The walk advances on screen first; the server is told after. A write that
  // did not land (res.ok was never looked at) used to be forgotten, and the walk
  // would start over on the next load. So: once more, a moment later, quietly.
  // Only if nothing newer was sent meanwhile, or the retry would put an older
  // step on top of a newer one.
  const persistSeq = useRef(0);
  const persistOnboarding = useCallback((next: ObState) => {
    setOb(next);
    const mine = ++persistSeq.current;
    const body = JSON.stringify({ onboarding: next });
    const send = async (): Promise<boolean> => {
      try {
        const res = await fetchWithDeadline("/api/onboarding/progress", {
          method: "POST", headers: { "Content-Type": "application/json" }, body,
        });
        if (!res.ok) return false;
        // THE GATE, answered by the server: a step past the connection without
        // one standing is clamped back to the connection step, and the walk on
        // screen follows the server's word (unless something newer was sent).
        try {
          const j = (await res.json()) as { gated?: boolean; onboarding?: unknown };
          const back = j?.gated ? parseObState(j.onboarding) : null;
          if (back && persistSeq.current === mine) setOb(back);
        } catch { /* an answer without a body: the write still landed */ }
        return true;
      } catch {
        return false;
      }
    };
    void (async () => {
      if (await send()) return;
      await new Promise((r) => setTimeout(r, 2500));
      if (persistSeq.current !== mine) return;
      await send(); // best-effort; local state already advanced
    })();
  }, []);

  // THE NEWBORN COMES HOME — the birth page minted a netti before the door and
  // left it in localStorage; the first signed-in arrival adopts it onto the
  // odyssey state, so the creature under this dashboard is the one they held
  // into existence. Read once, then the note is gone.
  useEffect(() => {
    if (!ob || ob.netti) return;
    try {
      // the birth page lives on the apex; this dashboard may live on its own
      // host — and localStorage is per-origin, so the newborn crosses over in
      // a cookie on the shared domain (the same way the session does). Read
      // the cookie first, then the same-origin note; clear both.
      const cookie = document.cookie.split("; ").find((c) => c.startsWith("innernet_newborn="));
      const raw = cookie ? decodeURIComponent(cookie.slice("innernet_newborn=".length)) : localStorage.getItem("innernet:onboarding-netti");
      if (!raw) return;
      const j = JSON.parse(raw) as { identity?: string; owner?: string | null; name?: string | null };
      if (!j.identity) return;
      const owner = (j.owner ?? j.name ?? "").trim();
      localStorage.removeItem("innernet:onboarding-netti");
      try {
        const domain = process.env.NEXT_PUBLIC_COOKIE_DOMAIN;
        document.cookie = `innernet_newborn=; path=/; max-age=0; SameSite=Lax${domain ? `; domain=${domain}` : ""}`;
      } catch { /* fine */ }
      persistOnboarding({ ...ob, netti: { identity: j.identity, ...(owner ? { owner } : {}) } });
      // the name they gave their netti is the name we know them by from here.
      // The WRITE happens server-side inside /api/onboarding/progress (one
      // round trip, and it can read whether a name is already set); this is
      // only the local echo so the corner is right immediately.
      if (owner) setChosenName(owner);
    } catch { /* private mode / bad json — the email-minted netti stands */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [!!ob, ob?.netti, persistOnboarding]);

  // the guided wire-up window (opened from the guide's chips) + the live
  // connections it teaches. Declared here so the choreography below can see it.
  const [connectTool, setConnectTool] = useState<string | null>(null);
  // THE VEILED WINDOW. While a guide window is up (StepShell `veil`), the room
  // darkens and blurs under its veil and netti steps IN FRONT of it, sharp and
  // awake, just behind the panel, never over the words. Only the layer
  // changes; where netti sits stays the choreography's.
  const [guideVeil, setGuideVeil] = useState(false);
  useEffect(() => {
    const read = () => setGuideVeil(document.documentElement.hasAttribute("data-guide-veil"));
    read();
    window.addEventListener(GUIDE_VEIL_EVENT, read);
    return () => window.removeEventListener(GUIDE_VEIL_EVENT, read);
  }, []);
  // THE NAME THEY CHOSE, live. `/api/onboarding/progress` writes it to the
  // profile server-side, but that only shows on the next load — and the person
  // is looking at the top-right corner right now. This is the same name, held
  // locally until the server's copy comes back around.
  const [chosenName, setChosenName] = useState<string | null>(null);
  const who = chosenName ?? userName;
  const liveConns = useLiveConnections();
  // THE GATE'S LIVE WORD. A connection stands when the page said so (a session
  // at load) or the live list holds one that has made its first call; the walk
  // reads this, never the page's word alone, so a call that lands mid-walk is
  // seen without a reload. `connectedKnown`: the live list has answered at least
  // once (its first answer replaces the empty list it starts with), so the walk
  // can tell "already connected" from "just arrived".
  const firstConns = useRef(liveConns.connections);
  const connectedKnown = liveConns.connections !== firstConns.current;
  const connectedLive = connected || liveConns.connections.some((c) => !!c.first_seen_at);
  // THE DOOR THEY CAME THROUGH. A person who made their account inside a tool
  // and met their netti arrives here with that tool still waiting on its
  // authorization (lib/connect/pendingDoor). The walk's connections stage
  // opens on "let it in"; finishing it takes them back to the tool.
  const pendingDoor = usePendingDoor();

  // where we are, or the projects section, or, if even that is ever missing
  // (a malformed splice), the root itself: the shell always has a level to draw
  // and never dereferences a node that is not there.
  // (a path under /agents whose agent has left the tree falls back to the agents home,
  // not the projects: the person was in the agents space, and the effect below walks the
  // path itself up to where it can stand)
  const current =
    findByPath(tree, path) ??
    (path.startsWith("/agents/") ? findByPath(tree, "/agents") : null) ??
    findByPath(tree, "/projects") ??
    tree;
  const layout = useOsLayout(path);

  const parentP = parentPath(path);
  const parentNode = parentP ? findByPath(tree, parentP) : null;
  // the sibling strip shows only visible siblings; a hidden panel (settings · dev ·
  // activity · parked — reached by path, never in the nav) stands on its own.
  const visibleSiblings = (parentNode?.children ?? []).filter((s) => !s.hidden && !s.offStrip && !isLocked(s.key));
  const siblings = current.hidden || !visibleSiblings.length ? [current] : visibleSiblings;
  const activeIdx = Math.max(0, siblings.findIndex((s) => s.path === current.path));

  // the dedicated Task List view (the "tasks" chip) now owns a project's tasks, so
  // the synthetic "tasks" folder is dropped from the spatial / grid / list surfaces
  // to avoid a duplicate entry. The task data still rides in the tree.
  const dirNode = useMemo(() => {
    // other accounts keep the legacy synthetic "tasks" folder; the dedicated tasks
    // view that replaces it is beta-gated, so only drop the folder when it's on.
    if (!tasksEnabled) return current;
    const kids = current.children ?? [];
    if (!kids.some((c) => c.key === "grp:tasks")) return current;
    return { ...current, children: kids.filter((c) => c.key !== "grp:tasks") };
  }, [current, tasksEnabled]);
  const children = useMemo(() => dirNode.children ?? [], [dirNode]);
  const pages = useMemo(() => children.filter((c) => c.icon === "page"), [children]);
  const hasFolders = children.some((c) => c.icon === "folder");
  // a project carries its commit history — the timeline view reads from it.
  const commits = useMemo(() => current.commits ?? [], [current]);
  const hasTimeline = current.kind === "project" && commits.length > 0;
  // a project root gets the running Task List view — create + check, synced to the
  // same `tasks` the MCP / CLI task tools read and write.
  const isProjectRoot = current.kind === "project" && !!current.projectId;
  // the projects level tells every project as a story, beside orbit (founders, for now)
  const storyLevel = current.path === "/projects" && canSee(F.projectStory, { isAdmin });
  // a code project (or a parent holding code projects) gets its repo view: every
  // commit as branch lanes, with the reason it was made (founders, for now).
  const hasRepo =
    current.kind === "project" &&
    !!current.projectId &&
    canSee(F.projectRepo, { isAdmin }) &&
    (!!current.code || (current.codeChildren ?? 0) > 0 || children.some((c) => c.kind === "project" && !!c.code));

  // ---- chips per level (base order) -----------------------------------------
  const chips: ChipDef[] = useMemo(() => {
    const c: ChipDef[] = [];
    // the spatial "orbit" view leads at ANY level with content — folders, pages,
    // or both (the deep field scatters the whole rooted subtree, so it's never
    // empty-handed). It's offered on mobile too (a narrower ~90° window, scroll
    // to revolve), though the phone still DEFAULTS to grid — orbit is opt-in
    // there (see below).
    // GRID LEADS (John, 2026-09-02) — the leftmost chip is the level's default,
    // and every level now opens as the grid of what is in it. The orbit is one
    // chip away, never the first thing.
    // grid/list only matter when there's content to lay out — an empty level
    // shows no view bar at all (and no "+ new view").
    if (hasFolders || pages.length) c.push({ id: "grid", label: "grid" }, { id: "list", label: "list" });
    if (children.length) c.push({ id: "orbit", label: "orbit" });
    if (storyLevel && children.length) c.push({ id: "story", label: "story" });
    // no "artifacts" chip (John, 18 oct): every page is a document now, and a
    // document opens in the reader from the grid or the list it sits in.
    // NOTE: there is deliberately no "arrange" (id `free`) chip. Offering a
    // by-hand free-scatter at every directory level made each one a layout you
    // had to maintain. The `free`/`home` render path still exists in
    // DirectoryView because a ROOM's desktop (ours) uses it.
    // a project's running task list — its own checklist view (beta-gated account).
    if (isProjectRoot && tasksEnabled) c.push({ id: "tasks", label: "tasks" });
    // timeline rides at the end on a project — its commit history.
    if (hasTimeline) c.push({ id: "timeline", label: "timeline" });
    // …and a code project's repo right after it.
    if (hasRepo) c.push({ id: "repo", label: "repo" });
    return c;
  }, [children.length, hasFolders, pages.length, hasTimeline, isProjectRoot, tasksEnabled, storyLevel, hasRepo]);

  // The level's chip order: a hand-saved order wins; otherwise on a phone we lead
  // with "grid" (far easier to manage on mobile). Press-and-hold a chip to drag
  // this order — and the LEFTMOST chip is always the level's default-selected view.
  const orderedChips: ChipDef[] = useMemo(() => {
    const saved = layout.state.chips;
    if (saved && saved.length) {
      const byId = new Map(chips.map((c) => [c.id, c]));
      const ordered = saved.map((id) => byId.get(id)).filter(Boolean) as ChipDef[];
      const missing = chips.filter((c) => !saved.includes(c.id));
      return [...ordered, ...missing];
    }
    if (isNarrow) {
      const grid = chips.find((c) => c.id === "grid");
      if (grid) return [grid, ...chips.filter((c) => c.id !== "grid")];
    }
    return chips;
  }, [chips, layout.state.chips, isNarrow]);

  const defaultViewId = orderedChips[0]?.id ?? defaultView(current);
  // "free" (arrange) is retired as a directory view. Anyone whose saved selection
  // still points at it would otherwise land on a scatter with no chip to leave by,
  // so it resolves to the level's default instead.
  const selectedView = viewSel === "free" ? defaultViewId : viewSel;
  const rawView = selectedView ?? defaultViewId;
  // mobile keeps the free SCATTER off (its drag-arrange fights touch scroll) → clamp
  // the desktop layout to grid. The orbit IS allowed on mobile (a narrower window).
  const gridView = isNarrow && rawView === "home" ? "grid" : rawView;
  const inViewer = (gridView === "documents" || viewerIndex !== null) && pages.length > 0;

  // the navigator's frosted band is tinted by THE ground gradient, masked to the
  // band's place on screen (law_one_ground_gradient) — publish where it ends.
  const navRef = useRef<HTMLDivElement>(null);
  // the house tail, like every other pinned surface. It carried a 56px one for a
  // day to soften netti's crown against an always-on band; the band ramps now, so
  // at rest there is nothing over the creature at all — and the long tail only
  // dragged the band's fade further down over content that had not moved yet.
  useBandHeight(navRef, !inViewer && !stage);
  // the detached fold — scroll-linked, hover-opened, never a reflow (lib/os/lockFold).
  // On the stage it folds for real: the head shrinks, the content area grows.
  const stageScrollRef = useRef<HTMLDivElement>(null);
  // "layer" (the default) keeps the head's box still, so it folds exactly as the
  // detached fold always has; "split" really shortens it, so it only folds when
  // the content can still be scrolled past the line afterwards (room), and never
  // under reduced motion (the content would jump by the folded height).
  useLockFold(
    navRef,
    stage
      ? stageFold === "split"
        ? { scroller: stageScrollRef, room: true, active: !reduced }
        : { scroller: stageScrollRef, hover: !inViewer }
      : { active: !inViewer },
  );
  // THE SEAM (trial): the head has no band; the content dissolves under it
  // (lib/os/seamMask). `?seam=band` keeps the old band, to compare the two.
  const slideRef = useRef<HTMLDivElement>(null);
  const [seamOn, setSeamOn] = useState(true);
  useEffect(() => {
    try { if (new URLSearchParams(window.location.search).get("seam") === "band") setSeamOn(false); } catch { /* keep the seam */ }
  }, []);
  useSeamMask(navRef, slideRef, { active: !stage && !inViewer && seamOn, key: current.path });
  useAvatarAnchor(avatarRef);
  const docIndex = Math.min(pages.length - 1, Math.max(0, viewerIndex ?? 0));
  // THE STAGE — what the scene is (a place, or a document in it) and which way
  // the directory moved to reach it. Asked of every arrival, whatever caused it
  // (a tile, the crumb, the ticker, a swipe, search, netti, Back, Forward):
  // deeper is forward, out is back, siblings step by their place on the ticker.
  const sceneKey = inViewer ? `${current.path}#${docIndex}` : current.path;
  // the place on screen, as the address names it
  const shownRef = useRef(path);
  shownRef.current = inViewer ? pages[docIndex]?.path ?? path : path;
  const travel = useRef<{ key: string; dir: Dir; swipe: boolean }>({ key: sceneKey, dir: 1, swipe: false });
  const popDir = useRef<Dir | null>(null);
  if (stage && travel.current.key !== sceneKey) {
    const [pPath, pDoc] = travel.current.key.split("#");
    let dir: Dir;
    if (pPath === current.path && pDoc != null && inViewer) dir = wrapStep(Number(pDoc), docIndex, pages.length);
    else if (pPath === current.path && pDoc != null) dir = -1; // out of the reader, back to its folder
    else {
      const rel = relationOf(pPath, current.path, siblings.map((x) => x.path));
      // a nested project has a flat path (/projects/<child>): the tree says
      // which one holds the other, not the string
      const pa = pPath.match(/^\/projects\/([^/]+)$/)?.[1];
      const pb = current.path.match(/^\/projects\/([^/]+)$/)?.[1];
      if (rel.kind === "elsewhere" && pa && pb && ancestorIds(projectsRef.current, pb).includes(pa)) dir = 1;
      else if (rel.kind === "elsewhere" && pa && pb && ancestorIds(projectsRef.current, pa).includes(pb)) dir = -1;
      // Back and Forward between unrelated places travel the way history did
      else if (rel.kind === "elsewhere" && popDir.current != null) dir = popDir.current;
      else dir = rel.dir;
    }
    popDir.current = null;
    travel.current = { key: sceneKey, dir, swipe: navSwipePath.current === current.path };
  }
  // the navigator is one instance per LEVEL on the stage: stepping between
  // siblings keeps it (the ticker glides), a new level brings its own in
  const levelKey = `lvl:${parentPath(current.path) ?? ""}`;

  // leaving the document surface always drops the full-page expansion, so a
  // lingering `expanded` never suppresses the navigator on a non-viewer level.
  useEffect(() => { if (!inViewer) { setExpanded(false); setEditing(false); } }, [inViewer]);

  // ── editing memory (law_edit_frame) ──────────────────────────────────────
  // the page under the caret, and its body as it stood when editing began.
  const editPage = inViewer ? pages[docIndex] : undefined;
  const editingKey = editPage?.key ?? "";
  useEffect(() => {
    if (!editing || !editPage) return;
    if (editSnapshots.current.has(editPage.key)) return;
    editSnapshots.current.set(editPage.key, {
      markdown: docEdits[editPage.key] ?? (editPage.document || editPage.summary || ""),
      at: Date.now(),
      projectId: editPage.projectId,
      name: editPage.editKey,
    });
    // docEdits is read once, at the moment editing begins on this page — a
    // later keystroke must not re-snapshot the edited body.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing, editPage]);
  useEffect(() => {
    if (editing) return;
    editSnapshots.current.clear();
  }, [editing]);
  // while editing, the whole reader is frosted: the ramp is held fully in (the
  // veil, netti's frost, the corner veils all at 1, the constant blur lit) and
  // the organism rests under it; :root[data-editing] lets the chrome honour the
  // same state in CSS (the frame, the scrims). Released the moment edit mode
  // ends, and the ramp continues from wherever the scroll actually is.
  useEffect(() => {
    if (!editing) return;
    const root = document.documentElement;
    root.setAttribute("data-editing", "");
    holdReadRamp(true, "art");
    return () => {
      root.removeAttribute("data-editing");
      holdReadRamp(false, "art");
    };
  }, [editing]);
  // escape = cancel, from anywhere on the page (the caret included).
  useEffect(() => {
    if (!editing || !editingKey) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape" || e.defaultPrevented) return;
      e.preventDefault();
      void cancelDocEdit(editingKey);
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [editing, editingKey, cancelDocEdit]);

  // ---- navigation -----------------------------------------------------------
  // law_snappy — ONE fetch per project subtree in flight, shared by everyone who
  // wants it: the hover that guessed you'd open it, the click that did, the idle
  // warm-up. Whoever asks second rides the first request instead of starting a
  // race (two splices of the same subtree, the later one possibly older).
  //
  // A FORCED read (after a rename, a move, a paste: the memory just changed) is
  // the opposite case. It must not ride a request that left before the change,
  // so it takes over: the older request is cancelled, and if its answer still
  // arrives it is dropped, because a read applies only while it is the newest one
  // started for its project. (An interval read that set off just ahead of a
  // rename used to land after the rename's own read and put the old name back
  // until the next poll.) Whoever was waiting on the older request is handed the
  // newer one's outcome.
  //
  // Every read has a deadline (lib/os/fetchDeadline), so none of this can wait for
  // ever; a read that comes back unauthorised says so (lib/os/outage) and leaves
  // the tree it has loaded exactly as it was.
  const subtreeReqs = useRef(new Map<string, SubtreeReq>());
  const subtreeSeq = useRef(0);
  // the project whose folder could not be opened: the stage says so, calmly, with
  // a way to try again, instead of drawing the unloaded folder as an empty one
  const [openFailed, setOpenFailed] = useState<string | null>(null);
  // the session ended: one quiet note with a way back in; the tree stays as it is
  const [signedOut, setSignedOut] = useState(false);
  useEffect(() => onSignedOut(() => setSignedOut(true)), []);
  useEffect(() => onSignedIn(() => setSignedOut(false)), []);
  const pullSubtree = useCallback((pid: string, o: { loud?: boolean; join?: boolean; signal?: AbortSignal } = {}): Promise<boolean> => {
    const cur = subtreeReqs.current.get(pid);
    if (cur && o.join) return cur.done;
    cur?.ctl.abort();
    const ctl = new AbortController();
    const seq = ++subtreeSeq.current;
    if (o.signal) {
      if (o.signal.aborted) ctl.abort();
      else o.signal.addEventListener("abort", () => ctl.abort(), { once: true });
    }
    const url = `/api/os/project/${pid}`;
    const done = (async (): Promise<boolean> => {
      // the newer read for this project, when one has taken over from this one
      const successor = () => {
        const now = subtreeReqs.current.get(pid);
        return now && now.seq !== seq ? now.done : null;
      };
      try {
        // reachOrReport: a 5xx or a dead network tells the outage window BEFORE we
        // return, so a folder that couldn't be read never draws itself as empty.
        // A speculative read stays silent: the real open reports.
        const res = o.loud
          ? await reachOrReport(url, "your innernet", { signal: ctl.signal })
          : await reachQuietly(url, { signal: ctl.signal });
        if (!res?.ok) return successor() ?? false;
        const sub = (await res.json()) as unknown;
        const newer = successor();
        if (newer) return newer; // an older answer never overwrites a newer read
        if (!isProjectSubtree(sub)) return false;
        const nt = spliceSubtree(treeRef.current, sub);
        treeRef.current = nt;
        setTree(nt);
        setOpenFailed((f) => (f === pid ? null : f));
        setSignedOut(false); // a read landed: the session is good
        return true;
      } catch {
        return successor() ?? false;
      } finally {
        if (subtreeReqs.current.get(pid)?.seq === seq) subtreeReqs.current.delete(pid);
      }
    })();
    subtreeReqs.current.set(pid, { seq, ctl, done });
    return done;
  }, []);
  // the project whose folder is open but still arriving — the stage draws its
  // placeholder for exactly this one (never an empty folder, which would read as
  // "nothing here" for the few hundred ms the subtree is on the wire).
  const [arrivingPid, setArrivingPid] = useState<string | null>(null);
  const ensureProject = useCallback(async (pid: string, opts?: { quiet?: boolean }): Promise<boolean> => {
    const loaded = () => !!findByPath(treeRef.current, `/projects/${pid}`)?.children;
    if (loaded()) return true;
    // asking again IS the retry: the calm "couldn't open" state steps aside
    setOpenFailed((f) => (f === pid ? null : f));
    // quiet = the shell already moved there optimistically: the placeholder is
    // the loading state, so the page is not also dimmed.
    if (opts?.quiet) setArrivingPid(pid);
    else setLoadingN((n) => n + 1);
    try {
      const joinedPrefetch = subtreeReqs.current.has(pid);
      // one budget for the whole open, however many asks it takes
      const startedAt = Date.now();
      const left = () => Math.max(0, OPEN_CAP_MS - (Date.now() - startedAt));
      // the read has its own deadline; `within` is the second lock on the same
      // door, so the dim and the ghosts come down even if something under it held on
      let ok = await within(pullSubtree(pid, { loud: true, join: true }), left(), false);
      // we rode a hover prefetch and it failed silently: the real open asks once
      // more, loudly, so an unreadable folder still reaches the outage window. Only
      // if the failure came quickly: one that spent the deadline has no time left
      // for a second wait, and a folder that is not answering is not helped by one.
      if (!ok && joinedPrefetch && !loaded() && left() > SECOND_ASK_MIN_MS) {
        ok = await within(pullSubtree(pid, { loud: true, join: true }), left(), false);
      }
      // a folder that could not be read is not an empty folder
      if (!loaded()) setOpenFailed(pid);
      return ok || loaded();
    } finally {
      if (opts?.quiet) setArrivingPid((cur) => (cur === pid ? null : cur));
      else setLoadingN((n) => Math.max(0, n - 1));
    }
  }, [pullSubtree]);
  // warm a project's subtree before it's asked for (hover · press · idle).
  const prefetchProject = useCallback((pid: string) => {
    if (findByPath(treeRef.current, `/projects/${pid}`)?.children) return;
    void pullSubtree(pid, { join: true });
  }, [pullSubtree]);
  // the tile under the pointer, when it is a project FOLDER (/projects/<id>).
  const intentPid = (e: React.PointerEvent) => {
    const el = (e.target as Element | null)?.closest?.("[data-node-path]");
    const m = /^\/projects\/([^/]+)$/.exec(el?.getAttribute("data-node-path") ?? "");
    return m ? m[1] : null;
  };
  // a short hover intent, so sweeping the pointer across a row of folders
  // doesn't fetch every one it crosses; a press skips the wait.
  const intentTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onIntentOver = useCallback((e: React.PointerEvent) => {
    const pid = intentPid(e);
    if (intentTimer.current) clearTimeout(intentTimer.current);
    intentTimer.current = pid ? setTimeout(() => prefetchProject(pid), 70) : null;
  }, [prefetchProject]);
  const onIntentOut = useCallback(() => {
    if (intentTimer.current) clearTimeout(intentTimer.current);
    intentTimer.current = null;
  }, []);
  const onIntentDown = useCallback((e: React.PointerEvent) => {
    const pid = intentPid(e);
    if (pid) prefetchProject(pid);
  }, [prefetchProject]);
  // and once the dashboard has settled, quietly warm the few projects you touched
  // most recently — the likeliest next click — one at a time, only when idle.
  useEffect(() => {
    const recent = [...projects]
      .filter((p) => !p.parentId && !(p as RootProject & { parked?: boolean }).parked)
      .sort((a, b) => (b.updated_at ?? "").localeCompare(a.updated_at ?? ""))
      .slice(0, 3)
      .map((p) => p.id);
    let cancelled = false;
    const w = window as Window & { requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => number };
    const idle = (cb: () => void) => (w.requestIdleCallback ? w.requestIdleCallback(cb, { timeout: 4000 }) : setTimeout(cb, 400));
    const start = setTimeout(() => {
      // the document reader's rich-text chunk too, so the first page you open
      // doesn't wait on a download.
      idle(() => {
        if (cancelled) return;
        warmArtifactReader();
        warmByUsage({ ...load, edit: loadArtifactEditor });
      });
      const next = async (i: number) => {
        if (cancelled || i >= recent.length || document.hidden) return;
        if (!findByPath(treeRef.current, `/projects/${recent[i]}`)?.children) await pullSubtree(recent[i], { join: true });
        idle(() => void next(i + 1));
      };
      idle(() => void next(0));
    }, 2500);
    return () => { cancelled = true; clearTimeout(start); };
    // once per mount: the live-projects effect keeps the list current after this.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // force a re-fetch of a project subtree (after a paste mutates it) — unlike
  // ensureProject this never short-circuits on a cached subtree, and it takes over
  // from any read already on the wire (see pullSubtree). A BACKGROUND refresh (a
  // poll, a pulse, the orbit warming) is the polite kind: it rides a read that is
  // already running instead of competing with it. Resolves whether it landed.
  const refreshProject = useCallback(
    (pid: string, o?: { background?: boolean; signal?: AbortSignal }): Promise<boolean> =>
      pullSubtree(pid, { join: !!o?.background, signal: o?.signal }),
    [pullSubtree],
  );

  // THE OPEN PROJECT STAYS CURRENT. While a wired tool is writing a project —
  // the first folder during the walk, or any save afterwards — the view you are
  // standing in refetches its subtree on a quiet cadence (fast during the walk,
  // slow after). Realtime covers the roots list; this covers the dimensions inside
  // a folder you already opened, so nothing ever needs a refresh to show what
  // just landed. The poll (lib/os/poll) is silent while the tab is hidden or the
  // network is gone, backs off to a minute while the server is unwell, and stops
  // the moment you leave the folder, cancelling the read it had on the wire.
  useEffect(() => {
    const segs = path.split("/").filter(Boolean);
    if (segs[0] !== "projects" || !segs[1]) return;
    const pid = segs[1];
    const every = ob && ob.stage !== "free" ? 6000 : 20000;
    const leave = new AbortController();
    const stop = startPoll({ every, run: () => refreshProject(pid, { background: true, signal: leave.signal }) });
    return () => {
      stop();
      leave.abort();
    };
  }, [path, ob, refreshProject]);

  // RECONCILE MOVES / REMOVALS — router.refresh() (after netti moves a project, or
  // another tab acts) re-pulls `projects` carrying fresh parent_project_id. The
  // additive LIVE PROJECTS effect only folds in NEW ids; this one drops a
  // /projects top-level folder once its project became NESTED (a move), parked,
  // or deleted, and refreshes the destination parent's subtree if it's already
  // open — so a netti move is finally felt live instead of leaving a stale
  // (duplicated) tile behind.
  useEffect(() => {
    const section = findByPath(treeRef.current, "/projects");
    if (!section?.children) return;
    const topLevelIds = new Set(
      projects
        .filter((p) => !p.parentId && !(p as RootProject & { parked?: boolean }).parked)
        .map((p) => p.id),
    );
    const gone = section.children.filter((c) => c.kind === "project" && !topLevelIds.has(c.key));
    if (!gone.length) return;
    setTree((prev) => {
      const sec = findByPath(prev, "/projects");
      if (!sec?.children) return prev;
      const kids = sec.children.filter((c) => c.kind !== "project" || topLevelIds.has(c.key));
      if (kids.length === sec.children.length) return prev;
      const nt = spliceSubtree(prev, { ...sec, children: kids, stats: [{ label: kids.length === 1 ? "project" : "projects", value: kids.length }] });
      treeRef.current = nt;
      return nt;
    });
    // a moved project's NEW parent, if its subtree is open, re-fetches so the
    // child shows up nested where it landed.
    for (const c of gone) {
      const parentId = projects.find((p) => p.id === c.key)?.parentId;
      if (parentId && findByPath(treeRef.current, `/projects/${parentId}`)?.children) void refreshProject(parentId);
    }
    firePulse();
  }, [projects, refreshProject, firePulse]);

  // the orbit scatters the whole subtree ROOTED at the current level, so any
  // lazy project folders under it quietly load in the background while the
  // orbit is up — each arriving subtree splices in and deepens the field live.
  // (No loading dim: this is ambient, never a veil.) Fetched once per project,
  // and three at a time: every folder is a full read of a project, and asking
  // for thirty at once held the line for the one you were about to open.
  const orbitPrefetched = useRef(new Set<string>());
  const orbitQueue = useRef<string[]>([]);
  const orbitBusy = useRef(0);
  const pumpOrbit = useCallback(() => {
    while (orbitBusy.current < ORBIT_PREFETCH_AT_ONCE && orbitQueue.current.length) {
      const pid = orbitQueue.current.shift()!;
      orbitBusy.current++;
      void refreshProject(pid, { background: true }).finally(() => {
        orbitBusy.current--;
        pumpOrbit();
      });
    }
  }, [refreshProject]);
  useEffect(() => {
    if (gridView !== "orbit") {
      // left the orbit with folders still waiting their turn: they were never
      // asked for, so forget them and they are queued afresh next time
      for (const pid of orbitQueue.current) orbitPrefetched.current.delete(pid);
      orbitQueue.current = [];
      return;
    }
    const walk = (n: OsNode) => {
      for (const c of n.children ?? []) {
        if (c.hidden) continue;
        if (c.lazy && !c.children && c.projectId && !orbitPrefetched.current.has(c.projectId)) {
          orbitPrefetched.current.add(c.projectId);
          orbitQueue.current.push(c.projectId);
        } else if (c.children) walk(c);
      }
    };
    walk(current);
    pumpOrbit();
  }, [gridView, current, pumpOrbit]);

  // THE LATEST NAVIGATION WINS. goTo can wait (a project's subtree landing
  // before a path inside it resolves), and a wait that finished AFTER a newer
  // click used to land on top of it: open a slow project, then pick "netti" on
  // the rail, and the project's arrival dragged you back. Every goTo (and every
  // Back/Forward) takes a ticket; one that wakes to a newer ticket stands down.
  const navSeq = useRef(0);
  // THE STAGE — a move that lands exactly where the shell already stands (a
  // stale link walked up to here, an ?open= of this very place) changes no
  // state, so nothing downstream runs: release what was waiting on it, and let
  // the address be put right.
  const arrivalReplace = useRef(false);
  const followReplace = useRef(false);
  const [urlTick, setUrlTick] = useState(0);
  const landedWhereWeStand = (p: string, vi: number | null) => {
    const nav = navSync.current;
    if (nav.path !== p || nav.viewerIndex !== vi) return;
    arrivalReplace.current = false;
    followReplace.current = false;
    setUrlTick((t) => t + 1);
  };
  const goTo = useCallback(
    async (asked: string, opts?: { view?: string }) => {
      const seq = ++navSeq.current;
      // an old space word under an agent (overview · projects · with-you · rituals · sub-agents) is the space that holds it now
      let target = agentMovedPath(asked);
      const segs = target.split("/").filter(Boolean);
      // onboarding lock guard — a still-asleep space can't be entered yet.
      if (segs[0] && isLocked(segs[0])) {
        window.dispatchEvent(new CustomEvent("innernet:clip", { detail: { action: "note", name: "not yet — that space is still asleep" } }));
        // the move did not happen: nothing is waiting to be written as a replace
        arrivalReplace.current = false;
        followReplace.current = false;
        setUrlTick((t) => t + 1);
        return;
      }
      // a project opened inside an agent (147) is the same project: it loads exactly as /projects/<id> does
      const inProject = canonicalProjectOf(target);
      if (inProject) {
        const pid = inProject.pid;
        // law_snappy — opening a project FOLDER never waits on the network. Its
        // node is already in the tree (name, colour, summary), so the shell moves
        // there now — breadcrumb, navigator and title answer the click on the
        // same frame — and the subtree fills the stage when it lands. Only a
        // DEEPER target (a page or folder inside the project) must wait, because
        // until the subtree arrives there is nothing to resolve it against.
        const shallow = inProject.depth === 0 && !!findByPath(treeRef.current, `/projects/${pid}`);
        if (shallow) void ensureProject(pid, { quiet: true });
        else {
          // a NESTED project is only in the tree once its parent's contents are.
          // Opening the parent and then the child (the activity repos list does)
          // must let the parent's subtree land first, or the child can't be found.
          // It waits for THAT read (the project's own, and those of the projects
          // it sits inside), never for every unrelated one that happens to be on
          // the wire; and never for longer than an open may take.
          if (!findByPath(treeRef.current, `/projects/${pid}`)) {
            const related = [pid, ...ancestorIds(projectsRef.current, pid)]
              .map((id) => subtreeReqs.current.get(id)?.done)
              .filter((p): p is Promise<boolean> => !!p);
            if (related.length) await within(Promise.all(related), OPEN_CAP_MS, []);
          }
          await ensureProject(pid);
          if (seq !== navSeq.current) return; // a newer click already moved on
        }
      }
      let node = findByPath(treeRef.current, target);
      // an agent just handed in via setAgentRows is in `agentRowsRef` a render before
      // it is in the tree: resolve it there so "make an agent → open its page" lands
      if (!node && segs[0] === "agents" && segs.length > 1 && agentRowsRef.current) {
        const base = findByPath(treeRef.current, "/agents");
        if (base) node = findByPath(agentsSection(base, agentRowsRef.current), target);
      }
      // an agent's own path is a door: it lands on its overview, where its strip is its spaces
      const landing = agentLanding(node);
      if (landing && landing !== node) {
        node = landing;
        target = landing.path;
      }
      // a view is only asked of the level that was named, never of a fallback
      const view = node ? opts?.view : undefined;
      setExpanded(false);
      // UNRESOLVABLE TARGET — a stale/hallucinated netti link, or a project that
      // moved. Landing on a dead path desyncs the shell: `current` silently falls
      // back to the root while the breadcrumb + sibling strip keep deriving from
      // the dead path (the "broken screen" — root content under a /projects nav).
      // Walk up to the nearest ancestor that actually exists and land THERE, so
      // current and path always agree.
      if (!node) {
        let up = parentPath(target);
        while (up && up !== "/" && !findByPath(treeRef.current, up)) up = parentPath(up);
        const safe = up && findByPath(treeRef.current, up) ? up : "/projects";
        target = safe;
        node = findByPath(treeRef.current, target);
      }
      if (node && node.icon === "page") {
        const pp = parentPath(target);
        const pNode = pp ? findByPath(treeRef.current, pp) : null;
        const ps = (pNode?.children ?? []).filter((c) => c.icon === "page");
        const idx = Math.max(0, ps.findIndex((p) => p.path === target));
        landedWhereWeStand(pNode?.path ?? target, idx);
        setPath(pNode?.path ?? target);
        setViewerIndex(idx);
      } else {
        // a requested view: the path effect picks it up on arrival; already here,
        // the effect won't run, so it is set directly.
        if (view) {
          if (target === pathRef.current) setViewSel(view);
          else pendingView.current = view;
        }
        landedWhereWeStand(target, null);
        setPath(target);
        setViewerIndex(null);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ensureProject, isLocked],
  );
  // ── netti's tours (lib/tours) ──────────────────────────────────────────────
  // "welcome to this new update": an update greets once, a place's walk plays the
  // first time you stand there, a guide when a "walk me through it" link asks. Never
  // while the first-run walk owns netti's voice; one tour at a time; what was seen
  // is kept (POST /api/tours) so it greets once on every device.
  const [seenTours, setSeenTours] = useState<Record<string, string>>(toursSeen);
  const [tourOn, setTourOn] = useState<Tour | null>(null);
  const firstRunOwnsNetti = overtureActive || guideActive || mobileParked;
  const seesFeature = useCallback(
    (f: string) => canSee(f, { isAdmin, betaFeatures, gates: featureGates }) && (f !== F.agents || agentsEnabled),
    [isAdmin, betaFeatures, featureGates, agentsEnabled],
  );
  const keepSeen = useCallback((t: Tour, how: "done" | "later" | "made") => {
    const at = new Date().toISOString();
    // made an agent from the walk: the walk is over, but its room should still greet them when they arrive in it
    const seenNow = how === "made" ? { id: t.id, covers: (t.covers ?? []).filter((c) => TOUR_BY_ID.get(c)?.kind !== "space") } : t;
    setSeenTours((m) => markSeen(m, { ...t, ...seenNow } as Tour, at));
    void fetch("/api/tours", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ id: t.id }) }).catch(() => {});
    if (how === "made") {
      for (const c of seenNow.covers ?? []) {
        void fetch("/api/tours", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ id: c }) }).catch(() => {});
      }
    }
  }, []);
  // a tour that is due starts a beat after you arrive, so the place has painted before netti speaks
  useEffect(() => {
    if (tourOn || firstRunOwnsNetti) return;
    const due = dueTour({ seen: seenTours, canSee: seesFeature, path, joinedAt });
    if (!due) return;
    const t = window.setTimeout(() => setTourOn(due), due.kind === "update" ? 1400 : 700);
    return () => window.clearTimeout(t);
  }, [tourOn, firstRunOwnsNetti, seenTours, seesFeature, path, joinedAt]);
  // a "walk me through it" link
  useEffect(() => {
    const onStart = (e: Event) => {
      const id = (e as CustomEvent).detail?.id as string | undefined;
      const t = id ? TOUR_BY_ID.get(id) : undefined;
      if (t && (!t.feature || seesFeature(t.feature)) && !firstRunOwnsNetti) setTourOn(t);
    };
    window.addEventListener(TOUR_EVENT, onStart as EventListener);
    return () => window.removeEventListener(TOUR_EVENT, onStart as EventListener);
  }, [seesFeature, firstRunOwnsNetti]);
  // who a tour is about: the agent you stand in, else the newest agent you keep
  const tourFill = useMemo(() => {
    const rows = agentRowsRef.current ?? [];
    const here = /^\/agents\/([^/]+)/.exec(path)?.[1];
    const pick = (here && rows.find((r) => r.slug === here && !r.parentSlug)) || rows.find((r) => !r.parentSlug) || null;
    return { name: (who ?? "").split(/\s+/)[0]?.toLowerCase() ?? "", agent: pick ? { name: pick.name, path: `/agents/${pick.slug}` } : null };
    // read when a tour starts; the path matters only for which agent a room tour is about
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tourOn?.id]);

  // AN AGENT'S OWN PATH IS A DOOR. Standing on /agents/<slug> itself (an old link, a reload, a tree that learned
  // the agent's spaces after the path was set) would draw the other agents as the strip; the agent lands on its
  // overview instead, as a replace, so Back never returns to the door.
  useEffect(() => {
    const here = findByPath(tree, path);
    const land = agentLanding(here);
    if (!here || !land || land === here) return;
    arrivalReplace.current = true;
    void goTo(land.path);
  }, [tree, path, goTo]);
  // AN AGENT THAT IS GONE. A path under /agents names an agent; if the tree stops
  // holding it (removed here, in another tab, or by an agent's own cascade) the
  // shell must not keep standing on a path that is nothing: `current` has already
  // fallen back to the agents home, and this walks the path itself up to the nearest
  // place that exists. A short grace first, so a goTo that arrives a beat before the
  // rows that hold its agent (setAgentRows) is not bounced on the way in.
  useEffect(() => {
    if (!path.startsWith("/agents/") || findByPath(tree, path)) return;
    const t = setTimeout(() => {
      if (findByPath(treeRef.current, pathRef.current)) return;
      void goTo(parentPath(pathRef.current) ?? "/agents");
    }, 1200);
    return () => clearTimeout(t);
  }, [tree, path, goTo]);

  // ARRIVING ON ONE MEMORY. A link from a chat ("here is exactly what would be
  // forgotten") names a single page; goTo already knows how to load its project
  // and open the reader on it. Once, and then the parameter leaves the address
  // bar so a reload lands where the person now is rather than back on the link.
  // THE STAGE: the address named a place inside a project the tree had not
  // loaded yet. Open it now; the history entry the page loaded on stays the one
  // it is (replaced, never a second entry for the same arrival).
  const placeOpened = useRef(false);
  useEffect(() => {
    if (!arrival?.pending || !initialPlace || placeOpened.current) return;
    placeOpened.current = true;
    arrivalReplace.current = true;
    void goTo(livePath(initialPlace), initialView ? { view: initialView } : undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [arrival, initialPlace, goTo]);
  // …and an address that names a project the tree knows only by name (every
  // project is a lazy folder on first load) opens its contents at once, with the
  // ghost tiles while they come — never an empty folder until the next poll
  useEffect(() => {
    if (!stage || !arrival || arrival.pending) return;
    const at = canonicalProjectOf(arrival.path);
    const pid = at && at.depth === 0 ? at.pid : undefined;
    if (pid && !findByPath(treeRef.current, `/projects/${pid}`)?.children) void ensureProject(pid, { quiet: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openedOnce = useRef(false);
  useEffect(() => {
    if (!initialOpen || openedOnce.current) return;
    openedOnce.current = true;
    // on the stage the arrival is the entry the page loaded on: replaced with the
    // place's own address (which carries no ?open=), never a second entry
    if (stage) arrivalReplace.current = true;
    void goTo(initialOpen, initialOpenView ? { view: initialOpenView } : undefined);
    if (stage) return;
    try {
      const u = new URL(window.location.href);
      if (u.searchParams.has("open")) {
        u.searchParams.delete("open");
        if (initialOpenView) u.searchParams.delete("view");
        window.history.replaceState(window.history.state, "", u.pathname + (u.search || "") + u.hash);
      }
    } catch { /* the address bar is cosmetic here */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialOpen, initialOpenView, goTo]);

  const open = useCallback(
    (child: OsNode) => {
      setSearchHighlight(null); // a manual open isn't a search result — drop any highlight
      if (child.icon === "folder") {
        navDir.current = 1; // descending reads as moving forward
        void goTo(child.path);
      } else {
        const idx = pages.findIndex((p) => p.path === child.path);
        if (idx >= 0) {
          setViewerIndex(idx);
        } else {
          // a DEEP tile (the orbit shows the whole rooted subtree) — dive to the
          // page's own level; goTo lands on its parent with the viewer open on it.
          navDir.current = 1;
          void goTo(child.path);
        }
      }
    },
    [goTo, pages],
  );

  // when netti acts on memory it emits a nav directive — carry the user to the
  // space that changed and refresh the server data so the live update is felt.
  const router = useRouter();
  useEffect(() => {
    const onNav = (e: Event) => {
      const detail = (e as CustomEvent<{ path?: string; view?: string }>).detail;
      const raw = detail?.path;
      if (!raw) return;
      // an optional view of the destination (e.g. { path, view: "repo" })
      const view = typeof detail?.view === "string" ? detail.view : undefined;
      // a path may carry a fragment — `/activity#<id>` from netti's
      // find_activity — the shell lands on the panel, the panel focuses the row.
      const [path, fragment] = raw.split("#");
      if (!path) return;
      // a page that is not a PLACE in the dashboard (docs, community, company, a
      // public page…) opens in its own tab, through netti's UI bridge, instead of
      // goTo stranding the person on /projects. (Mirrors lib/netti/agent SHELL_PATH_RE.)
      const placePath = path.split("?")[0];
      if (!/^\/(projects|you|netti|skills|connections|agents|activity|tasks|parked|settings|ours)(\/|$)/.test(placePath) && placePath !== "/" && placePath !== "/dashboard") {
        window.dispatchEvent(new CustomEvent("innernet:netti-ui", { detail: { open_tab: raw } }));
        return;
      }
      // `/you#fact-<id>` names a record on the private view, not a river row
      const record = path === "/you" ? parseRecordAnchor(fragment) : null;
      if (record) {
        window.dispatchEvent(new CustomEvent(OPEN_RECORD_EVENT, { detail: record }));
        return;
      }
      navDir.current = 1;
      void (async () => {
        if (fragment) {
          // the activity space restores its last view on mount: make that the
          // river, so a row netti points at is never behind the repos list
          // (ActivitySpace's VIEW_KEY). A mounted space hears the event instead.
          try {
            window.sessionStorage.setItem("ac:view", "river");
          } catch {
            /* storage can be unavailable */
          }
          window.dispatchEvent(new CustomEvent("innernet:activity-focus", { detail: { id: fragment } }));
        }
        // no river listening yet (the panel wasn't open): tell it again once it is
        const retell = !!fragment && !document.getElementById(`activity-${fragment}`);
        // land on the target (goTo loads a project subtree if needed, and now
        // falls back to the nearest real ancestor if the path is stale/wrong so
        // netti can never strand you on a dead page — even when it navigates you
        // straight to an artifact or dimension it just located).
        const segs = path.split("/").filter(Boolean);
        // a subtree that was already here may have gone stale; one goTo has just
        // fetched is as fresh as it gets, and is not fetched a second time
        const wasCached = segs[0] === "projects" && !!segs[1] && !!findByPath(treeRef.current, `/projects/${segs[1]}`)?.children;
        await goTo(path, view ? { view } : undefined);
        if (retell) {
          requestAnimationFrame(() =>
            requestAnimationFrame(() => window.dispatchEvent(new CustomEvent("innernet:activity-focus", { detail: { id: fragment } }))),
          );
        }
        // freshen the destination project's subtree so netti carrying you INTO a
        // space shows its current contents (goTo's ensureProject short-circuits a
        // cached one). This is light — a mutation's full top-level re-pull rides
        // the acts handler below, so a pure navigation stays snappy.
        if (wasCached && findByPath(treeRef.current, `/projects/${segs[1]}`)) {
          await refreshProject(segs[1]);
        }
      })();
    };
    window.addEventListener("innernet:netti-nav", onNav);
    return () => window.removeEventListener("innernet:netti-nav", onNav);
  }, [goTo, refreshProject]);

  // THE ONE RECORD (lib/you/record): anything that shows a fact or a pattern
  // away from its home (the inbox, a share card, netti) opens it where it
  // lives, the private view, lit. The you space restores its view on mount and
  // the private view reads the pending focus once its data lands; a mounted one
  // hears `innernet:you-focus` instead. Told again after the panel mounts.
  useEffect(() => {
    const onOpen = (e: Event) => {
      const ref = (e as CustomEvent<RecordRef>).detail;
      if (!ref || (ref.kind !== "fact" && ref.kind !== "pattern") || !ref.id) return;
      try {
        window.sessionStorage.setItem("yo:view", "private");
        window.sessionStorage.setItem("you:private:focus", recordAnchor(ref));
      } catch {
        /* storage can be unavailable */
      }
      const tell = () => window.dispatchEvent(new CustomEvent("innernet:you-focus", { detail: ref }));
      tell();
      navDir.current = 1;
      void (async () => {
        await goTo("/you");
        requestAnimationFrame(() => requestAnimationFrame(tell));
      })();
    };
    window.addEventListener(OPEN_RECORD_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_RECORD_EVENT, onOpen);
  }, [goTo]);

  // NETTI ACTS — the control frame names what netti actually DID (move · rename
  // · delete · park). Replay it as motion so the dashboard is alive under its
  // hands: the tile dissolves where it sits now, and once the refreshed tree
  // paints the destination, the same name blooms back in. Multi-step asks
  // arrive as their final state — one settled picture, animated at the end.
  const arriveRef = useRef<{ name: string; until: number } | null>(null);
  const tileOf = useCallback(
    (name: string) =>
      document.querySelector<HTMLElement>(`[data-node-name="${(window.CSS?.escape ?? ((s: string) => s))(name)}"]`),
    [],
  );
  useEffect(() => {
    const onActs = (e: Event) => {
      const acts = (e as CustomEvent<{ acts?: Array<{ kind?: string; name?: string; to?: string }> }>).detail?.acts ?? [];
      for (const a of acts) {
        if (!a?.name) continue;
        // dissolve the tile at its CURRENT seat (if it's on screen right now)
        const el = tileOf(a.name);
        if (el && a.kind !== "rename") {
          el.animate(
            [
              { opacity: 1, transform: "scale(1)", filter: "blur(0px)" },
              { opacity: 0, transform: "scale(0.55)", filter: "blur(6px)" },
            ],
            { duration: 420, easing: "cubic-bezier(0.4, 0, 0.2, 1)", fill: "forwards" },
          );
        }
        // moves + renames re-arrive under their (new) name once the tree lands
        if (a.kind === "move") arriveRef.current = { name: a.name, until: Date.now() + 8000 };
        else if (a.kind === "rename" && a.to) arriveRef.current = { name: a.to, until: Date.now() + 8000 };
      }
      if (acts.length) {
        firePulse(); // netti's hands fire the organism too
        // a mutation changed the server data — re-pull it so the reconcile effect
        // (which removes a moved project from its old top-level spot, etc.) sees
        // the fresh parent_project_id. Only mutations carry acts, so a pure
        // navigation never triggers this.
        router.refresh();
      }
    };
    window.addEventListener("innernet:netti-acts", onActs as EventListener);
    return () => window.removeEventListener("innernet:netti-acts", onActs as EventListener);
  }, [firePulse, router]);
  // once the refreshed tree paints the destination, bloom the arrived tile in.
  useEffect(() => {
    const pending = arriveRef.current;
    if (!pending) return;
    if (Date.now() > pending.until) { arriveRef.current = null; return; }
    // wait a frame so the fresh tiles exist before we look for the arrival
    const raf = requestAnimationFrame(() => {
      const el = tileOf(pending.name);
      if (!el) return; // not painted yet — the next tree/path change retries
      arriveRef.current = null;
      // release any lingering dissolve fill — the SAME element may host the
      // arrival when the view didn't remount (e.g. a move replayed in place).
      el.getAnimations().forEach((a) => a.cancel());
      el.animate(
        [
          { opacity: 0, transform: "scale(0.55)", filter: "blur(8px)" },
          { opacity: 1, transform: "scale(1)", filter: "blur(0px)" },
        ],
        { duration: 640, easing: "cubic-bezier(0.22, 1, 0.36, 1)" },
      );
    });
    return () => cancelAnimationFrame(raf);
  }, [tree, path, loading]);

  // LIVE REACTIVITY — the dashboard reflects a change the instant it lands,
  // wherever it came from: a fold enriching a dimension, a capture, an edit or
  // sync from another tab or tool. Those all write an activity row, which
  // useLiveMemory turns into a `pulse`. On each pulse we freshen what's ON
  // SCREEN — re-fetch the open project's subtree (its dimensions/artifacts may
  // have just changed; the projects-table subscription in DashboardClient never
  // sees a fold, since a fold touches dimensions/commits, not the project row)
  // and re-pull the server props (counts, the home + projects lists). Debounced
  // so a burst folds into one refresh; the mount baseline pulse is skipped.
  //
  // law_snappy — the open subtree refreshes on every pulse, but the WHOLE-PAGE
  // re-pull (router.refresh re-runs the server loader and re-renders the entire
  // dashboard with fresh props) is throttled to one per SERVER_REFRESH_EVERY.
  // netti fires a pulse after every answer, so a conversation was re-pulling the
  // whole dashboard every few seconds, under whatever you were clicking.
  const SERVER_REFRESH_EVERY = 6000;
  const livePulseRef = useRef(live.pulse);
  const liveRefreshTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // a pulse whose refresh has not run yet. The effect below is torn down and set
  // up again whenever the path changes, which used to throw a waiting refresh away
  // for good (the pulse was already marked seen): a level opened inside the 400ms
  // window stayed stale until the next pulse or the 20s poll.
  const livePending = useRef(false);
  const serverRefreshAt = useRef(0);
  const serverRefreshTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => { if (serverRefreshTimer.current) clearTimeout(serverRefreshTimer.current); }, []);
  useEffect(() => {
    if (livePulseRef.current !== live.pulse) {
      livePulseRef.current = live.pulse;
      livePending.current = true;
    }
    if (!livePending.current) return;
    if (liveRefreshTimer.current) clearTimeout(liveRefreshTimer.current);
    liveRefreshTimer.current = setTimeout(() => {
      livePending.current = false;
      // whatever level is open NOW, not the one the pulse arrived on
      const pid = findByPath(treeRef.current, path)?.projectId;
      // (forced, not background: the pulse means something JUST changed, so it must
      // not settle for a read that left before it did)
      if (pid) void refreshProject(pid);
      // leading edge now, then at most one trailing re-pull per window — a burst
      // still ends with the page current, just never more often than this.
      if (serverRefreshTimer.current) return;
      const wait = Math.max(0, serverRefreshAt.current + SERVER_REFRESH_EVERY - Date.now());
      serverRefreshTimer.current = setTimeout(() => {
        serverRefreshTimer.current = null;
        serverRefreshAt.current = Date.now();
        router.refresh();
      }, wait);
    }, 400);
    return () => {
      if (liveRefreshTimer.current) clearTimeout(liveRefreshTimer.current);
    };
  }, [live.pulse, path, refreshProject, router]);

  // INLINE RENAME (the inline-edit law). Optimistic-ish: PATCH the row, then
  // refresh the subtree (authoritative) + pulse the organism. Errors surface in
  // the StatusCorner. Used by the views (commitRename, which records undo) and by
  // ⌘Z/⇧⌘Z (applyRename directly, no re-record).
  const applyRename = useCallback(
    (o: { key: string; projectId: string; name: string }) => {
      void (async () => {
        try {
          const res = await fetch("/api/os/rename", {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ key: o.key, projectId: o.projectId, name: o.name }),
          });
          const out = (await res.json().catch(() => ({}))) as { error?: string };
          if (res.ok) {
            if (o.projectId) await refreshProject(o.projectId);
            firePulse();
          } else {
            window.dispatchEvent(new CustomEvent("innernet:clip", { detail: { action: "note", name: out.error || "couldn't rename" } }));
          }
        } catch {
          window.dispatchEvent(new CustomEvent("innernet:clip", { detail: { action: "note", name: "couldn't rename — offline?" } }));
        }
      })();
    },
    [refreshProject, firePulse],
  );
  const flashNote = useCallback((name: string) => {
    window.dispatchEvent(new CustomEvent("innernet:clip", { detail: { action: "note", name } }));
  }, []);

  // remove a project node wherever it sits (root section or nested under a parent).
  const removeProjectNode = useCallback((pid: string) => {
    setTree((prev) => {
      const walk = (n: OsNode): OsNode => {
        if (!n.children) return n;
        const kids = n.children.filter((c) => c.key !== pid).map(walk);
        return n.path === "/projects"
          ? { ...n, children: kids, stats: [{ label: kids.length === 1 ? "project" : "projects", value: kids.length }] }
          : { ...n, children: kids };
      };
      const nt = walk(prev);
      treeRef.current = nt;
      return nt;
    });
  }, []);

  // ── PARK / UNPARK ──────────────────────────────────────────────────────────
  // parked nodes we removed this session, kept so an unpark (or an undo) can put
  // the exact folder back without a refetch.
  const parkedStash = useRef<Map<string, OsNode>>(new Map());

  // keep a data-backed /parked node honest after a park/unpark. That panel is a
  // sibling surface which shapes its own node — we only refresh its children
  // when it already carries some (be defensive about its shape).
  const refreshParked = useCallback(async () => {
    if (!findByPath(treeRef.current, "/parked")?.children) return;
    try {
      const res = await fetch("/api/os/parked");
      if (!res.ok) return;
      const data = (await res.json().catch(() => null)) as { ok?: boolean; parked?: RootProject[] } | null;
      if (!data?.ok || !Array.isArray(data.parked)) return;
      const kids = data.parked.map((p) => ({ ...projectFolderNode(p), parked: true } as OsNode));
      setTree((prev) => {
        const node = findByPath(prev, "/parked");
        if (!node?.children) return prev;
        const nt = spliceSubtree(prev, { ...node, children: kids, stats: [{ label: kids.length === 1 ? "project" : "projects", value: kids.length }] });
        treeRef.current = nt;
        return nt;
      });
    } catch { /* ignore */ }
  }, []);

  // put a project back at the top level of /projects — from the session stash if
  // we removed it ourselves, else refetched (an unpark from another tab, an undo
  // of a nest). No-op when the folder is already there.
  const restoreTopLevel = useCallback(async (projectId: string) => {
    let node = parkedStash.current.get(projectId) ?? null;
    if (!node) {
      try {
        const r = await fetch(`/api/os/project/${projectId}`);
        if (r.ok) node = (await r.json()) as OsNode;
      } catch { /* ignore */ }
    }
    if (!node) return;
    parkedStash.current.delete(projectId);
    const restored = { ...node, parked: false } as OsNode;
    setTree((prev) => {
      const section = findByPath(prev, "/projects");
      if (!section || (section.children ?? []).some((c) => c.key === projectId)) return prev;
      const kids = [restored, ...(section.children ?? [])];
      const nt = spliceSubtree(prev, { ...section, children: kids, stats: [{ label: kids.length === 1 ? "project" : "projects", value: kids.length }] });
      treeRef.current = nt;
      return nt;
    });
  }, []);

  // the one lifecycle toggle behind the options menu, the settings panel, and
  // undo/redo: flip projects.archived via /api/os/appearance and keep the tree
  // honest on both sides — /projects loses/regains the folder, /parked refreshes.
  // Returns success so callers can record the op and narrate in their own voice.
  const parkProject = useCallback(
    async (projectId: string, park: boolean): Promise<boolean> => {
      try {
        const res = await fetch("/api/os/appearance", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ projectId, park }),
        });
        const out = (await res.json().catch(() => ({}))) as { error?: string };
        if (!res.ok) {
          flashNote(out.error || (park ? "couldn't park that" : "couldn't bring it back"));
          return false;
        }
      } catch {
        flashNote("couldn't park — offline?");
        return false;
      }
      if (park) {
        const node = findByPath(treeRef.current, `/projects/${projectId}`);
        if (node) parkedStash.current.set(projectId, node);
        removeProjectNode(projectId);
        // parking the project you're standing in — step back home.
        if (path.startsWith(`/projects/${projectId}`)) void goTo("/projects");
      } else {
        await restoreTopLevel(projectId);
      }
      await refreshParked();
      firePulse();
      return true;
    },
    [flashNote, removeProjectNode, restoreTopLevel, refreshParked, firePulse, goTo, path],
  );

  // ── UNDO / REDO ────────────────────────────────────────────────────────────
  // replay a recorded MOVE (undo = back to where it came from, redo = forward
  // again). Projects (bare-id keys) re-nest via /api/os/move; items re-issue the
  // same /api/os/paste. The server stays the source of truth — we just refetch
  // the affected subtrees after.
  const applyMove = useCallback(
    (op: Extract<EditOp, { kind: "move" }>, dir: "undo" | "redo") => {
      const back = dir === "undo";
      const leaving = back ? op.toProjectId : op.fromProjectId;
      const entering = back ? op.fromProjectId : op.toProjectId;
      void (async () => {
        try {
          if (!op.sourceKey.includes(":")) {
            // a whole PROJECT — re-nest (or un-nest to the top with parentId null).
            const res = await fetch("/api/os/move", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ projectId: op.sourceKey, parentId: entering || null }),
            });
            const out = (await res.json().catch(() => ({}))) as { error?: string };
            if (!res.ok) { flashNote(out.error || "couldn't move it back"); return; }
            if (leaving) await refreshProject(leaving);
            else removeProjectNode(op.sourceKey);
            if (entering) await refreshProject(entering);
            else await restoreTopLevel(op.sourceKey);
          } else {
            const res = await fetch("/api/os/paste", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                sourceKey: op.sourceKey,
                sourcePath: back ? op.toParentPath : op.fromParentPath,
                destPath: back ? op.fromParentPath : op.toParentPath,
                destProjectId: entering,
                mode: "move",
              }),
            });
            const out = (await res.json().catch(() => ({}))) as { error?: string };
            if (!res.ok) { flashNote(out.error || "couldn't move it back"); return; }
            if (leaving && leaving !== entering) await refreshProject(leaving);
            if (entering) await refreshProject(entering);
          }
          firePulse();
          flashNote(back ? `undid — moved ${op.name} back` : `redid — moved ${op.name}`);
        } catch {
          flashNote("couldn't move — offline?");
        }
      })();
    },
    [flashNote, refreshProject, removeProjectNode, restoreTopLevel, firePulse],
  );

  // the one dispatcher every recorded op replays through — ⌘Z/⇧⌘Z and the
  // options-menu undo/redo buttons both land here, routed by op kind.
  const applyOp = useCallback(
    (op: EditOp, dir: "undo" | "redo") => {
      const back = dir === "undo";
      if (op.kind === "rename") {
        applyRename({ key: op.key, projectId: op.projectId, name: back ? op.before : op.after });
        flashNote(back ? `undid — renamed back to ${op.before}` : `redid — renamed to ${op.after}`);
      } else if (op.kind === "move") {
        applyMove(op, dir);
      } else {
        const park = back ? !op.parked : op.parked;
        void (async () => {
          if (await parkProject(op.projectId, park)) {
            flashNote(`${back ? "undid" : "redid"} — ${park ? `parked ${op.name}` : `brought ${op.name} back`}`);
          }
        })();
      }
    },
    [applyRename, applyMove, parkProject, flashNote],
  );
  const editStack = useEditStack(applyOp);

  // the options-menu undo/redo pair — successful replays narrate themselves
  // (applyOp above); an empty stack side says so here.
  const undoRedo = useMemo(
    () => ({
      canUndo: editStack.canUndo,
      canRedo: editStack.canRedo,
      onUndo: () => { if (!editStack.undo()) flashNote("nothing to undo"); },
      onRedo: () => { if (!editStack.redo()) flashNote("nothing to redo"); },
    }),
    [editStack, flashNote],
  );

  const commitRename = useCallback(
    (child: OsNode, name: string) => {
      if (!child.projectId) return;
      const next = name.trim();
      if (!next || next === child.name.trim()) return;
      editStack.push({ kind: "rename", key: child.key, projectId: child.projectId, before: child.name, after: next });
      applyRename({ key: child.key, projectId: child.projectId, name: next });
    },
    [editStack, applyRename],
  );

  // DRAG TO MOVE — drop one thing onto a folder to MOVE it there (never a copy).
  // Two cases: (1) a PROJECT dropped onto another project → nest it as a subproject;
  // (2) an ITEM (node / page) dropped into a folder → reparent the row, same project
  // OR across projects. Returns true if a move was started (so the tile doesn't also
  // reposition). The corner narrates every step as a MOVE.
  const moveInto = useCallback(
    (source: OsNode, dest: OsNode): boolean => {
      if (source.key === dest.key || source.path === dest.path) return false;
      if (dest.icon !== "folder") return false;
      // never drop something into its own descendant (a cycle).
      if (dest.path.startsWith(source.path + "/")) return false;

      // (1) PROJECT → PROJECT: nest as a subproject (persisted via parent_project_id).
      // Remove the moved project from the root /projects section and refresh the
      // destination so it shows up nested inside it.
      if (source.kind === "project" && dest.kind === "project") {
        if (!source.projectId || !dest.projectId) return false;
        const movedId = source.projectId;
        const destId = dest.projectId;
        // where it sits NOW (root section or a parent project) — recorded so ⌘Z
        // can un-nest it back to exactly there.
        const prevParent = findParentOf(treeRef.current, movedId);
        window.dispatchEvent(new CustomEvent("innernet:clip", { detail: { action: "note", name: `moving ${source.name} into ${dest.name}…` } }));
        void (async () => {
          try {
            const res = await fetch("/api/os/move", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ projectId: movedId, parentId: destId }),
            });
            const out = (await res.json().catch(() => ({}))) as { error?: string };
            if (res.ok) {
              setTree((prev) => {
                const section = findByPath(prev, "/projects");
                if (!section) return prev;
                const kids = (section.children ?? []).filter((c) => c.key !== movedId);
                const next: OsNode = { ...section, children: kids, stats: [{ label: kids.length === 1 ? "project" : "projects", value: kids.length }] };
                const nt = spliceSubtree(prev, next);
                treeRef.current = nt;
                return nt;
              });
              await refreshProject(destId);
              firePulse();
              // re-pull the server projects list too — the HOME desktop renders
              // from that prop, so a move begun there sheds its tile live.
              router.refresh();
              editStack.push({
                kind: "move",
                sourceKey: movedId,
                name: source.name,
                fromProjectId: prevParent?.projectId ?? "",
                toProjectId: destId,
                fromParentPath: prevParent?.path ?? "/projects",
                toParentPath: dest.path,
              });
              window.dispatchEvent(new CustomEvent("innernet:clip", { detail: { action: "moved", name: source.name, folder: dest.name } }));
            } else {
              window.dispatchEvent(new CustomEvent("innernet:clip", { detail: { action: "note", name: out.error || "couldn't nest there" } }));
            }
          } catch {
            window.dispatchEvent(new CustomEvent("innernet:clip", { detail: { action: "note", name: "couldn't move — offline?" } }));
          }
        })();
        return true;
      }

      // (2) ITEM → FOLDER: reparent the row (cross-project allowed).
      if (!source.projectId || !dest.projectId) return false;
      window.dispatchEvent(new CustomEvent("innernet:clip", { detail: { action: "note", name: `moving ${source.name} → ${dest.name}…` } }));
      void (async () => {
        try {
          const res = await fetch("/api/os/paste", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ sourceKey: source.key, sourcePath: source.path, destPath: dest.path, destProjectId: dest.projectId, mode: "move" }),
          });
          const out = (await res.json().catch(() => ({}))) as { error?: string };
          if (res.ok) {
            if (source.projectId && source.projectId !== dest.projectId) await refreshProject(source.projectId);
            await refreshProject(dest.projectId!);
            firePulse();
            editStack.push({
              kind: "move",
              sourceKey: source.key,
              name: source.name,
              fromProjectId: source.projectId!,
              toProjectId: dest.projectId!,
              fromParentPath: parentPath(source.path) ?? source.path,
              toParentPath: dest.path,
            });
            window.dispatchEvent(new CustomEvent("innernet:clip", { detail: { action: "moved", name: source.name, folder: dest.name } }));
          } else {
            window.dispatchEvent(new CustomEvent("innernet:clip", { detail: { action: "note", name: out.error || "couldn't move there" } }));
          }
        } catch {
          window.dispatchEvent(new CustomEvent("innernet:clip", { detail: { action: "note", name: "couldn't move — offline?" } }));
        }
      })();
      return true;
    },
    [refreshProject, firePulse, editStack, router],
  );

  // ── project settings (folder appearance + lifecycle) ──────────────────────
  const [psTarget, setPsTarget] = useState<PSTarget | null>(null);
  // folder-create: opening the composer in create mode over a parent (null = top-level).
  // `onboarding`: today's walk opens it as its create step (lib/os/onboarding.v1), with a skip
  const [fcCreate, setFcCreate] = useState<{ parentId: string | null; onboarding?: boolean } | null>(null);

  // drop a freshly-created top-level project onto the /projects section, newest first.
  const addTopLevelProject = useCallback((p: RootProject) => {
    knownProjects.current.add(p.id);
    setTree((prev) => {
      const section = findByPath(prev, "/projects");
      if (!section) return prev;
      const kids = [projectFolderNode(p), ...(section.children ?? [])];
      const next: OsNode = { ...section, children: kids, stats: [{ label: kids.length === 1 ? "project" : "projects", value: kids.length }] };
      const nt = spliceSubtree(prev, next);
      treeRef.current = nt;
      return nt;
    });
    firePulse();
  }, [firePulse]);

  // create a new folder (project / subproject) or page (dimension) in `container`.
  // Everything under a project carries projectId; panels/sections/root don't — so a
  // null parentId means "make it top-level".
  const createInto = useCallback(async (kind: "folder" | "file", container: OsNode) => {
    const parentId = container.projectId ?? null;
    if (kind === "file" && !parentId) { flashNote("open a project to add a page inside it"); return; }
    // folders open the composer (name · colour · icon · description · auto) rather
    // than dropping an instant "untitled". Pages stay instant.
    if (kind === "folder") { setFcCreate({ parentId }); return; }
    // page (file) — instant, named "untitled", double-click to rename.
    try {
      const res = await fetch("/api/os/create", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ kind, parentId, name: "untitled" }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { flashNote(data?.error || "couldn't create that"); return; }
      if (parentId) {
        await refreshProject(parentId);
        firePulse();
        flashNote("new page — double-click to rename");
      }
    } catch {
      flashNote("couldn't create that — try again");
    }
  }, [refreshProject, firePulse, flashNote]);

  const onContextAction = useCallback((action: ContextAction, node: OsNode) => {
    if (action === "settings" && node.projectId) {
      setPsTarget({ projectId: node.projectId, name: node.name, symbol: node.symbol, color: node.color ?? null, icon: node.iconKey ?? null, tagline: node.summary ?? null });
    } else if (action === "new-folder") {
      void createInto("folder", node);
    } else if (action === "new-file") {
      void createInto("file", node);
    } else if (action === "park") {
      if (node.kind !== "project" || !node.projectId) return;
      // OsNode is gaining an optional `parked` flag (the parked surface) — read
      // it tolerantly so this toggles correctly before and after.
      const park = !(node as OsNode & { parked?: boolean }).parked;
      const pid = node.projectId;
      void (async () => {
        if (await parkProject(pid, park)) {
          editStack.push({ kind: "park", projectId: pid, name: node.name, parked: park });
          flashNote(park ? `parked ${node.name} — find it under parked` : `brought ${node.name} back`);
        }
      })();
    } else if (action === "move") {
      setMvSource(node);
    } else if (action === "icon") {
      setIconTarget(node);
    }
  }, [createInto, flashNote, parkProject, editStack]);

  // panels outside the shell's own views (the home desktop) hand their context
  // actions over through this event — same MovePicker, same FolderComposer,
  // same park toggle, wherever the right-click happened.
  useEffect(() => {
    const onCtx = (e: Event) => {
      const d = (e as CustomEvent<{ action?: ContextAction; node?: OsNode }>).detail;
      if (d?.action && d?.node) onContextAction(d.action, d.node);
    };
    window.addEventListener("innernet:os-context-action", onCtx as EventListener);
    return () => window.removeEventListener("innernet:os-context-action", onCtx as EventListener);
  }, [onContextAction]);

  // ── ICON PICKER (the context menu's "Icon…") ───────────────────────────────
  // Any folder/page can wear an emoji: a project persists it on its row (the
  // icon follows the project everywhere, cross-device); anything else persists
  // through the level's layout store (the same manual-law channel as summaries).
  // (pickIconFor lives below, after patchProjectNode is declared.)
  const [iconTarget, setIconTarget] = useState<OsNode | null>(null);
  // has an icon ever been asked for? Until it has, the picker is not even loaded.
  const [iconEver, setIconEver] = useState(false);
  if (iconTarget && !iconEver) setIconEver(true);
  useEffect(() => { if (iconTarget) noteUse("emoji"); }, [iconTarget]);

  // ── MOVE PICKER (the context menu's "Move…") ───────────────────────────────
  // The picker only chooses the destination; the actual move rides the same
  // persisted paths as drag-and-drop (moveInto → /api/os/move | /api/os/paste),
  // so undo, narration and refresh behave identically however the move began.
  const [mvSource, setMvSource] = useState<OsNode | null>(null);
  // un-nest a subproject back to the top level of /projects.
  const moveToTop = useCallback((source: OsNode) => {
    if (source.kind !== "project" || !source.projectId) return;
    const pid = source.projectId;
    const prevParent = findParentOf(treeRef.current, pid);
    void (async () => {
      try {
        const res = await fetch("/api/os/move", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ projectId: pid, parentId: null }),
        });
        const out = (await res.json().catch(() => ({}))) as { error?: string };
        if (!res.ok) { flashNote(out.error || "couldn't move it out"); return; }
        if (prevParent?.projectId) await refreshProject(prevParent.projectId);
        else removeProjectNode(pid);
        await restoreTopLevel(pid);
        firePulse();
        editStack.push({
          kind: "move",
          sourceKey: pid,
          name: source.name,
          fromProjectId: prevParent?.projectId ?? "",
          toProjectId: "",
          fromParentPath: prevParent?.path ?? "/projects",
          toParentPath: "/projects",
        });
        window.dispatchEvent(new CustomEvent("innernet:clip", { detail: { action: "moved", name: source.name, folder: "top level" } }));
      } catch {
        window.dispatchEvent(new CustomEvent("innernet:clip", { detail: { action: "note", name: "couldn't move — offline?" } }));
      }
    })();
  }, [refreshProject, removeProjectNode, restoreTopLevel, firePulse, editStack, flashNote]);
  // optimistic tree edits for a settings change — update / remove the project node
  // wherever it sits (root section or nested under a parent).
  const patchProjectNode = useCallback((pid: string, patch: { name?: string; color?: string | null; icon?: string | null; tagline?: string | null }) => {
    setTree((prev) => {
      const walk = (n: OsNode): OsNode => {
        let next: OsNode = n;
        if (n.key === pid) {
          next = { ...n };
          if (patch.name !== undefined) next.name = patch.name;
          if (patch.color !== undefined) next.color = patch.color;
          if (patch.icon !== undefined) { next.iconKey = patch.icon; next.symbol = symbolFor({ kind: "project", name: next.name }, patch.icon); }
          else if (patch.name !== undefined && !next.iconKey) next.symbol = symbolFor({ kind: "project", name: next.name }, null);
        }
        if (n.children) next = { ...next, children: n.children.map(walk) };
        return next;
      };
      const nt = walk(prev);
      treeRef.current = nt;
      return nt;
    });
  }, []);

  // commit a picked emoji onto its node (see the ICON PICKER note above).
  const pickIconFor = useCallback(
    (node: OsNode, emoji: string) => {
      if (node.kind === "project" && node.projectId) {
        patchProjectNode(node.projectId, { icon: emoji });
        void fetch("/api/os/appearance", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ projectId: node.projectId, icon: emoji }),
        }).catch(() => {
          /* optimistic — the tile already updated */
        });
      } else {
        layout.setSymbol(node.key, emoji);
      }
      firePulse();
      window.dispatchEvent(new CustomEvent("innernet:clip", { detail: { action: "note", name: `${node.name} wears ${emoji}` } }));
    },
    [patchProjectNode, layout, firePulse],
  );
  // a new space opens at its top. The page kept the scroll it had: deep in the
  // private record, picking "netti" landed you partway down netti (or, on a short
  // page, clamped somewhere that looked like nothing had happened). Consumed on
  // the commit that actually changes the level, before paint.
  const scrollHome = useRef(false);
  useLayoutEffect(() => {
    if (!scrollHome.current) return;
    scrollHome.current = false;
    // on the stage every new place opens at its top on its own (components/stage)
    if (stage) return;
    if (window.scrollY > 0) window.scrollTo({ top: 0, behavior: "instant" as ScrollBehavior });
  }, [current.path]);
  const selectSibling = useCallback((key: string) => {
    const idx = siblings.findIndex((x) => x.key === key);
    if (idx < 0) return;
    const n = siblings.length;
    const delta = idx - activeIdx;
    // a wrap step (last→first / first→last) keeps the SAME visual direction rather
    // than reading as a long jump backwards.
    navDir.current = Math.abs(delta) === n - 1 ? (delta > 0 ? -1 : 1) : Math.sign(delta) || 1;
    navSwipePath.current = siblings[idx].path;
    scrollHome.current = true;
    void goTo(siblings[idx].path);
  }, [siblings, activeIdx, goTo]);

  // ── per-view reveal ─────────────────────────────────────────────────────────
  // While the onboarding odyssey is live (ob != null), each level's views that
  // haven't been opened yet carry a slight shimmer; opening one reveals it and —
  // once graduated (free) — netti names it. The view you're already looking at is
  // never shimmered. Keyed per level so each space reveals its own views.
  const revealViews = ob != null;
  const [seenViews, setSeenViews] = useState<Set<string>>(() => new Set());
  const viewKey = useCallback((id: string) => `${current.key}::${id}`, [current.key]);

  useEffect(() => {
    if (!revealViews) return;
    const k = `${current.key}::${rawView}`;
    setSeenViews((s) => (s.has(k) ? s : new Set(s).add(k)));
  }, [revealViews, current.key, rawView]);

  const unseenViews = useMemo(() => {
    if (!revealViews) return undefined;
    const set = new Set<string>();
    for (const c of orderedChips) if (!seenViews.has(`${current.key}::${c.id}`)) set.add(c.id);
    return set;
  }, [revealViews, orderedChips, seenViews, current.key]);

  const onView = useCallback(
    (id: string) => {
      setViewSel(id); // transient — the level's default stays the leftmost chip
      if (id !== "documents") setViewerIndex(null);
      else setViewerIndex((v) => v ?? 0);
      // reveal: mark seen, and once graduated netti names the view on first open
      // (during active stages the conductor owns the corner, so we stay quiet).
      if (revealViews) {
        const k = `${current.key}::${id}`;
        setSeenViews((s) => {
          if (s.has(k)) return s;
          if (ob?.stage === "free" && VIEW_INTRO[id]) {
            narrate({ id: `view.${id}`, text: VIEW_INTRO[id], sticky: false });
            // only this line: a tour asking meanwhile keeps its chips (lib/os/noticer)
            window.setTimeout(() => narrateClear(`view.${id}`), 5200);
          }
          return new Set(s).add(k);
        });
      }
    },
    [revealViews, ob?.stage, current.key],
  );

  // ---- one swipe model: docs when a reader is open, else siblings ----
  // siblings WRAP both ways — swiping right on the first section lands on the last
  // (settings), and past the last wraps back to the first. Same navigator behavior
  // everywhere, on desktop and mobile.
  const goSibling = (delta: number) => {
    if (siblings.length < 2) return;
    navDir.current = delta >= 0 ? 1 : -1;
    const i = (activeIdx + delta + siblings.length) % siblings.length;
    navSwipePath.current = siblings[i].path;
    scrollHome.current = true;
    void goTo(siblings[i].path);
  };
  const goDoc = (delta: number) =>
    setViewerIndex((v) => Math.max(0, Math.min(pages.length - 1, (v ?? 0) + delta)));
  const { bind } = useHSwipe({
    onPrev: () => (inViewer ? goDoc(-1) : goSibling(-1)),
    onNext: () => (inViewer ? goDoc(1) : goSibling(1)),
    enabled: !expanded,
  });

  // ---- breadcrumb -----------------------------------------------------------
  const crumbs: Crumb[] = useMemo(() => {
    const chain: string[] = [];
    let pp = parentPath(path);
    while (pp && pp !== "/") {
      chain.unshift(pp);
      pp = parentPath(pp);
    }
    const out: Crumb[] = chain
      .map((cp) => {
        const n = findByPath(tree, cp);
        return n ? { label: n.name, onClick: () => { navDir.current = -1; void goTo(cp); } } : null;
      })
      .filter(Boolean) as Crumb[];
    // In the READER the navigator folds away as you read (the chips and the
    // level's own name go with it), so the breadcrumb has to carry the depth
    // instead — it grows to name the folder you are reading inside. Without
    // this, a collapsed navigator leaves you holding a document with no sense
    // of where it lives.
    if (inViewer) {
      const here = findByPath(tree, path);
      // an artifact name is agent-authored and unbounded — let it ellipsize
      if (here) out.push({ label: here.name, truncate: true });
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, tree, inViewer]);

  // ── section menu (the dashboard's ≡ — mirrors the landing menu) ──
  const [menuOpen, setMenuOpen] = useState(false);
  // the active top-level section = the first path segment (matches a panel key;
  // /projects/<id>/… collapses to "projects").
  const menuActiveKey = path.split("/").filter(Boolean)[0] ?? "home";
  // the menu is a swiper of SPACES. Everyone gets "spaces" (the sections). A
  // the ONE menu truth: this dashboard's own space ("yours" — its sections) +
  // the public "innernet" pages + (for founders) the founders' tools. Same
  // buildSpaces every surface uses. No nav → innernet/founders hard-navigate out
  // of the shell, and the netti-lab item opens its modal IN PLACE (its event
  // fires; with no router it doesn't also route away).
  // "yours" is a real TREE: each section (home · projects · …) drills into its own
  // contents (the project list, the desktop folders) two levels deep — exactly the
  // breadcrumb navigator innernet › yours › projects › <project>. Single-click a
  // directory drills; double-click opens it.
  // During the onboarding odyssey `isLocked` hides not-yet-unlocked spaces so the
  // menu only ever shows what the user has reached.
  const menu = useMemo(() => {
    const rootNode = findByPath(tree, "/");
    const openable = (c: OsNode) =>
      (c.kind === "panel" || c.kind === "section" || c.kind === "project" || c.kind === "group") &&
      !isLocked(c.key);
    // full depth: the menu tracks however deep the CURRENT branch is loaded, so a
    // folder inside a folder inside a subfolder all appear in the pill menu tree.
    const toItem = (c: OsNode, depth: number): MenuItem => ({
      key: c.key,
      label: c.name,
      here: c.key === menuActiveKey,
      onSelect: () => {
        setMenuOpen(false);
        // a node that lives at its own route (a shared space) is a real
        // navigation, not a move within this shell's tree.
        if (c.href) { router.push(c.href); return; }
        navDir.current = 1;
        setExpanded(false);
        setViewerIndex(null);
        void goTo(c.path);
      },
      children: depth < 8 ? (c.children ?? []).filter(openable).map((k) => toItem(k, depth + 1)) : undefined,
    });
    const sections = (rootNode?.children ?? []).filter(openable).map((c) => toItem(c, 1));
    const built = buildMenuTree({
      current: { id: "yours", label: "yours", items: sections },
      founder: isAdmin,
      signedIn: true,
      close: () => setMenuOpen(false),
    });
    // OPEN DRILLED TO WHERE YOU ARE — walk the real node tree along the current
    // path and translate it into the menu's key-chain (innernet › yours › …), so
    // opening ≡ from deep inside a project lands on that exact level with the full
    // breadcrumb visible. Falls back to the deepest ancestor the menu tree
    // actually contains (a leaf document isn't in the menu — stop at its folder).
    const segs = path.split("/").filter(Boolean);
    const keyChain: string[] = ["innernet", "yours"];
    let acc = "";
    let cursor = built.root.children?.find((c) => c.key === "yours");
    for (const s of segs) {
      acc += `/${s}`;
      const node = findByPath(tree, acc);
      if (!node) break;
      const nextInMenu = cursor?.children?.find((c) => c.key === node.key);
      if (!nextInMenu) break; // this level isn't represented in the menu — stop here
      keyChain.push(node.key);
      cursor = nextInMenu;
    }
    return { root: built.root, startPath: keyChain };
  }, [tree, menuActiveKey, isAdmin, goTo, isLocked, path, router]);

  // Reorder the chips by hand-drag → persist the new order. The leftmost chip is
  // the default view, so selecting it keeps the active view in sync with the new
  // default unless the user has explicitly chosen another.
  const onReorderChips = useCallback(
    (ids: string[]) => {
      layout.setChips(ids); // the ORDER persists — its leftmost is the new default
      setViewSel(ids[0]);
      if (ids[0] !== "documents") setViewerIndex(null);
      firePulse();
    },
    [layout, firePulse],
  );

  // ---- self-summary (the level's own description, manual wins) ----
  const selfSummary = layout.state.summaries?.["_self"];
  const summary = selfSummary?.text ?? current.summary;
  const summaryManual = !!selfSummary?.manual;
  // law_say_it_once — the line under the head is shown only when a person wrote it
  // (a manual summary, or a project's real tagline); a generated one just restates
  // the head and the stat line, so it rides as a hover hint instead.
  // A project always keeps its line, because that is where a person writes one:
  // with no tagline it shows empty and quiet (a placeholder), never the
  // generated flavor ("a project in motion…").
  // …and every ROOT space keeps its line too (John, 2026-10-15): "whatever the navigator has to
  // say about a space is what the line under its title should say". Projects · you · netti ·
  // skills · connections · activity · settings each open on one plain sentence of what the
  // space is, written in tree.ts's PANELS, never generated.
  const rootSpace = current.kind !== "project" && path.split("/").filter(Boolean).length === 1;
  const showMatter = summaryManual || current.kind === "project" || rootSpace;
  const matter = current.kind === "project" && !summaryManual && !current.hasTagline ? "" : summary;

  // ---- organism choreography -----------------------------------------------
  // Bottom-center bloom that scales + focuses deeper as you DIVE a project, and
  // relocates between dashboard SECTIONS with a single-axis slide or a
  // de-age/regrow morph (you → top-right by the avatar, etc.). All owned by the
  // choreography hook so the two systems never fight.
  const depth = path.split("/").filter(Boolean).length;
  // the commit timeline view scrubs the organism's age (like the activity log), so
  // it gets the same consistent center-right placement (Task 1).
  const inTimeline = gridView === "timeline" && hasTimeline;
  // AGE-SCRUB VIEWS keep netti IN FOCUS — the activity river and the commit
  // timeline age the organism back as you scroll (the age bus); watching it
  // de-age IS the effect, so these views must NOT frost or rest it. Everywhere
  // else, scrolling into reading blurs + rests netti (law_read_ramp).
  const inAgeScrub = current.panel === "activity" || inTimeline;
  // a VIEW that seats netti somewhere of its own (VIEW_ANCHOR): the timeline at
  // centre-right; the repo graph, and the activity panel's repos list, at
  // bottom-left, where the branch lanes come out of it.
  // …and the AGENTS ORBIT: the agents home draws every agent in a ring of cell thread
  // round netti, so netti leaves its seat at the bottom and takes the CENTRE of the
  // viewport, in focus (AGENTS_CENTER). Only the home (one segment deep) while the
  // panel reports its orbit; an agent's own page is a page to read and netti steps
  // back to its home seat at the soft deep focus. (Connections has no orbit any
  // more, 2026-10-22: the agents left it for a space of their own.)
  const agentsOrbit = next
    ? current.panel === "agents" && agentsOrbitUp(path, panelView)
    : (current.panel === "agents" || current.panel === "connections") && agentsOrbitUp(path, panelView, true);
  // the window's height, for netti's size in the agents orbit (a short window gets a smaller bloom)
  const [viewH, setViewH] = useState(900);
  useEffect(() => {
    const on = () => setViewH(window.innerHeight);
    on();
    window.addEventListener("resize", on);
    return () => window.removeEventListener("resize", on);
  }, []);
  const viewAnchor = inTimeline
    ? "timeline"
    : gridView === "repo" && hasRepo
      ? "repo"
      : current.panel === "activity" && panelView === "repos"
        ? "repo"
        : agentsOrbit
          ? "agents"
          : null;
  const choreo = useOrganismChoreography(path, { depth, inViewer, activeIdx, reduced, expanded, viewAnchor });
  // THE ORBIT HOLDS NETTI STILL. The orbit's field is laid out around netti's
  // centre cell at the bottom-centre seat (OrbitView's origin), so in that view
  // netti never drifts: the dive's focus would pan the engine's camera toward a
  // sector (up to ~0.4 of the frame, up/down/left/right) and slide the cell off
  // the seat the tiles orbit. Here the focus keeps only its zoom, about the
  // cell's own centre, so a deeper level may magnify netti but never move it.
  // The agents orbit holds it the same way (the rings are drawn round netti's heart at
  // AGENTS_CENTER), and keeps it IN FOCUS at the top of the page.
  const orbitHold = (gridView === "orbit" && !inViewer && current.kind !== "panel") || agentsOrbit;
  const shellFocus = orbitHold && choreo.focus ? { ...choreo.focus, dist: 0 } : choreo.focus;
  // law_focus_pull — netti is IN FOCUS on the navigator's outer rooms (your
  // projects, a project's own page, the activity and you panels) and rests OUT
  // of focus once you go deeper: a folder, a document, settings. Deep in, the
  // words are the point, so netti stays a presence (its colour, its motion) and
  // costs the least it can. Scrolling takes it the rest of the way anywhere
  // (lib/os/nettiFocus). The orbit keeps it sharp at any depth: its tiles are
  // laid out around netti's centre, so it is the room's structure there.
  // CONNECTIONS rests it too (2026-10-22): the invite card and the tools are
  // things to read and copy, and connections has no orbit any more.
  const navSection = sectionFromPath(path);
  const deepPlace = inViewer || (navSection === "projects" ? depth >= 3 : navSection === "settings" || (next && navSection === "connections") || depth >= 2);
  // a document open in the reader is read: netti fully out of focus from the first
  // frame (law_focus_pull, 2026-10-19), not only resting soft like a deep folder
  const focusFloor = orbitHold ? 0 : inViewer ? 1 : deepPlace ? DEEP_FOCUS : 0;
  // what this person reaches for, so the shell warms it next time (lib/os/usage).
  useEffect(() => {
    if (gridView === "orbit" || gridView === "timeline" || gridView === "tasks" || gridView === "repo") noteUse(gridView);
  }, [gridView]);
  useEffect(() => { if (fcCreate || psTarget) noteUse("compose"); }, [fcCreate, psTarget]);
  useEffect(() => { if (mvSource) noteUse("move"); }, [mvSource]);
  useEffect(() => { if (current.panel) noteUse(`panel:${current.panel}`); }, [current.panel]);
  // preloader overrides: before reveal the organism is small + invisible; the
  // first reveal grows it in slowly (a gentle ease-out), then normal motion.
  // every user meets a purposefully-different organism — palette + form derived
  // deterministically from their identity (enforced for existing accounts too).
  // normalise the seed via identityKey (lowercase, local-part) so the dashboard
  // and the admin console — which both seed from the operator's email — land on
  // the exact same organism (hue AND shape) for the same person, always.
  const idKey = identityKey(userName);
  // the netti they were born with wins over the email mint (law_one_netti — it
  // is still one creature per person; only where the seed came from changed).
  const nettiId = ob?.netti?.identity ?? null;
  const mintedOrganism = useMemo(() => (nettiId ? futurePalette(nettiId) : organismFromSeed(idKey)), [idKey, nettiId]);
  const userSeed = useMemo(() => seedFromString(nettiId ? identityKey(nettiId) : idKey), [idKey, nettiId]);
  // what this person wears beyond the code's mint — the live bands + any
  // hand-tuned body (migration 111), and a hand-tuned palette (migration 112),
  // both from the netti tuner. The palette arrives CURATED, which is exactly
  // what the mint hands over too, so it simply stands in for it: one value
  // dresses the creature AND the whole UI tint (law_organism_color_authority).
  const { body: userBody, color: tunedColor } = useNettiBody(userSeed);
  const userOrganism = tunedColor ?? mintedOrganism;

  // ── onboarding colour journey ───────────────────────────────────────────────
  // During the odyssey every stage wears its own hue (STAGE_HUE): the bloom
  // recolours and the whole UI retints so each space glows a different colour
  // instead of sitting flat on white. Home runs netti's "bond" — showcase cycles
  // it through varied colours before the tour settles space by space. Once the
  // flow reaches `free`, the user's own organism takes back over.
  const obStageColor = useMemo<ColorState | null>(() => {
    // an account that arrived with its own netti walks in THAT colour — the
    // stage hues are for accounts that never had a birth.
    if (!ob || ob.stage === "free" || ob.netti) return null;
    const h = ((STAGE_HUE[ob.stage] ?? 217) + growthHue) % 360;
    return {
      primary: { hue: h, sat: 0.64, light: 0.53 },
      secondary: { hue: (h + 42) % 360, sat: 0.58, light: 0.57 },
      tinge: { hue: (h + 316) % 360, sat: 0.6, light: 0.57 },
      contrast: 0.7,
    };
  }, [ob, growthHue]);
  const onbActive = obStageColor != null;
  const onbBond = onbActive && ob?.stage === "home"; // netti's colour-cycling bond
  const organismColor = obStageColor ?? userOrganism;

  // NETTI VEIL — the calm DOTS dither is on by default (no literal "1"s / block
  // glyphs). The user re-tunes every property from the /netti appearance panel,
  // which dispatches `innernet:veil-tune` live and persists via veilPrefs. Seed
  // the running overlay from the saved prefs on mount so netti opens on the
  // user's chosen look rather than the raw shader defaults.
  const veilPrefs = useMemo(() => loadVeilPrefs(), []);
  useEffect(() => {
    window.dispatchEvent(new CustomEvent("innernet:veil-tune", { detail: veilPrefs }));
  }, [veilPrefs]);

  // BROWSER HISTORY — the dashboard is a single-document SPA, so without this the
  // browser Back button leaves the app entirely. Mirror each in-app location
  // (path + open document) into history.pushState and restore it on `popstate`,
  // so Back/Forward step through the dashboard's own navigation seamlessly (and
  // pressing Back from an open document simply closes it). The URL itself never
  // changes — only history state — so a refresh always lands on the shell root.
  const navSync = useRef<OsNav>({ path, viewerIndex });
  // THE STAGE: the offset a place opens at when history brings it back (Back,
  // Forward, or a refresh of the very entry), read from lib/stage/memory.
  // Keyed on the SCENE (the folder and the open document), never the folder
  // alone: a stand-in folder on the way to a document must not spend it.
  const stageRestore = useRef<{ path: string; viewerIndex: number | null; top: number } | null>(null);
  const restoreSeeded = useRef(false);
  if (stage && !restoreSeeded.current && typeof window !== "undefined") {
    restoreSeeded.current = true;
    const id = entryIdOf(window.history.state);
    const at = osNavOf(window.history.state);
    const top = id ? readScroll(id) : null;
    if (id && at && top) stageRestore.current = { path: livePath(at.path), viewerIndex: at.viewerIndex, top };
  }
  const skipPush = useRef(false);
  // We ride ON TOP of the App Router's own history entries: every entry keeps
  // Next's internal state markers (spread from the live history.state) and we
  // tuck our location under `osNav`. The URL is left untouched. This is what
  // keeps Back a soft popstate (no full reload) — dropping Next's markers makes
  // it force an MPA reload instead.
  //
  // Next REWRITES the current entry's state on router.refresh() (a netti act, a
  // live pulse, a drag-move) and keeps only its own keys, so the entry the person
  // is standing on stopped carrying `osNav` and Back no longer moved the shell:
  // `onPop` saw no location and let go, and the next Back left the app. Every
  // refresh hands the shell a new `projects` payload, so that is when the entry
  // is stamped again (without adding one). It also seeds the first entry on mount.
  // THE STAGE: the entry's id (its scroll is kept against it, lib/stage/memory).
  // Held here, so a re-stamp after a refresh keeps the SAME id and the offset
  // it already has.
  const entryRef = useRef<string | null>(null);
  // where this entry stands along the tab's stage history, and the place each
  // index holds (Backspace steps BACK when the place it climbs to is the one
  // just before, so Back and Backspace never disagree)
  const idxRef = useRef<number | null>(null);
  const idxPlaces = useRef(new Map<number, OsNav>());
  useEffect(() => {
    const here = navSync.current;
    if (stage) {
      const id = entryRef.current ?? entryIdOf(window.history.state) ?? newEntryId();
      entryRef.current = id;
      const idx = idxRef.current ?? stageIdxOf(window.history.state) ?? 0;
      idxRef.current = idx;
      idxPlaces.current.set(idx, { path: here.path, viewerIndex: here.viewerIndex });
      if (sameNav(window.history.state, here) && entryIdOf(window.history.state) === id && stageIdxOf(window.history.state) === idx) return;
      // the same address: Next's own keys may ride along (there is nothing to learn)
      window.history.replaceState({ ...withEntryId(withOsNav(window.history.state, here), id), stageIdx: idx }, "", window.location.href);
      return;
    }
    if (sameNav(window.history.state, here)) return;
    window.history.replaceState(withOsNav(window.history.state, here), "", window.location.href);
  }, [projects, stage]);
  useEffect(() => {
    if (skipPush.current) { skipPush.current = false; return; }
    const prev = navSync.current;
    const next: OsNav = { path, viewerIndex };
    const step = historyStep(prev, next);
    if (step === "none") return;
    navSync.current = next;
    if (stage) {
      // EVERY PLACE ITS OWN ADDRESS. Written WITHOUT Next's own markers on the
      // state: the App Router's history patch then copies them over itself and
      // moves its idea of the URL along with ours (usePathname, and every
      // router.refresh() after this, ask for this address — never the old one).
      const place = next.viewerIndex != null ? pages[next.viewerIndex]?.path ?? next.path : next.path;
      const url = urlFor(base, place, { search: window.location.search });
      const replace = step === "replace" || arrivalReplace.current || followReplace.current;
      arrivalReplace.current = false;
      followReplace.current = false;
      const id = replace ? entryRef.current ?? newEntryId() : newEntryId();
      const idx = (idxRef.current ?? 0) + (replace ? 0 : 1);
      entryRef.current = id;
      idxRef.current = idx;
      idxPlaces.current.set(idx, { path: next.path, viewerIndex: next.viewerIndex });
      writeStageEntry(replace ? "replace" : "push", { osNav: { path: next.path, viewerIndex: next.viewerIndex }, stageEntry: id, stageIdx: idx }, url);
      return;
    }
    const state = withOsNav(window.history.state, next);
    // paging between documents of one reader replaces: a swipe is a turn of the
    // page, not a place to come back to, and each one used to cost a Back press
    if (step === "replace") window.history.replaceState(state, "", window.location.href);
    else window.history.pushState(state, "", window.location.href);
  }, [path, viewerIndex]);
  useEffect(() => {
    const onPop = (e: PopStateEvent) => {
      const os = osNavOf(e.state);
      // no in-app location on this entry → the browser is leaving for a real prior
      // page (or a route we don't own); let it proceed untouched.
      if (!os) return;
      let target = { path: livePath(os.path), viewerIndex: os.viewerIndex };
      const cur = navSync.current;
      let restore: { path: string; viewerIndex: number | null; top: number } | null = null;
      let dir: Dir | null = null;
      if (stage) {
        // the entry's ADDRESS names the document; its stored index is only a
        // fallback (documents reorder as they change)
        const place = shellPathOf(base, window.location.pathname);
        const hit = place ? resolvePlace(treeRef.current, livePath(place)) : null;
        if (hit && hit.path === target.path && hit.viewerIndex != null) target = { ...target, viewerIndex: hit.viewerIndex };
        // Back / Forward land where that page was scrolled to (lib/stage/memory),
        // and travel the way history did when the two places aren't related
        const id = entryIdOf(e.state);
        entryRef.current = id ?? newEntryId();
        if (id) restore = { path: target.path, viewerIndex: target.viewerIndex, top: readScroll(id) ?? 0 };
        const ti = stageIdxOf(e.state);
        dir = ti != null && idxRef.current != null ? (ti < idxRef.current ? -1 : 1) : null;
        if (ti != null) { idxRef.current = ti; idxPlaces.current.set(ti, { path: target.path, viewerIndex: target.viewerIndex }); }
      }
      if (cur.path === target.path && cur.viewerIndex === target.viewerIndex) return;
      // only a real move carries a restore and a direction (a same-place entry,
      // like a #fragment, must leave nothing behind for the next move)
      if (stage) {
        stageRestore.current = restore;
        popDir.current = dir;
      }
      navSeq.current++; // Back/Forward outranks any navigation still waiting
      skipPush.current = true; // this state change came FROM history — don't re-push it
      navSync.current = target;
      setExpanded(false);
      const back = canonicalProjectOf(target.path);
      if (back) void ensureProject(back.pid);
      setPath(target.path);
      setViewerIndex(target.viewerIndex);
      // Back out of a document is out of the reader: the entry has no document in
      // it, and a level still set to its "artifacts" view would keep the reader
      // open on the first page (the view, not the index, is what holds it open)
      if (target.viewerIndex === null) setViewSel((v) => (v === "documents" ? null : v));
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ensureProject, stage]);

  // THE STAGE — an address the shell did not write itself (a netti link pushed
  // through the router, a <Link> to /dashboard/…) still moves the shell there.
  // Our own writes reach usePathname too, and name the place already standing.
  const pathname = usePathname();
  useEffect(() => {
    if (!stage || !pathname) return;
    const place = shellPathOf(base, pathname);
    if (!place) return;
    const want = livePath(place);
    // what is on screen (the canonical effect names an open document by its
    // page, even when the reader was opened as a view), or the folder itself
    if (want === shownRef.current || want === navSync.current.path) return;
    // a deep arrival is already opening this very place (with its view)
    if (arrivalReplace.current && placeOpened.current && initialPlace && want === livePath(initialPlace)) return;
    // the router already wrote this entry: our move only names it
    followReplace.current = true;
    void goTo(want);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage, pathname]);

  // a restore is spent once its place has arrived (the stage read it on arrival)
  useEffect(() => {
    const r = stageRestore.current;
    if (r && r.path === current.path && r.viewerIndex === viewerIndex) stageRestore.current = null;
  }, [sceneKey, current.path, viewerIndex]);

  // THE STAGE — the address follows the view too (`?v=list`), as a replace: a
  // way of looking at a place is not a place to come back to.
  const urlView = !inViewer && viewSel && viewSel !== defaultViewId && viewSel !== "documents" ? viewSel : null;
  // …and the address always names what is on screen: the open document (even
  // when the reader was opened as a view rather than from a tab), the place a
  // stale link walked up to, a legacy ?panel= link. Never while a deep arrival
  // is still being opened (that would write the stand-in over the link).
  useEffect(() => {
    if (!stage || arrivalReplace.current) return;
    const place = inViewer ? pages[docIndex]?.path ?? path : path;
    // the entry is what is on screen (a reader opened as a view IS a document
    // entry): Backspace asks this when it decides whether to step back
    if (idxRef.current != null) idxPlaces.current.set(idxRef.current, { path, viewerIndex: inViewer ? docIndex : null });
    const want = urlFor(base, place, { view: urlView, search: window.location.search }) + window.location.hash;
    if (want === window.location.pathname + window.location.search + window.location.hash) return;
    replaceStageUrl(want);
  }, [stage, base, urlView, path, inViewer, docIndex, pages, urlTick]);

  // the browser's tab and its Back menu name the place, not just the app
  useEffect(() => {
    if (!stage) return;
    const name = inViewer ? pages[docIndex]?.name ?? current.name : current.name;
    document.title = current.path === "/projects" ? "your innernet" : `${name} · your innernet`;
  }, [stage, current.path, current.name, inViewer, pages, docIndex]);

  // where the open document's reading place is kept (a project's reader only)
  const readKey = current.kind === "project" && inViewer && pages[docIndex] ? PLACES.reading(path, pages[docIndex].key) : null;
  const readKeyRef = useRef<string | null>(readKey);
  useEffect(() => {
    readKeyRef.current = readKey;
  }, [readKey]);

  // …and where each place was scrolled to, kept against its history entry as
  // it scrolls, so Back, Forward and a refresh can put it back.
  useEffect(() => {
    if (!stage) return;
    let keep: ReturnType<typeof setTimeout> | null = null;
    const off = onStageScroll(() => {
      const id = entryRef.current;
      if (id) saveScroll(id, stageScrollTop());
      // …and how far a project's document was read, for the next visit (lib/os/places reading)
      const read = readKeyRef.current;
      if (read) {
        if (keep) clearTimeout(keep);
        const top = stageScrollTop();
        keep = setTimeout(() => keepResumable(read, Math.round(top)), 160);
      }
    });
    return () => {
      off();
      if (keep) clearTimeout(keep);
    };
  }, [stage]);

  // a project's place, kept as the person moves through it: the view, and the document open in the reader
  // (by its path, so a reorder never opens another). Declared BEFORE the effect below, so the render where
  // the path changed (still holding the last level's view) is never written under the new path.
  const placeOf = useRef<string | null>(null);
  // what was on screen when the place was last kept or taken back: only a CHANGE from it is the person's
  // (a render still holding the last level's view, or the one before a restore lands, is never written)
  const shown = useRef<{ path: string; view: string; doc: string | null } | null>(null);
  const onScreen = { view: inViewer ? "documents" : rawView, doc: inViewer ? pages[docIndex]?.path ?? null : null };
  useEffect(() => {
    if (current.kind !== "project" || placeOf.current !== path) return;
    const was = shown.current;
    if (was && was.path === path && was.view === onScreen.view && was.doc === onScreen.doc) return;
    shown.current = { path, ...onScreen };
    keepResumable(PLACES.project(path), onScreen);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, current.kind, onScreen.view, onScreen.doc]);
  // the reading place a kept project place hands the reader on a continue (never a plain open); the reader
  // takes it once (ArtifactMobile), and only for the document it was kept for
  const [continueRead, setContinueRead] = useState<{ path: string; doc: string; top: number } | null>(null);
  const resumeTop = continueRead && continueRead.path === path && continueRead.doc === pages[docIndex]?.path ? continueRead.top : null;

  // reopening a level lands on its leftmost (priority) chip, or on the view the navigation asked for.
  const askedFor = useRef<string | null>(null);
  useEffect(() => {
    // a view asked for with the navigation (goTo(…, { view })) wins over the default, and over a kept place
    const asked = pendingView.current;
    pendingView.current = null;
    askedFor.current = asked ? path : null;
    // the place of this level is not known until the level itself is (a project's tree arrives after its path)
    placeOf.current = null;
    setViewSel(asked);
    setOrbitSpin(0); setSelectedKey(null); setMenuOpen(false);
  }, [path]);
  // …and a project lands where it was left, once its chips and pages have arrived: the view, the document
  // open in the reader, and how far it was read. Never over a view the navigation asked for.
  useEffect(() => {
    // a lazy folder opened before its pages arrived has only the few chips that need none: taking the place back
    // now would set it, find its document missing, and leave the view the subtree then defaults to to overwrite it
    if (placeOf.current === path || !projectPlaceReady(current, path, orderedChips.length)) return;
    placeOf.current = path;
    shown.current = { path, ...onScreen };
    if (askedFor.current === path) return;
    const kept = peekResumable<{ view?: string; doc?: string | null }>(PLACES.project(path));
    if (kept?.view === "documents") {
      // the reader only with its document still here
      const at = kept.doc ? pages.findIndex((p) => p.path === kept.doc) : -1;
      if (at < 0) return;
      // the kept place is read from the browser once the level has arrived, so it is set here
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setViewSel("documents");
      setViewerIndex((v) => v ?? at);
      // and how far it was read: the stage puts it back as it would on Back, unless history already has a place
      const top = peekResumable<number>(PLACES.reading(path, pages[at].key));
      if (typeof top === "number" && top > 0 && kept.doc) {
        setContinueRead({ path, doc: kept.doc, top });
        if (stage && !stageRestore.current) stageRestore.current = { path, viewerIndex: at, top };
      }
    } else if (kept?.view && orderedChips.some((c) => c.id === kept.view)) {
      setViewSel(kept.view);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, current.kind, current.path, !!current.children, orderedChips, pages, stage]);
  // collapse the search to a magnifying glass ONLY when the breadcrumb has grown
  // long enough (on a tight screen) that the "search" string would run into the
  // avatar. Measured from the breadcrumb's right edge to the identity block; stable
  // because neither edge depends on whether the search is collapsed.
  useEffect(() => {
    if (!isNarrow) { setSearchTight(false); return; }
    const measure = () => {
      const c = crumbRef.current, idn = identityRef.current;
      if (!c || !idn) return;
      const gap = idn.getBoundingClientRect().left - c.getBoundingClientRect().right;
      setSearchTight(gap < 96); // not enough room for "/ search" → show the glass
    };
    measure();
    const ro = new ResizeObserver(measure);
    if (crumbRef.current) ro.observe(crumbRef.current);
    window.addEventListener("resize", measure);
    return () => { ro.disconnect(); window.removeEventListener("resize", measure); };
  }, [isNarrow, path, crumbs.length]);

  // NO needless scroll — when everything fits the screen (all folders visible),
  // lock the page so it can't rubber-band/scroll into empty space. When content
  // genuinely overflows, scrolling is restored. Measured with scroll allowed first,
  // then toggled in the same frame so there's no flicker.
  // This holds the page through the COUNTED lock like everything else: it used
  // to write the root's overflow-y by hand, which a sheet open on the same page
  // overwrote on close (the page then scrolled into empty space again) and which
  // overwrote the sheet's own hold whenever the list grew (the page then moved
  // under the sheet).
  useEffect(() => {
    // the stage's own scroller simply doesn't scroll when everything fits
    if (stage) return;
    let raf = 0;
    let release: (() => void) | null = null;
    const apply = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const doc = document.documentElement;
        // READ, then act only on change. This used to reset overflow, read,
        // and write again on EVERY body resize — a forced synchronous reflow of
        // the whole document each time the connections list changed height
        // (expand, poll, rename), which is the stutter people felt there.
        // scrollHeight is the content height regardless of overflow.
        const fits = doc.scrollHeight <= window.innerHeight + 6;
        if (fits && !release) release = lockScroll("page-fits");
        else if (!fits && release) { release(); release = null; }
      });
    };
    apply();
    const ro = new ResizeObserver(apply);
    ro.observe(document.body);
    window.addEventListener("resize", apply);
    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      window.removeEventListener("resize", apply);
      release?.();
    };
  }, [path, isNarrow, gridView, revealed, stage]);

  // law_read_ramp — the page-level reading ramp (same mechanism as the artifact
  // reader's --art-ramp): window scroll writes --os-ramp 0→1 over the first
  // ~220px, the .os-read-scrim reads it. Imperative — no React state on the
  // scroll hot path; resets on route change so every page opens fully lit.
  useEffect(() => {
    if (!stage) return;
    // THE STAGE: the same ramp, from the stage's own scroller (lib/stage/scroller).
    // In the reader it is the document's ramp (--art-*: the reading scrim, the
    // seam's frost) and its progress line; everywhere else the page's (--os-*).
    const write = () => {
      const top = stageScrollTop();
      if (inViewer) {
        const max = stageScrollMax();
        window.dispatchEvent(new CustomEvent("innernet:art-read", { detail: max > 0 ? top / max : 0 }));
        const lv = writeReadRamp(top / 200, "art");
        setOrganismCalm("os-page", Math.max(lv, focusFloor));
        return;
      }
      const raw = Math.min(1, Math.max(0, top / 220));
      const lv = writeReadRamp(raw, "os", inAgeScrub ? { calm: false } : undefined);
      if (inAgeScrub) writeBandRamp(raw);
      setOrganismCalm("os-page", Math.max(lv, focusFloor));
    };
    write();
    const off = onStageScroll(write);
    // the content grows or shrinks after it opens (a panel's data lands, a list
    // unfolds), which can move the scroll without a scroll event.
    let raf = 0;
    const later = () => { if (!raf) raf = requestAnimationFrame(() => { raf = 0; write(); }); };
    const ro = new ResizeObserver(later);
    const sc = stageScroller();
    if (sc?.firstElementChild) ro.observe(sc.firstElementChild);
    window.addEventListener("resize", later);
    return () => {
      off();
      ro.disconnect();
      window.removeEventListener("resize", later);
      cancelAnimationFrame(raf);
      clearReadRamp("os", false);
      if (inViewer) writeReadRamp(0, "art");
      setOrganismCalm("os-page", null);
    };
  }, [stage, path, inViewer, inAgeScrub, focusFloor]);
  useEffect(() => {
    if (stage) return;
    let raf = 0;
    const write = () => {
      raf = 0;
      // age-scrub views hold netti fully lit + sharp (no frost, no rest) so its
      // de-aging reads clearly; every other page blurs + rests as you read.
      const raw = Math.min(1, Math.max(0, window.scrollY / 220));
      // age-scrub views (the activity river, a timeline) read too now
      // (law_read_frost): the ramp is real, so netti frosts behind the rows;
      // only the organism's rest stays off there, so its de-aging keeps moving.
      const v = raw;
      // three channels, quantised by cost (lib/os/readRamp): --os-ramp fine for
      // opacity, --os-blur coarse for the scrim's gaussian radius.
      const lv = writeReadRamp(v, "os", inAgeScrub ? { calm: false } : undefined);
      // an age-scrub view keeps the frost off netti, but its rows still pass under
      // the head — the band lights with the real scroll so they never draw
      // through the crumb and the navigator.
      if (inAgeScrub) writeBandRamp(raw);
      // the same ramp rests the organism (dynamic rendering): as netti goes out
      // of focus its fps/resolution wind down with it (a defocused creature never
      // shows the pixels it lost), and a DEEP place (focusFloor, below) rests it
      // by as much as it has already taken it out of focus.
      // …including the age-scrub views (the activity river, a timeline). They
      // used to keep netti at full rate AND full resolution "so its de-aging
      // reads" — half rate out of focus still reads as de-aging (2026-09-27).
      setOrganismCalm("os-page", Math.max(lv, focusFloor));
    };
    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(write);
    };
    write();
    // the page grows or shrinks after it opens (a panel's data lands, a list
    // unfolds), which can move the scroll without a scroll event.
    const ro = new ResizeObserver(onScroll);
    ro.observe(document.body);
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      ro.disconnect();
      cancelAnimationFrame(raf);
      clearReadRamp("os", false);
      setOrganismCalm("os-page", null);
    };
  }, [path, inAgeScrub, focusFloor, stage]);

  // THE STAGE — Backspace steps back up the directory (out of a document to its
  // folder, out of a folder to the level above it). Never while typing, never
  // under the menu or a sheet, never with a modifier (those are the browser's).
  useEffect(() => {
    if (!stage) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Backspace" || e.repeat || e.metaKey || e.ctrlKey || e.altKey || e.shiftKey) return;
      if (shellShouldIgnoreKey(e)) return;
      // anything open over the page owns its keys: the menu, a sheet, a studio,
      // the dial, a context menu, a dialog
      if (menuOpen || scrollHolders().length > 0) return;
      if (document.querySelector("[data-gesture-root], .ground-dial.is-open, .ctx-menu, [role=dialog][aria-modal=true]")) return;
      // the place it climbs to is the entry just before (the bare folder, no
      // document open in it): that is Back
      const prev = idxRef.current != null ? idxPlaces.current.get(idxRef.current - 1) : undefined;
      const isPrev = (p: string) => !!prev && prev.path === p && prev.viewerIndex == null;
      if (inViewer) {
        e.preventDefault();
        if (isPrev(current.path)) { window.history.back(); return; }
        setViewerIndex(null);
        setViewSel((v) => (v === "documents" ? null : v));
        return;
      }
      const up = parentPath(current.path);
      if (!up || up === "/") return;
      e.preventDefault();
      if (isPrev(up)) { window.history.back(); return; }
      navDir.current = -1;
      setExpanded(false);
      void goTo(up);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [stage, inViewer, current.path, goTo, menuOpen]);
  // law_focus_pull — how far out of focus netti rests HERE before any scroll.
  // Written on its own so a change of place eases the pull (the CSS transition)
  // instead of waiting for the next scroll; released on unmount.
  useEffect(() => {
    setFocusFloor(focusFloor);
  }, [focusFloor]);
  useEffect(() => () => setFocusFloor(0), []);
  // hand the organism's rotation back when the orbit view isn't showing.
  useEffect(() => {
    if (gridView !== "orbit") {
      setOrbitSpin(0);
      setOrbitDial((d) => (d.active ? { active: false, windowArc: d.windowArc, page: 0, pages: 1 } : d));
    }
  }, [gridView]);
  // …and when a PANEL's own orbit (the agents home) is put away: its revolve left
  // netti turned, and nothing else will turn it back.
  useEffect(() => {
    if (panelView !== "orbit") setOrbitSpin(0);
  }, [panelView]);

  // (the row glow's --mx/--my used to be pinned here by a document listener for
  // .list-row only; the one HoverField tracker (components/HoverField, mounted in
  // the root layout) now owns every hover surface, everywhere.)

  // PICK UP / MOVE — there is no copy in here, only moving. ⌘C (or Ctrl+C) picks up
  // the folder/file under the cursor (its link is also placed on the system clipboard
  // for sharing); ⌘V MOVES it into the folder you're now in — same project or across
  // projects. The bottom-right StatusCorner narrates each step as a move. We track the
  // hovered node by its data attributes so no explicit "select" click is needed.
  const hoveredNode = useRef<{ name: string; key: string; path: string } | null>(null);
  const clipped = useRef<{ name: string; key: string; path: string } | null>(null);
  useEffect(() => {
    const onOver = (e: PointerEvent) => {
      const el = (e.target as HTMLElement | null)?.closest?.("[data-node-key]") as HTMLElement | null;
      hoveredNode.current = el
        ? { key: el.getAttribute("data-node-key") || "", name: el.getAttribute("data-node-name") || "item", path: el.getAttribute("data-node-path") || "" }
        : null;
    };
    const onKey = (e: KeyboardEvent) => {
      // these chords are for the directory. In a field, in the document editor (a
      // contenteditable, which an INPUT/TEXTAREA check never saw), or on a key the
      // editor already handled, they mean what they always mean: undo the last
      // word, copy the selected words (lib/os/shellKeys).
      const ignore = shellShouldIgnoreKey(e);
      // Escape clears the selection (EditableText stops its own Escape, so this
      // only fires outside an open editor).
      if (e.key === "Escape" && !ignore) { setSelectedKey(null); return; }
      if (ignore || !(e.metaKey || e.ctrlKey)) return;
      const k = e.key.toLowerCase();
      // ⌘Z / ⇧⌘Z — undo / redo inline renames (skipped in fields → native undo).
      if (k === "z") { e.preventDefault(); if (e.shiftKey) editStack.redo(); else editStack.undo(); return; }
      if (k === "c") {
        // words are selected: the person is copying words, not picking up a folder
        if (window.getSelection()?.toString()) return;
        const h = hoveredNode.current;
        if (!h) return;
        clipped.current = h;
        try {
          const link = stage && h.path
            ? `${window.location.origin}${urlFor(base, h.path)}`
            : h.path ? `${window.location.origin}${h.path.startsWith("/") ? "" : "/"}${h.path}` : window.location.origin;
          navigator.clipboard?.writeText(link);
        } catch { /* ignore */ }
        // ⌘C PICKS IT UP TO MOVE — there's no copy/duplicate in here, only moving,
        // so the corner says so. (The link is also on your clipboard, for sharing.)
        window.dispatchEvent(new CustomEvent("innernet:clip", { detail: { action: "cut", name: h.name } }));
      } else if (k === "v") {
        const c = clipped.current;
        if (!c || !current.projectId) {
          window.dispatchEvent(new CustomEvent("innernet:clip", { detail: { action: c ? "note" : "clear", name: c ? "open a folder to move it here" : "" } }));
          return;
        }
        if (c.path === current.path) { window.dispatchEvent(new CustomEvent("innernet:clip", { detail: { action: "note", name: "it's already here" } })); return; }
        const srcProject = c.path.match(/\/projects\/([^/]+)/)?.[1] || null;
        void (async () => {
          try {
            // always a MOVE — pasting reparents the row (cross-project too), never duplicates.
            const res = await fetch("/api/os/paste", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ sourceKey: c.key, sourcePath: c.path, destPath: current.path, destProjectId: current.projectId, mode: "move" }),
            });
            const out = (await res.json().catch(() => ({}))) as { error?: string };
            if (res.ok) {
              if (srcProject && srcProject !== current.projectId) await refreshProject(srcProject);
              await refreshProject(current.projectId!);
              firePulse();
              editStack.push({
                kind: "move",
                sourceKey: c.key,
                name: c.name,
                fromProjectId: srcProject ?? current.projectId!,
                toProjectId: current.projectId!,
                fromParentPath: parentPath(c.path) ?? c.path,
                toParentPath: current.path,
              });
              window.dispatchEvent(new CustomEvent("innernet:clip", { detail: { action: "moved", name: c.name, folder: current.name } }));
              clipped.current = null;
            } else {
              window.dispatchEvent(new CustomEvent("innernet:clip", { detail: { action: "note", name: out.error || "couldn't move it here" } }));
            }
          } catch {
            window.dispatchEvent(new CustomEvent("innernet:clip", { detail: { action: "note", name: "couldn't move — offline?" } }));
          }
        })();
      }
    };
    document.addEventListener("pointerover", onOver, { passive: true });
    window.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerover", onOver);
      window.removeEventListener("keydown", onKey);
    };
  }, [current.name, current.path, current.projectId, refreshProject, firePulse, editStack]);

  // the organism's resting age is its NATURAL size — earned slowly from how much
  // memory exists (a saturating curve, so two weeks of use never fills the screen).
  // Activity-scroll scrubbing flexes it a little around this, never past it.
  const baseAge = useMemo(() => ageFromMemory(cells), [cells]);
  // the ground under the viewer's netti (the share card draws it on this)
  const { bgRgb: themeBgRgb } = useTheme();
  // the navigator's rail slot (a callback ref, so a view portalling into it
  // re-renders once the head has mounted)
  const [railSlot, setRailSlot] = useState<HTMLDivElement | null>(null);
  // the navigator's chip slot, where a panel's own view chips stand on the stage
  // (components/os/NavChips); a callback ref, so the panel re-renders into it
  const [chipSlot, setChipSlot] = useState<HTMLDivElement | null>(null);
  // the crumb's search spot, where a searchable panel's own field stands
  // (components/os/CrumbFind); a callback ref for the same reason
  const [findSlot, setFindSlot] = useState<HTMLSpanElement | null>(null);

  // bridge for panels rendered inside the shell (the Activity river scrubs the
  // organism's age and jumps within the shell). `setOrganismAge` is IMPERATIVE —
  // it dispatches straight to the organism's render loop (via the age bus) instead
  // of setting React state, so scrolling the timeline never re-renders the shell.
  const shellApi = useMemo<OsShellApi>(
    () => ({
      goTo: (t, o) => goTo(t, o),
      setPanelView,
      path,
      setAgentRows,
      setOrbitSpin,
      setOrganismAge: (age) =>
        window.dispatchEvent(new CustomEvent("innernet:organism-age", { detail: { age } })),
      naturalAge: baseAge,
      showCodeMarks: canSee(F.projectRepo, { isAdmin }),
      railSlot,
      chipSlot,
      findSlot,
      netti: { seed: userSeed, color: userOrganism, body: userBody ?? null, age: baseAge, ground: themeBgRgb },
      me: { name: who, avatarUrl },
    }),
    [goTo, path, setAgentRows, baseAge, isAdmin, railSlot, chipSlot, findSlot, userSeed, userOrganism, userBody, themeBgRgb, who, avatarUrl],
  );

  // the share card's host lives beside the shell (DashboardClient), not inside
  // its context: hand it the netti the shell is drawing (law_one_netti)
  useEffect(() => publishShareNetti(shellApi.netti ?? null), [shellApi.netti]);

  // …and to the Mac app's corner, which draws this same netti over every other
  // app (law_one_netti). Only a real change crosses the bridge: shellApi is
  // rebuilt for reasons that leave netti as it was.
  const deskNettiSent = useRef<OsShellApi["netti"]>(null);
  useEffect(() => {
    const n = shellApi.netti ?? null;
    if (!n || sameNetti(deskNettiSent.current ?? null, n)) return;
    const b = desktopBridge();
    if (!b?.deskNetti) return;
    deskNettiSent.current = n;
    b.deskNetti(n);
  }, [shellApi.netti]);

  const introNow = revealed && firstReveal.current;
  // a touch smaller on phones so the bloom never overbears the screen.
  const mobileScale = isNarrow ? (agentsOrbit ? AGENTS_ORBIT_NARROW : 0.8) : agentsOrbit ? agentsOrbitShort(viewH) : 1;
  // …and a touch smaller again while there is barely any memory. A newborn drawn
  // at full size is mostly centre and air; at 0.86 it reads as a small complete
  // creature with room to grow into. Grows back to 1.0 over a life of memory.
  const growthScale = useMemo(() => sizeFromMemory(baseAge), [baseAge]);
  const orgScale = (revealed ? choreo.scale : choreo.scale * 0.3) * mobileScale * growthScale;
  const orgOpacity = revealed ? choreo.opacity : 0;
  const orgTransMs = introNow ? 1500 : choreo.innerTransMs;
  const orgEase = introNow ? "cubic-bezier(0.16, 1, 0.3, 1)" : choreo.innerEase;

  // MENU CENTRE-STAGE — when the section menu opens, the dashboard dissolves and
  // netti takes over: it rises to bottom-centre, grows, brightens, drops any
  // focus-lean, and lifts ABOVE the frosted veil so it reads sharp behind the
  // list (the veil blurs everything below it). Same on mobile and desktop. On
  // close these revert to the choreography values and it glides back home.
  const menuOrg = menuOpen
    ? {
        anchor: "bottom" as const,
        offset: { y: "-6vh" },
        glide: true,
        glideAxis: "both" as const,
        scale: (isNarrow ? 1.02 : 1.24),
        opacity: 0.94,
        focus: null,
        zIndex: 44,
        transMs: 760,
        ease: "var(--ease-soft)",
      }
    : null;

  // OVERTURE — netti's birth walks through three places, and the organism
  // actually MOVES between them (long, slow glides; this is a birth, not a
  // transition): born dead-centre where nothing else exists, then descending to
  // the seat it will keep under the dashboard forever, then stepping aside so
  // the room belongs to the person and netti's words can rise at the edge.
  const [ovtPlace, setOvtPlace] = useState<"top" | "centre" | "bottom" | "aside">("top");
  useEffect(() => {
    const on = (e: Event) => {
      const p = (e as CustomEvent<{ place?: string }>).detail?.place;
      if (p === "top" || p === "centre" || p === "bottom" || p === "aside") setOvtPlace(p);
    };
    window.addEventListener("innernet:overture-place", on as EventListener);
    return () => window.removeEventListener("innernet:overture-place", on as EventListener);
  }, []);


  // netti rides ABOVE the overture's sky (z 66) and below its words (z 70). At
  // z 0 — where this sat — the birth happened underneath an opaque field of
  // colour and nobody ever saw the creature the whole scene is about.
  const OVT_Z = 68;
  const overtureOrg = overtureActive
    ? ovtPlace === "top"
      ? {
          // the arrival — netti hangs at the top of the room for a breath
          // before it descends to the seat it keeps.
          anchor: "top" as const,
          offset: { y: isNarrow ? "20vh" : "24vh" },
          glide: true,
          glideAxis: "both" as const,
          scale: isNarrow ? 0.92 : 1.06,
          opacity: 1,
          focus: null,
          zIndex: OVT_Z,
          transMs: 1800,
          ease: "var(--ease-soft)",
        }
      : ovtPlace === "centre"
      ? {
          anchor: "center" as const,
          offset: { y: "0" },
          glide: true,
          glideAxis: "both" as const,
          scale: isNarrow ? 1.0 : 1.2,
          opacity: 1,
          focus: null,
          zIndex: OVT_Z,
          transMs: 2200,
          ease: "var(--ease-soft)",
        }
      : ovtPlace === "bottom"
        ? {
            anchor: "bottom" as const,
            offset: { y: isNarrow ? "-6vh" : "-9vh" },
            glide: true,
            glideAxis: "both" as const,
            scale: isNarrow ? 0.96 : 1.1,
            opacity: 1,
            focus: null,
            zIndex: OVT_Z,
            transMs: 2000,
            ease: "var(--ease-soft)",
          }
        : {
            // aside — netti keeps the bottom but leans off-centre, leaving the
            // words their own air before the guide takes the right edge.
            anchor: "bottom" as const,
            offset: { x: isNarrow ? "0" : "-16vw", y: isNarrow ? "-3vh" : "-6vh" },
            glide: true,
            glideAxis: "both" as const,
            scale: isNarrow ? 0.9 : 1.0,
            opacity: 1,
            focus: null,
            zIndex: OVT_Z,
            transMs: 1800,
            ease: "var(--ease-soft)",
          }
    : null;
  // the closing — netti centre, everything else stood down, until "explore".
  const gradOrg = closing
    ? {
        anchor: "center" as const,
        offset: { y: "0" },
        glide: true,
        glideAxis: "both" as const,
        scale: isNarrow ? 1.02 : 1.18,
        opacity: 1,
        focus: null,
        zIndex: 0,
        transMs: 900,
        ease: "var(--ease-soft)",
      }
    : null;
  // THE GUIDE — while netti walks you in from the right edge, it stands BEHIND
  // its own words: bottom-right, under the guide's frost (z 1, the veil sits in
  // the guide's z 46 context), so the room and the text stay on the right side
  // of it and the creature reads as the one speaking. Not during the arrival,
  // the closing, the section menu, or while the connect window is open.
  const guideOrg =
    guideActive && !overtureActive && !closing && !menuOpen && !connectTool && ob && ob.stage !== "free"
      ? {
          anchor: "bottom-right" as const,
          offset: { x: isNarrow ? "-50vw" : "-13vw", y: isNarrow ? "-9vh" : "-14vh" },
          glide: true,
          glideAxis: "both" as const,
          scale: isNarrow ? 0.76 : 0.92,
          opacity: 1,
          focus: null,
          zIndex: 1,
          transMs: 1400,
          ease: "var(--ease-soft)",
        }
      : null;
  const orgOver = menuOrg ?? overtureOrg ?? guideOrg ?? gradOrg;

  const isPanel = current.kind === "panel";
  const docProjectId = pages[docIndex]?.projectId ?? current.projectId;
  // the folder a page belongs to — the share studio's face reads it as the
  // quiet second line ("a page from …"), so the sharer sees the same context
  // line the person on the other end will.
  const docProjectName = useMemo(() => {
    if (current.kind === "project") return current.name;
    let pp: string | null = path;
    while (pp && pp !== "/") {
      const n = findByPath(tree, pp);
      if (n?.kind === "project") return n.name;
      pp = parentPath(pp);
    }
    return null;
  }, [tree, path, current]);

  // THE STAGE: the reader splits across the stage — its tabs in the head, the
  // document in the scroller. The old reader keeps its own scroller and band.
  const readerBody = stage ? (
    <ReaderDoc
      key={docIndex}
      pages={pages}
      index={docIndex}
      onIndex={(i) => setViewerIndex(i)}
      highlight={searchHighlight}
      editing={editing}
      onToggleEdit={() => setEditing((e) => !e)}
      onSaveDocument={saveArtifactDocument}
      edits={docEdits}
      widthOf={() => stageScrollRef.current?.clientWidth || 360}
    />
  ) : (
    <ArtifactMobile
      pages={pages}
      index={docIndex}
      onIndex={(i) => setViewerIndex(i)}
      readKey={readKey}
      resumeTop={resumeTop}
      highlight={searchHighlight}
      editing={editing}
      onToggleEdit={() => setEditing((e) => !e)}
      onSaveDocument={saveArtifactDocument}
      onSaveNow={commitDocEdit}
      onCancelEdit={cancelDocEdit}
      edits={docEdits}
      topNav={
        <OsNavigator
          siblings={siblings}
          activeKey={current.key}
          onSelectSibling={selectSibling}
          summary={matter}
          summaryManual={summaryManual}
          showMatter={showMatter}
          onEditSummary={(t) => { layout.setSummary("_self", t); firePulse(); }}
          chips={isPanel ? [] : orderedChips}
          view="documents"
          onView={(id) => { onView(id); firePulse(); }}
          onNewView={() => { setViewSel("orbit"); firePulse(); }}
          onReorderChips={onReorderChips}
          compact
          // IN THE READER the navigator drops its descriptive half.
          // Everything pinned here is chrome the document must scroll
          // under, and carrying the summary + stat line made that band
          // ~428px on a desktop — nearly half a screen of fixed
          // furniture above the page you are trying to read, and two
          // more lines for the text to ghost against. The title ticker
          // and the view chips are what this surface needs; the
          // document's own title follows directly below.
          quiet
          navOffset={0}
        />
      }
    />
  );
  // the view in the stage: a view (directory · orbit · timeline · tasks · repo ·
  // story), a panel or the reader. If it trips, the last good view stays on
  // screen, inert, and is put back; switching the view starts it clean.
  const sceneBody = (
    <SafeBoundary where="stage" keepLast resetKey={`${gridView}:${inViewer ? "reader" : ""}`} crash={["stage", "chunk"]}>
    {isPanel ? (
      // no .view-fade here: a panel only ever arrives WITH the section
      // slide (the stage is keyed by path), and a second fade from 0
      // multiplied the two opacities, leaving the panel near-invisible
      // for its first frames. The slide is its one entrance.
      <div>
        {/* one space tripping never takes the dashboard with it */}
        <PanelBoundary key={current.path} where={`panel:${current.panel}`}>
        {panels[current.panel!] ?? (
          <div style={{ color: "var(--ink-3)", fontSize: "0.95rem", maxWidth: 460 }}>
            open <a href="/" style={{ color: "var(--accent-ink)" }}>home</a> to see {current.name}.
          </div>
        )}
        </PanelBoundary>
      </div>
    ) : inViewer ? (
      // ONE reader on EVERY width (was: mobile scroll reader / desktop card
      // rail + expanded page). The living organism stays behind it. The
      // navigator and the document tabs are PINNED together as one region
      // (ArtifactMobile's sticky) so the view chips never leave the screen,
      // and the document folds away underneath them behind a progressive
      // frost. No card, no expand step.
      <PanelBoundary key={current.path} where="reader">{readerBody}</PanelBoundary>
    ) : arrivingPid && current.path === `/projects/${arrivingPid}` && !current.children ? (
      // law_snappy — the folder opened before its contents arrived. Ghost
      // tiles hold the stage (never an empty folder); they only show if the
      // wait outlasts ~120ms, so a warm fetch never flashes them.
      <div className="os-arriving" aria-busy="true" aria-label="opening">
        {Array.from({ length: 6 }, (_, i) => (
          <div key={i} className="os-arriving__tile" style={{ ["--i" as string]: i } as React.CSSProperties}>
            <span className="skeleton-line os-arriving__icon" />
            <span className="skeleton-line os-arriving__label" />
          </div>
        ))}
      </div>
    ) : openFailed && current.path === `/projects/${openFailed}` && !current.children ? (
      // a folder that could not be read is NOT an empty folder (lib/os/outage:
      // a failure must never render as emptiness). Say so, calmly, and offer
      // the one thing that helps. The tree around it is untouched. (If the
      // session ended, the one sign-in note below says so; it is not said twice.)
      <div className="view-fade" role="alert" style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: "1.1rem", padding: "2.2rem 0", maxWidth: 420 }}>
        <p style={{ margin: 0, color: "var(--ink-2)", fontSize: "var(--t-base, 1rem)", lineHeight: 1.6, textWrap: "balance" }}>
          {"couldn't open this one. please try again."}
        </p>
        <PillButton onClick={() => void ensureProject(openFailed, { quiet: true })}>try again</PillButton>
      </div>
    ) : gridView === "tasks" && isProjectRoot && tasksEnabled ? (
      <div className="view-fade">
        <TaskListView projectId={current.projectId!} onPulse={firePulse} />
      </div>
    ) : gridView === "repo" && hasRepo ? (
      <div className="view-fade">
        <ProjectRepoView
          projectId={current.projectId!}
          node={current}
          onOpenChild={(id: string) => void goTo(`/projects/${id}`, { view: "repo" })}
        />
      </div>
    ) : gridView === "timeline" && hasTimeline ? (
      <div className="view-fade">
        <ProjectTimeline commits={commits} contents={children} />
      </div>
    ) : gridView === "story" && storyLevel ? (
      <div className="view-fade">
        <ProjectStoryView nodes={children} onOpen={open} />
      </div>
    ) : gridView === "orbit" && children.length > 0 ? (
      <div className="view-fade">
        <OrbitView
          node={dirNode}
          onOpen={open}
          onSpin={setOrbitSpin}
          onState={onOrbitState}
          selectedKey={selectedKey}
          onSelect={setSelectedKey}
          onRename={commitRename}
          layout={layout}
          onMoveInto={moveInto}
          onContextAction={onContextAction}
        />
      </div>
    ) : (
      <>
        {SORTABLE_VIEWS.has(gridView) && children.length > 1 && <FolderSortLine sort={folderSort} onSort={saveSort} />}
        <DirectoryView node={dirNode} view={gridView} layout={layout} onOpen={open} selectedKey={selectedKey} onSelect={setSelectedKey} onRename={commitRename} onMoveInto={moveInto} onContextAction={onContextAction} sort={folderSort} />
      </>
    )}
    </SafeBoundary>
  );
  return (
    <OsShellProvider value={shellApi}>
    {/* overflow-x is CLIP, not hidden: `hidden` on one axis forces the other to
        compute to `auto`, turning <main> into a scroll container that captures the
        pinned navigator's `position: sticky` (so it wouldn't stick). `clip` bounds
        the horizontal section-slide the same way without establishing a scrollport,
        so sticky keeps resolving against the page. */}
    <main style={{ position: "relative", minHeight: "100vh", overflowX: "clip" }}>
      {/* the organism palette is THIS user's — ThemeProvider is the single
          authority for --organism-*, so folders / avatar / glows all match it.
          Seeded from the normalised idKey so admin + dashboard never diverge. */}
      <OrganismIdentity identity={idKey} palette={nettiId || tunedColor ? userOrganism : undefined} />
      {/* netti itself is mounted FURTHER DOWN, deliberately — see the note by the
          reading scrim. It has to be painted after the veils it must not be
          blurred by, and before the content it must stay behind. */}

      {/* onboarding colour wash — a soft radial in the CURRENT stage's organism
          colour so each space glows its own hue instead of sitting flat on white.
          Retints automatically as publishHue moves --organism-color per stage;
          the home "bond" beat sweeps it through the spectrum. */}
      {/* not during the overture — the birth brings its OWN room (.ovt-sky), and
          two washes in the same colour stacked over each other only muddy it. */}
      {onbActive && !overtureActive && <div className={`os-ob-wash${onbBond ? " os-ob-wash--bond" : ""}`} aria-hidden />}

      {/* the reader's reading scrim — a ground-aware gradient wash + blur whose
          opacity IS --art-ramp, so as you scroll DOWN the living organism behind the
          text darkens gradually (content stays readable) and clears again on scroll
          up. On EVERY width now (was phone-only); the desktop card scrim is retired. */}
      {inViewer && <div className="art-mobile-scrim" aria-hidden />}

      {/* the SAME reading behaviour for every scrollable page (law_read_ramp):
          at the top the living ground is fully present; as any panel/list
          scrolls, this scrim blurs + settles the background so the content
          reads in full contrast — and clears again at the top. Driven by
          --os-ramp (written imperatively on window scroll, below). The age-scrub
          views (activity · timeline) opt OUT — netti stays sharp while it de-ages. */}
      {!inViewer && <div className="os-read-scrim" aria-hidden />}
      {/* a room that reads stronger (connections, 2026-10-22): the ground deepens a
          shade across the WHOLE window, under netti (which rests out of focus
          there), never a box behind the panel. Opacity only; no filter. */}
      {next && <div className="os-room-tint" data-on={current.panel === "connections" || undefined} aria-hidden />}

      {/* ── netti — law_netti_above_the_veil ───────────────────────────────────
          MOUNTED HERE ON PURPOSE, not at the top of <main>. The reading scrims
          above are `backdrop-filter`, so they frost whatever was painted before
          them; netti used to be painted first and so went soft and grey the
          moment any page scrolled — the one living thing on screen, blurred out
          to make room for text. Rendered AFTER them at the same z (NETTI_Z = 1),
          paint order does the rest: scrim → netti → content. The creature stays
          sharp through the veil, and the content surface below (one stacking
          context at z 1 — which is what caps the orbit's tiles) still paints
          over it, so the files keep orbiting OVER netti rather than behind it. */}
      {/* netti is a decoration: if it cannot draw, the ground stays and nothing else notices */}
      <SafeBoundary where="organism" silent>
      <OrganismBackdrop
        anchor={orgOver?.anchor ?? choreo.anchor}
        offset={orgOver?.offset ?? choreo.offset}
        glide={orgOver?.glide ?? choreo.glide}
        glideAxis={orgOver?.glideAxis ?? choreo.glideAxis}
        size="min(150vh, 1120px)"
        scale={orgOver ? orgOver.scale : orgScale}
        innerTransMs={orgOver ? orgOver.transMs : orgTransMs}
        innerEase={orgOver ? orgOver.ease : orgEase}
        blur={0}
        opacity={orgOver ? orgOver.opacity : orgOpacity}
        fade={0.82}
        zIndex={guideVeil ? GUIDE_VEIL_NETTI_Z : orgOver ? orgOver.zIndex : choreo.z}
        focus={orgOver ? orgOver.focus : shellFocus}
        // netti LEADS the orbit's revolve — it takes the scroll first and the
        // files are chased off its rotation a beat later (see OrbitView's
        // NETTI_FACTOR/FOLLOW). The two motions still mesh like gears; the
        // creature is simply the one driving them.
        rotation={choreo.rotation + orbitSpin * 0.6}
        spin
        // local dashboard edits (rename/move) + the live nervous system (any write
        // that hits innernet, from any tool/tab) both fire the organism.
        pulse={pulse + live.pulse}
        age={baseAge}
        // listen to the imperative age bus — the activity/commit timelines scrub
        // the bloom's age through it at 60fps without re-rendering the shell.
        ageBus
        // the ground dial repaints the bloom's ground live as you drag the arcs.
        driveBus
        color={organismColor}
        seed={userSeed}
        body={userBody}
        renderScale={1}
        // Normally the dashboard organism colour is static (this user's) so we
        // don't run the publish loop — ThemeProvider owns the vars. DURING the
        // onboarding journey we DO publish, so every stage's hue retints the whole
        // UI; home also runs showcase, netti's colour-cycling "bond" intro.
        publishHue={onbActive}
        showcase={onbBond}
        wander={onbBond}
        warm
        veil
        veilTune={veilPrefs}
        macros={{ bleed: 0.5, shimmer: 0.4, glow: 0, grain: 0, density: 0.52, network: 0.5 }}
        grow
        // law_read_frost: whenever you are reading (any view that scrolls, the
        // document reader, the activity river and the timelines included) netti
        // frosts and steps back inside its own box. Not in the orbit (its scroll
        // turns the files and netti leads them) and never under the guide.
        // …except the agents orbit: it is a PAGE, not a wheel. The orbit view turns by
        // scroll, so it never reads and never frosts; the agents home is sharp at the top
        // (the ramp is 0 there) and scrolls on to the lists under the stage, where
        // netti goes soft behind the words like everywhere else.
        readingFrost={(!orbitHold || agentsOrbit) && !guideVeil}
      />
      </SafeBoundary>
      {/* the agents orbit's depth of field: painted right after netti (so over it)
          and before the content, clear at netti's heart and softening outward
          (only someone who can see the agents space ever loads it) */}
      {agentsEnabled && (
        <SafeBoundary where="organism:veil" silent>
          <OrbitVeil on={agentsOrbit && current.panel === "agents"} />
        </SafeBoundary>
      )}

      {/* corner blur veils — like the bottom-left dock, all four corners seat their
          fixed chrome above scrolling content with a progressive blur that fades
          inward. top-left = breadcrumb, top-right = avatar; the Noticer carries its
          own (bottom-right), the dock its own (bottom-left). */}
      {/* No top corner frosts, in the reader or anywhere: the reader scrolls in
          its own column, which starts BELOW the header, so no document ever
          passes under the breadcrumb or the avatar. The two corner veils it used
          to mount (128px and 152px tall, blurring 2 to 16px) only ever lay over
          the reader's own document tabs once the head folded up to meet them:
          the blur over the top of the tabs. The reader's seam owns that edge. */}

      {/* header: brand + breadcrumb left, identity right */}
      <header
        className="os-topbar"
        // asleep: the overture or the graduation breath owns the room, and the
        // visible row (globals.css, .os-topbar) must not wake to the pointer
        data-asleep={overtureActive || closing || undefined}
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          padding: "1.4rem 0",
          // when the menu owns the screen the header lifts ABOVE the veil so the
          // ≡ (now an ✕) stays tappable to close — like the landing crumb bar.
          // At rest it sits at 9, above NETTI_Z_OVER_CHROME (7): on the sections
          // where netti rides over the rail, the breadcrumb and your avatar must
          // still be the things on top — netti settles ON the avatar's face,
          // never over it. Everything netti passes under is chrome you can click.
          zIndex: menuOpen ? 60 : 9,
          // asleep during the overture — netti's birth owns the room — and again
          // for the graduation breath, so the navigator ARRIVES rather than
          // simply having been there.
          opacity: overtureActive || closing ? 0 : revealed ? 1 : 0,
          pointerEvents: overtureActive || closing ? "none" : undefined,
          transition: "opacity 0.7s ease",
        }}
      >
        {/* centered shell column — on ultra-wide / 4K the chrome caps at
            --os-shell-max and centers; the inner gutter keeps the breadcrumb and
            the navigator starting at the SAME x as the content column below. */}
        <div style={{ maxWidth: "var(--os-shell-max)", margin: "0 auto", paddingInline: "var(--os-gutter)", display: "flex", justifyContent: "space-between", alignItems: "center", gap: "1rem" }}>
        <div style={{ display: "flex", alignItems: "baseline", gap: "0.5rem", minWidth: 0, fontSize: "var(--t-crumb)" }}>
          {/* law_no_clipping — overflow visible so the wordmark leaf-flick + i-dot
              and the ≡ never crop on a tight mobile crumb; the compact/collapse
              props already keep long trails from pushing the layout. */}
          <span ref={crumbRef} style={{ display: "inline-flex", minWidth: 0, overflow: "visible", flex: "0 1 auto" }}>
          <BrandCrumb
            segments={crumbs}
            size="inherit"
            // one directory deep on mobile → the wordmark becomes the compact icon;
            // and when the crumb would clash the avatar, collapse to icon + last dir.
            compact={isNarrow ? crumbs.length >= 1 : searchTight}
            collapse={searchTight || (isNarrow && crumbs.length >= 2)}
            afterRoot={
              // the section-glossary trigger, right after the wordmark:
              // "innernet ≡ / dir" — same ≡ + behaviour as the landing menu.
              <button
                type="button"
                className="tlm-btn"
                aria-label={menuOpen ? "close menu" : "menu"}
                aria-expanded={menuOpen}
                data-noswipe
                onClick={() => setMenuOpen((o) => !o)}
                // law_text_baseline — the ≡ rests on the crumb text's baseline
                // (the crumb row is baseline-aligned; no self-centering).
                style={{ flexShrink: 0 }}
              >
                <svg className="tlm-btn__ic" viewBox="0 0 44 30" aria-hidden>
                  <rect x="1.5" y="1.5" width="41" height="27" rx="13.5" fill="none" stroke="currentColor" strokeWidth="2.4" />
                  <line className="tlm-btn__l1" x1="15" y1="12" x2="29" y2="12" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" />
                  <line className="tlm-btn__l2" x1="15" y1="18" x2="29" y2="18" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" />
                </svg>
              </button>
            }
            onRoot={() => {
              navDir.current = -1;
              setExpanded(false);
              setViewerIndex(null);
              void goTo("/projects");
              // history state is kept in sync by the navSync effect; the URL never
              // changes (the app is served at "/" or "/dashboard"), so a refresh
              // always lands back on the shell root.
            }}
            style={{ flex: "0 1 auto", minWidth: 0, overflow: "visible" }}
          />
          </span>
          {/* search lives in the breadcrumb on EVERY level, scoped to where you are:
              the project (or folder) you're in, or everything at the root. */}
          {/* the search fades away with the rest of the chrome while the menu
              takes over — only the crumb + ≡ stay above the veil. */}
          <span className="os-crumb-find" style={{ display: "inline-flex", minWidth: 0, opacity: menuOpen ? 0 : 1, pointerEvents: menuOpen ? "none" : undefined, transition: "opacity 0.4s var(--ease-soft)" }}>
          {/* a searchable panel's own field stands here (CrumbFind); while it
              does, the search of everything below steps aside (css, no state) */}
          <span ref={setFindSlot} className="os-find-slot" />
          <span className="os-global-search" style={{ display: "inline-flex", minWidth: 0 }}>
          <SafeBoundary where="chrome:search" silent>
          <OsSearch
            onNavigate={(_pid, target, query) => { setSearchHighlight(query || null); navDir.current = 1; void goTo(target); }}
            scopeProjectId={current.projectId}
            scopePath={current.path}
            scopeLabel={current.projectId ? current.name : null}
            collapsible={searchTight}
          />
          </SafeBoundary>
          </span>
          </span>
        </div>
        <div ref={identityRef} style={{ display: "flex", alignItems: "center", gap: "1rem", flexShrink: 0, opacity: menuOpen ? 0 : 1, pointerEvents: menuOpen ? "none" : undefined, transition: "opacity 0.4s var(--ease-soft)" }}>
        {/* the help eye sits exactly where the admin button sits (admins see both) */}
        <SafeBoundary where="chrome:feedback" silent>
          <FeedbackLauncher userName={who} />
        </SafeBoundary>
        {/* desktop only — the operator console stays out of the phone chrome
            entirely; on mobile that slot belongs to netti's chat. */}
        {isAdmin && !isNarrow && (
          <a
            href="/admin"
            title="operator console"
            aria-label="open operator console"
            data-noswipe
            className="icon-circle os-headbtn"
            style={{ textDecoration: "none" }}
          >
            {/* the operator's window into the whole organism — the brand mark itself */}
            <OrganismMark size={20} />
          </a>
        )}
        {/* netti chat is hidden on mobile for now (to be reintroduced) — no mobile
            ask-netti entry point; the dock itself also no-ops under 640px. */}
        {/* you, top-right — the ONE chip every surface wears, and this is the
            size it is measured from. Pressing it opens the you-menu (home · you
            · settings · admin for founders · sign out); here its rows step the
            shell in place. `you` is the panel that seats netti's central cell
            exactly on this mark (avatarRef). */}
        <UserChip
          card={{ signedIn: true, name: who, avatarUrl, founder: isAdmin }}
          avatarRef={avatarRef}
          // the you-menu's rows step the shell in place: home is the root
          onGo={(place) => {
            navDir.current = 1;
            setExpanded(false);
            setViewerIndex(null);
            void goTo(place === "home" ? "/" : `/${place}`);
          }}
          style={{ flexShrink: 0 }}
        />
        </div>
        </div>
      </header>

      {/* law_edit_frame — while you edit memory, the WHOLE view wears a 3px
          frame in the organism's colour just inside the viewport edge: fixed,
          inert, above every piece of chrome including the header. Always
          mounted so it is uncovered and covered again (opacity), never popped. */}
      <div className="os-edit-frame" data-on={editing || undefined} aria-hidden />

      {/* THE STAGE (components/stage): the navigator in flow on top, the content in
          its own scroller below; the same views, panels and reader, the same
          gestures and fades, one stacking context at z 1 painted after netti. */}
      {stage ? (
        <Stage
          rootProps={{
            ...bind(),
            // law_snappy — guess the open before it happens (see the legacy surface)
            onPointerOverCapture: onIntentOver,
            onPointerOutCapture: onIntentOut,
            onPointerDownCapture: onIntentDown,
            // under the menu, the overture or the graduation breath the content is
            // gone: nothing in it can be tabbed to, read out or found
            inert: menuOpen || overtureActive || closing ? true : undefined,
            style: {
              // the content (and its navigator) fades away entirely while the menu,
              // the overture, or the graduation breath owns the screen
              opacity: menuOpen || overtureActive || closing ? 0 : (revealed ? (loading ? 0.5 : 1) : 0),
              pointerEvents: menuOpen || overtureActive || closing ? "none" : undefined,
              transition: `opacity ${introDone ? "0.22s" : "0.5s"} var(--ease-soft) ${loading ? "0.18s" : "0s"}`,
            },
          }}
          headRef={navRef}
          headClassName="os-lock--detached"
          headKey={inViewer ? `reader:${levelKey}` : levelKey}
          fold={stageFold}
          scrollRef={stageScrollRef}
          sceneKey={sceneKey}
          dir={travel.current.dir}
          swipe={travel.current.swipe}
          restoreTop={stageRestore.current && stageRestore.current.path === current.path && stageRestore.current.viewerIndex === viewerIndex ? stageRestore.current.top : null}
          label={current.name}
          variant={inViewer ? "reader" : undefined}
          slack={inViewer ? "52vh" : guideActive && isNarrow ? "44vh" : "6rem"}
          head={
            inViewer ? (
              // THE READER'S HEAD: the navigator folds away as one block, the
              // document tabs (and the edit row) stay — the stage's head shrinks
              // to them and the document gets the room.
              <div className="stage-reader-head">
                <div className="stage-reader-nav">
                  <SafeBoundary where="nav" keepLast compact staleNote={false} resetKey={`reader:${parentPath(current.path) ?? ""}`} crash="nav">
                    <OsNavigator
                      siblings={siblings}
                      activeKey={current.key}
                      onSelectSibling={selectSibling}
                      summary={matter}
                      summaryManual={summaryManual}
                      showMatter={showMatter}
                      onEditSummary={(t) => { layout.setSummary("_self", t); firePulse(); }}
                      chips={isPanel ? [] : orderedChips}
                      view="documents"
                      onView={(id) => { onView(id); firePulse(); }}
                      onNewView={() => { setViewSel("orbit"); firePulse(); }}
                      onReorderChips={onReorderChips}
                      compact
                      quiet
                      // the title strip folds away as you read; the view chips
                      // and the document tabs stay (law_stage)
                      foldRail
                      navOffset={0}
                    />
                  </SafeBoundary>
                </div>
                <ReaderTabsRow
                  pages={pages}
                  index={docIndex}
                  onIndex={(i) => setViewerIndex(i)}
                  editing={editing}
                  onToggleEdit={() => setEditing((e) => !e)}
                  onSaveNow={commitDocEdit}
                  onCancelEdit={cancelDocEdit}
                />
              </div>
            ) : (
              <>
                {/* a NEW LEVEL wires its own navigator in (the stage hands the old one
                    over, keyed by headKey); a step between siblings keeps this one and
                    its ticker glides in place */}
                <div>
                  <SafeBoundary where="nav" keepLast compact staleNote={false} resetKey={levelKey} crash="nav">
                    <OsNavigator
                      siblings={siblings}
                      activeKey={current.key}
                      onSelectSibling={selectSibling}
                      quiet={guideActive && ob?.stage !== "free"}
                      summary={matter}
                      summaryManual={summaryManual}
                      showMatter={showMatter}
                      onEditSummary={(t) => { layout.setSummary("_self", t); firePulse(); }}
                      chips={isPanel ? [] : orderedChips}
                      view={gridView}
                      onView={(id) => { if (id === gridView && SORTABLE_VIEWS.has(id)) saveSort(flip(folderSort)); else onView(id); firePulse(); }}
                      onNewView={() => { setViewSel("orbit"); firePulse(); }}
                      onReorderChips={onReorderChips}
                      fold
                      chipSlot={setChipSlot}
                      unseenViews={unseenViews}
                      renderChipAccessory={(id) =>
                        id === gridView && SORTABLE_VIEWS.has(id) ? <SortArrow sort={folderSort} /> : id === "orbit" && orbitDial.active ? <OrbitDial spin={orbitSpin} windowArc={orbitDial.windowArc} page={orbitDial.page} pages={orbitDial.pages} /> : null
                      }
                    />
                  </SafeBoundary>
                </div>
                <div ref={setRailSlot} className="os-rail-slot" />
              </>
            )
          }
        >
          {sceneBody}
        </Stage>
      ) : (
      <>
      {/* the swipe + content surface */}
      <div
        {...bind()}
        // law_snappy — guess the open before it happens. Resting on a project
        // tile (or pressing it) starts its subtree on the wire, so by the time
        // the click lands the folder is usually already here. Capture phase +
        // delegated, so it never collides with the swipe binding's own handlers.
        onPointerOverCapture={onIntentOver}
        onPointerOutCapture={onIntentOut}
        onPointerDownCapture={onIntentDown}
        style={{
          position: "relative",
          zIndex: 1,
          // tighter breadcrumb→nav gap on mobile; and in the artifact viewer the
          // page must NOT scroll (only the card body does) — so the bottom pad
          // collapses so the whole viewer fits the viewport.
          // ONE padding declaration. A separate `paddingBottom` alongside this
          // shorthand made React warn on every rerender (and the two could
          // silently disagree) — the guide's phone clearance folds in here.
          padding: `calc(1.5rem + 48px + ${isNarrow ? "0.7rem" : "1.8rem"}) var(--os-gutter) ${inViewer ? "1rem" : "0"}`,
          // the end slack rides INSIDE the navigator's column (on the stage) so
          // the pinned head can lean on it to the very end — see .os-lock--detached
          ["--os-slack" as string]: guideActive && isNarrow ? "44vh" : "6rem",
          // cap + center the whole content column (navigator + folders) on big
          // screens, in concert with the header above — same max-width and gutter.
          maxWidth: "var(--os-shell-max)",
          marginInline: "auto",
          touchAction: "pan-y",
          // the content fades away entirely while the menu, the overture, or the
          // graduation breath owns the screen.
          opacity: menuOpen || overtureActive || closing ? 0 : (revealed ? (loading ? 0.5 : 1) : 0),
          pointerEvents: menuOpen || overtureActive || closing ? "none" : undefined,
          // law_snappy — back from the menu in one beat; and the loading dim waits
          // 180ms before it starts, so a quick fetch never flickers the page.
          transition: `opacity ${introDone ? "0.22s" : "0.5s"} var(--ease-soft) ${loading ? "0.18s" : "0s"}`,
        }}
      >
        <div>
          {/* navigator → content */}
          <div style={{ minWidth: 0 }}>
            {/* the full navigator steps aside in the artifact reader — the reader
                renders its OWN navigator as scroll content (it scrolls off as you
                read), so the outer one is suppressed for every viewer width. */}
            {!inViewer && (
            // law_read_ramp + law_scroll_lock — the navigator is PINNED chrome: it
            // stays put while the content below it scrolls beneath it (sticky, above
            // the corner-veils + read-scrim), so it's never itself frosted and never
            // rides the page scroll. Its own frost bar (.os-nav-fixed::before)
            // settles the content that slides under it. `--os-lock-top` matches the
            // content column's header clearance so it locks in place with no jump —
            // and the band reaches exactly that far back up to meet the header.
            <div
              ref={navRef}
              className="os-nav-fixed os-lock--detached"
              style={{
                ["--os-lock-top" as string]: `calc(1.5rem + 48px + ${isNarrow ? "0.7rem" : "1.8rem"})`,
                zIndex: 6,
                paddingBottom: "2.6rem",
              } as React.CSSProperties}
            >
              {/* the navigator is the one part that must never go: if it trips, the last
                  good one stays (inert) while it is put back, and the stage below keeps working */}
              <SafeBoundary where="nav" keepLast compact staleNote={false} resetKey={current.path} crash="nav">
              <OsNavigator
                siblings={siblings}
                activeKey={current.key}
                onSelectSibling={selectSibling}
                quiet={guideActive && ob?.stage !== "free"}
                summary={matter}
                summaryManual={summaryManual}
                showMatter={showMatter}
                onEditSummary={(t) => { layout.setSummary("_self", t); firePulse(); }}
                chips={isPanel ? [] : orderedChips}
                view={inViewer ? "documents" : gridView}
                onView={(id) => { if (!inViewer && id === gridView && SORTABLE_VIEWS.has(id)) saveSort(flip(folderSort)); else onView(id); firePulse(); }}
                onNewView={() => { setViewSel("orbit"); firePulse(); }}
                onReorderChips={onReorderChips}
                compact={inViewer}
                fold
                unseenViews={inViewer ? undefined : unseenViews}
                renderChipAccessory={(id) =>
                  !inViewer && id === gridView && SORTABLE_VIEWS.has(id) ? <SortArrow sort={folderSort} /> : id === "orbit" && orbitDial.active ? <OrbitDial spin={orbitSpin} windowArc={orbitDial.windowArc} page={orbitDial.page} pages={orbitDial.pages} /> : null
                }
              />
              </SafeBoundary>
              {/* the rail slot: one quiet line a view may seat in the head (the
                  repo view's current day). Rides the fold with the chips; 0 tall
                  and inert when nothing is in it (globals.css .os-rail-slot). */}
              <div ref={setRailSlot} className="os-rail-slot" />
            </div>
            )}

            {/* content stage — keyed by the section/level so a navigation plays the
                directional slide (incoming content arrives from the side the nav was
                heading; the navigator title strip eases the opposite way in concert). */}
            <div
              key={current.path}
              ref={slideRef}
              className="section-slide"
              data-swipe={navSwipePath.current === current.path || undefined}
              style={{ ["--enter-x" as string]: navDir.current >= 0 ? "14px" : "-14px" } as React.CSSProperties}
            >
            {/* the stage: a view (directory · orbit · timeline · tasks · repo · story),
                a panel or the reader. If it trips, the last good view stays on screen,
                inert, and is put back; switching the view starts it clean. */}
{sceneBody}
            </div>
          </div>
        </div>
      </div>

      </>
      )}

      {/* bottom-left bar — ALWAYS the share/options pattern, on every surface.
          On a document (rail OR expanded) it acts on the open page; on every
          other level it acts on the level itself: the context downloads that
          used to live in OsDock, new folder/page, rename, park, settings. Both
          carry the undo/redo pair in the options menu's footer. */}
      <SafeBoundary where="chrome:share" silent>
      {inViewer ? (
        <ShareOptionsBar
          context={{ mode: "document", page: pages[docIndex] }}
          projectId={docProjectId}
          projectName={docProjectName}
          viewer={{ isAdmin }}
          ownerName={who}
          sharePath={pages[docIndex]?.path ?? path}
          onRename={(name) => commitRename(pages[docIndex], name)}
          onForgotten={() => {
            // the page is gone: leave the reader for the folder it was in, and
            // fetch that folder again so the list matches the memory.
            const pid = pages[docIndex]?.projectId;
            setEditing(false);
            setViewerIndex(null);
            if (pid) void refreshProject(pid);
          }}
          undoRedo={undoRedo}
        />
      ) : (
        <ShareOptionsBar
          context={{ mode: "directory", node: current }}
          projectId={current.projectId}
          projectName={docProjectName}
          viewer={{ isAdmin }}
          ownerName={who}
          sharePath={path}
          onRename={current.projectId ? (name) => commitRename(current, name) : undefined}
          onNewFolder={() => onContextAction("new-folder", current)}
          onNewFile={current.projectId ? () => onContextAction("new-file", current) : undefined}
          onPark={isProjectRoot ? () => onContextAction("park", current) : undefined}
          parked={!!(current as OsNode & { parked?: boolean }).parked}
          onSettings={isProjectRoot ? () => onContextAction("settings", current) : undefined}
          undoRedo={undoRedo}
        />
      )}
      </SafeBoundary>

      {/* the ground-colour dial — the corner dot + bloom-out hue/sat/brightness
          arcs. Seats to the left of the share/options dock; hidden during the
          onboarding colour journey (which drives the organism hue itself). */}
      {!onbActive && (
        <SafeBoundary where="chrome:ground-dial" silent>
          <GroundDial />
        </SafeBoundary>
      )}

      {/* the section menu — a full page-view takeover of every space, opened by
          the ≡ in the breadcrumb. One behaviour on every screen size. */}
      {/* the menu takes the whole screen, so if it trips it must give the screen back */}
      <SafeBoundary where="chrome:menu" silent onTrip={() => setMenuOpen(false)}>
      <OsMenu
        open={menuOpen}
        root={menu.root}
        startPath={menu.startPath}
        onClose={() => setMenuOpen(false)}
      />
      </SafeBoundary>

      {/* bottom-right info + clipboard readout (hover hints · ⌘C/⌘V). While the
          guide is walking someone in, IT carries the narration — the corner
          stays on plain hint duty. */}
      <SafeBoundary where="chrome:corner" silent>
        <StatusCorner muteNarration={guideActive || walkRunning || !!tourOn} />
      </SafeBoundary>
      <SafeBoundary where="chrome:outage" silent>
        <OutageWindow />
      </SafeBoundary>
      {/* the session ended. One quiet line with a way back in; everything already
          loaded stays exactly where it is (the note sits where the outage window
          does, because it is the same kind of news). */}
      {signedOut && (
        <p className="sb-note" role="status" style={{ pointerEvents: "auto" }}>
          please sign in again to keep going.{" "}
          <a className="quiet-link" href={signInHref()}>sign in</a>
        </p>
      )}

      {/* the in-dashboard onboarding conductor — sequences the walk (the
          connection, then the walk after it) and unlocks spaces as it goes. It
          renders only netti's pointer. Muted while the overture plays, so the
          first beat speaks into the guide the moment it exists. If it trips, the
          walk falls back to the connection step, never through to `free`. */}
      {ob && !next ? (
        <SafeBoundary where="onboarding:conductor" silent onTrip={() => walkFallback("conductor")}>
        <OnboardingConductorV1
          state={ob as obV1.ObState}
          userName={who}
          isNarrow={isNarrow}
          currentPath={path}
          connected={connected}
          waitingAtDoor={pendingDoor.door ? doorName(pendingDoor.door) : null}
          onGrant={() => void pendingDoor.finish()}
          onGrantLater={pendingDoor.dismiss}
          createdSignal={createdSignal}
          projectCount={projects.length}
          onPersist={(o) => persistOnboarding(o as ObState)}
          goTo={goTo}
          openCreate={() => setFcCreate({ parentId: null, onboarding: true })}
          openConnect={(id) => {
            if (id === "any" || !CONNECT_TOOLS.some((t) => t.id === id)) { goTo("/connections"); return; }
            setConnectTool(id);
          }}
          muted={overtureActive || mobileParked || !!tourOn}
        />
        </SafeBoundary>
      ) : ob ? (
        <SafeBoundary where="onboarding:conductor" silent onTrip={() => walkFallback("conductor")}>
        <OnboardingConductor
          state={ob}
          userName={who}
          isNarrow={isNarrow}
          currentPath={path}
          connected={connectedLive}
          connectedKnown={connectedKnown}
          waitingAtDoor={pendingDoor.door ? doorName(pendingDoor.door) : null}
          onGrant={() => void pendingDoor.finish()}
          onGrantLater={pendingDoor.dismiss}
          onPersist={persistOnboarding}
          goTo={goTo}
          pathResolves={walkPlaceResolves}
          muted={overtureActive || mobileParked || !!tourOn}
          ringOpen={canSee(F.privateRing, { isAdmin, betaFeatures })}
        />
        </SafeBoundary>
      ) : null}

      {/* the guided wire-up — name → wire (with the recorded pictures) → watch
          → self-start → the first-project prompt. Opened from the guide's chips. */}
      {connectTool && (() => {
        const t = CONNECT_TOOLS.find((x) => x.id === connectTool);
        const ConnectGuideWindow = next ? ConnectGuideModal : ConnectGuideModalV1;
        return t ? (
          <SafeBoundary where="sheet:connect" silent backoff={[]} note="that window tripped. nothing changed." onTrip={() => setConnectTool(null)} crash="sheet">
          <ConnectGuideWindow
            tool={t}
            connections={liveConns.connections.filter((c) => c.tool_id === t.id)}
            accountName={who}
            onClose={() => setConnectTool(null)}
            onChanged={() => void liveConns.reload()}
          />
          </SafeBoundary>
        ) : null;
      })()}

      {/* netti's birth — the cinematic first entry. The live organism grows from
          a seed under deep breathing shades; centred acts introduce what this
          is; the landing CTA begins the walk. Skippable at every beat. */}
      {overtureActive && ob && (
        <SafeBoundary where="onboarding:arrival" silent onTrip={() => walkFallback("arrival")}>
        {!next ? (
        <ArrivalV1
          userName={who}
          ownerName={ob.netti?.owner}
          isNarrow={isNarrow}
          onBegin={() => persistOnboarding({ ...ob, bornAt: new Date().toISOString() })}
          onDefer={() => {
            persistOnboarding({ ...ob, deferredAt: new Date().toISOString() });
            try { void fetch("/api/onboarding/defer", { method: "POST" }); } catch { /* best-effort */ }
          }}
        />
        ) : (
        <Arrival
          userName={who}
          ownerName={ob.netti?.owner}
          isNarrow={isNarrow}
          // "begin" goes straight to the connection: home has nothing of its own to say
          onBegin={() => persistOnboarding({ ...ob, bornAt: new Date().toISOString(), ...(ob.stage === "home" ? { stage: "connections" as const, beat: 0 } : {}) })}
          onDefer={() => {
            // park the walk for desktop: state first, then the door in their
            // inbox (the welcome email, sent now instead of on the 2h cron).
            persistOnboarding({ ...ob, deferredAt: new Date().toISOString() });
            try { void fetch("/api/onboarding/defer", { method: "POST" }); } catch { /* best-effort */ }
          }}
        />
        )}
        </SafeBoundary>
      )}

      {/* parked on a phone — the home space is open, the tour is waiting on the
          desktop. One quiet line says so; it leaves when the flow completes. */}
      {mobileParked && (
        <div className="ob-parked-note" role="status">
          the welcome lives on your desktop, and the way in is in your inbox. everything you keep here will be there too.
        </div>
      )}

      {/* netti walking you in — the conductor's beats as a real conversation,
          answered by tapping chips. Unmounts for good at "explore". */}
      {guideActive && (
        <SafeBoundary where="onboarding:guide" silent onTrip={() => walkFallback("guide")}>
          {next ? <NettiGuide centred={closing} flow="os_odyssey" /> : <NettiGuideV1 centred={closing} />}
        </SafeBoundary>
      )}

      {/* netti's tours (lib/tours): the same conversation as the first-run walk, for something new */}
      {tourOn && !guideActive && (
        <SafeBoundary where="tour" silent onTrip={() => setTourOn(null)}>
          {next ? (
          <NettiTour
            key={tourOn.id}
            tour={tourOn}
            fill={tourFill}
            goTo={(p: string) => void goTo(p)}
            currentPath={path}
            onMakeAgent={() => {
              keepSeen(tourOn, "made");
              setTourOn(null);
              // the agents have a space of their own now (2026-10-22), and it answers the sheet
              void goTo("/agents").then(() => window.setTimeout(openAgentMake, 120));
            }}
            onEnd={(how) => {
              keepSeen(tourOn, how);
              setTourOn(null);
            }}
          />
          ) : (
          <NettiTourV1
            key={tourOn.id}
            tour={tourOn}
            fill={tourFill}
            goTo={(p: string) => void goTo(p)}
            onMakeAgent={() => {
              keepSeen(tourOn, "made");
              setTourOn(null);
              void goTo("/connections").then(() => window.setTimeout(openAgentMake, 120));
            }}
            onEnd={(how) => {
              keepSeen(tourOn, how);
              setTourOn(null);
            }}
          />
          )}
        </SafeBoundary>
      )}

      {fcCreate && (
        <SafeBoundary where="sheet:new-folder" silent backoff={[]} note="that window tripped. nothing changed." onTrip={() => setFcCreate(null)} crash="sheet">
        <FolderSheet
          mode="create"
          parentId={fcCreate.parentId}
          onClose={() => setFcCreate(null)}
          {...(!next && fcCreate.onboarding ? { onSkip: () => { setFcCreate(null); narrateAct("skip-step"); } } : {})}
          onCreated={(project) => {
            setFcCreate(null);
            if (project.parentId) { void refreshProject(project.parentId); }
            else { addTopLevelProject(project); }
            firePulse();
            setCreatedSignal((n) => n + 1); // every arrival moves the colour (growthHue)
            flashNote(`created ${project.name}`);
          }}
        />
        </SafeBoundary>
      )}

      {mvSource && (
        <SafeBoundary where="sheet:move" silent backoff={[]} note="that window tripped. nothing changed." onTrip={() => setMvSource(null)} crash="sheet">
        <MovePicker
          source={mvSource}
          tree={tree}
          onClose={() => setMvSource(null)}
          onPick={(dest) => {
            const src = mvSource;
            setMvSource(null);
            if (!moveInto(src, dest)) flashNote("couldn't move that there");
          }}
          onPickTop={
            mvSource.kind === "project" && findParentOf(treeRef.current, mvSource.key)?.path !== "/projects"
              ? () => { const src = mvSource; setMvSource(null); moveToTop(src); }
              : undefined
          }
        />
        </SafeBoundary>
      )}

      {psTarget && (
        <SafeBoundary where="sheet:folder-settings" silent backoff={[]} note="that window tripped. nothing changed." onTrip={() => setPsTarget(null)} crash="sheet">
        <FolderSheet
          mode="edit"
          target={psTarget}
          // the map tidy rides moving in (F.moveIn, lib/netti/tidy): a quiet word at the sheet's foot
          {...(next && seesFeature(F.moveIn) ? { tidy: true } : {})}
          onClose={() => setPsTarget(null)}
          onChange={(patch) => patchProjectNode(psTarget.projectId, patch)}
          onRemoved={(kind) => {
            const removed = psTarget;
            // stash the folder before removal so an unpark/undo puts it right back.
            const node = findByPath(treeRef.current, `/projects/${removed.projectId}`);
            removeProjectNode(removed.projectId);
            setPsTarget(null);
            firePulse();
            if (kind === "parked") {
              if (node) parkedStash.current.set(removed.projectId, node);
              editStack.push({ kind: "park", projectId: removed.projectId, name: removed.name, parked: true });
              void refreshParked();
              flashNote(`parked ${removed.name} — find it under parked`);
            } else {
              flashNote(`forgot ${removed.name}`);
            }
            if (current.projectId === removed.projectId) void goTo("/projects");
          }}
        />
        </SafeBoundary>
      )}

      {/* the context menu's "Icon…" — the whole Apple emoji collection. It is its
          own chunk and mounts the first time an icon is asked for (iconEver), then
          stays mounted so closing it still plays the sheet's way out; each new
          target starts it clean (resetKey). */}
      {iconEver && (
      <SafeBoundary where="sheet:emoji" silent backoff={[]} resetKey={iconTarget ? iconTarget.name : null} note="that window tripped. nothing changed." onTrip={() => setIconTarget(null)}>
      <IconSheet
        open={!!iconTarget}
        title={iconTarget ? `an icon for ${iconTarget.name}` : undefined}
        onClose={() => setIconTarget(null)}
        onPick={(emoji) => {
          if (iconTarget) pickIconFor(iconTarget, emoji);
        }}
      />
      </SafeBoundary>
      )}
    </main>
    </OsShellProvider>
  );
}
